// Control UI startup English. Lazy fragments retain empty namespace anchors here
// so host-side composition preserves grouped translation-memory source order.
import type { TranslationMap } from "../lib/types.ts";
import * as agentEn from "./en-agents.ts";

export const en: TranslationMap & {
  configPage: TranslationMap;
  configView: TranslationMap;
  debug: TranslationMap & { overlay: TranslationMap };
  // Lazy en-devices.ts assigns into this namespace.
  devices: TranslationMap;
  desktop: TranslationMap &
    Record<"title" | "openWindow" | "unavailable" | "toggle" | "reconnect", string>;
  updates: TranslationMap;
  login: TranslationMap;
} = {
  pluginUi: {
    customize: "Customize UI",
    selectionScope: "Choose views for this browser window. Built-in views are always available.",
    builtin: "Built-in view",
    retryView: "Retry plugin view",
    reload: "Reload plugin UI",
    dashboardTitle: "Session dashboard",
    dashboardEmpty: "This session has no dashboard widgets yet.",
    customPluginsDisabled: "Custom plugin UI is off",
    customPluginsEnableHint:
      "Enable Custom plugin UI in Labs, then restart the Gateway and reload this browser tab.",
    openLabs: "Open Labs",
    surface: {
      "session-list": "Session list",
      composer: "Composer",
      workspace: "Workspace",
      transcript: "Transcript",
      "tool-result": "Tool results",
    },
  },
  capacityMeter: {
    workerSlots: "{used} of {total} slots busy",
    unavailable: "Slot utilization unavailable",
    execHost: "Codex exec",
  },
  common: {
    health: "Health",
    ok: "OK",
    yes: "Yes",
    no: "No",
    default: "Default",
    active: "Active",
    loading: "Loading…",
    gatewayStarting: "Gateway starting…",
    refreshing: "Refreshing…",
    offline: "Offline",
    connect: "Connect",
    connected: "Connected",
    refresh: "Refresh",
    retry: "Retry",
    staleData: "Showing stale data.",
    reload: "Reload",
    reset: "Reset",
    restore: "Restore",
    unarchive: "Unarchive",
    undo: "Undo",
    probe: "Probe",
    call: "Call",
    confirm: "Confirm",
    dontAskAgain: "Don't ask me again",
    cancel: "Cancel",
    close: "Close",
    previous: "Previous",
    next: "Next",
    continue: "Continue",
    back: "Back",
    create: "Create",
    copy: "Copy",
    copied: "Copied!",
    copyFailed: "Copy failed",
    copyCode: "Copy code",
    copyTable: "Copy table",
    expandTable: "Expand table",
    expandedTable: "Expanded table",
    closeTable: "Close expanded table",
    resizeSplitView: "Resize split view",
    delete: "Delete",
    remove: "Remove",
    dismiss: "Dismiss",
    dismissAndDontShowAgain: "Dismiss and don't show again",
    unselect: "Unselect",
    enabled: "Enabled",
    disabled: "Disabled",
    failed: "Failed",
    none: "none",
    na: "n/a",
    never: "never",
    justNow: "just now",
    now: "now",
    unknown: "Unknown",
    configured: "Configured",
    running: "Running",
    linked: "Linked",
    mode: "Mode",
    system: "System",
    light: "Light",
    dark: "Dark",
    baseUrl: "Base URL",
    lastStart: "Last start",
    lastProbe: "Last probe",
    lastConnect: "Last connect",
    lastMessage: "Last message",
    authAge: "Auth age",
    credential: "Credential",
    audience: "Audience",
    publicKey: "Public Key",
    probeOk: "Probe ok",
    probeFailed: "Probe failed",
    reloadConfig: "Reload Config",
    loadConfig: "Load config",
    loadApprovals: "Load approvals",
    settingsSections: "Settings sections",
    version: "Version",
    docs: "Docs",
    learnMore: "Learn more",
    colorMode: "Color mode",
    colorModeOption: "Color mode: {mode}",
    search: "Search",
    save: "Save",
    saving: "Saving…",
    saveAndPublish: "Save & Publish",
    importing: "Importing…",
    importFromRelays: "Import from Relays",
    showAdvanced: "Show Advanced",
    hideAdvanced: "Hide Advanced",
    unsavedChanges: "You have unsaved changes",
    working: "Working…",
    showQr: "Show QR",
    relink: "Relink",
    waitForScan: "Wait for scan",
    logout: "Logout",
    skipToMainContent: "Skip to main content",
  },
  focus: {
    unsupported: "This focused view is not supported.",
  },
  shortcutsOverlay: {
    title: "Keyboard shortcuts",
    sections: {
      general: "General",
      chat: "Chat",
      panels: "Panels",
      sidebar: "Sidebar",
      imageViewer: "Image viewer",
      approvals: "Approvals",
    },
    labels: {
      commandPalette: "Open command palette",
      keyboardShortcuts: "Show keyboard shortcuts",
      toggleSidebar: "Toggle sidebar",
      debugOverlay: "Toggle debug overlay",
      appearanceSettings: "Open appearance settings",
      startNewSession: "Start new session (from the new-session page)",
      closeDialog: "Close dialog or exit settings",
      sendMessage: "Send message",
      newline: "Insert new line",
      steerImmediately: "Steer active response",
      historyRecall: "Browse sent message history",
      transcriptSearch: "Search conversation",
      clearReply: "Clear reply",
      stopResponse: "Stop active response",
      cancelDictation: "Cancel dictation",
      saveQueuedMessage: "Save queued message",
      toggleSessionSelect: "Select multiple sessions",
      extendSessionSelect: "Extend session selection",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      zoomReset: "Reset zoom",
      terminalPanel: "Toggle terminal panel",
      homePanel: "Talk to your Home agent",
      workspaceFiles: "Toggle workspace files",
      sideChat: "Toggle side chat",
      browserPanel: "Toggle browser panel",
      tasksPanel: "Toggle tasks panel",
      desktopPanel: "Toggle desktop panel",
      discussionPanel: "Toggle discussion panel",
      dashboardPanel: "Toggle dashboard panel",
      reviewPanel: "Toggle review panel",
      approveOnce: "Approve once",
      approveAlways: "Always allow",
      denyApproval: "Deny approval",
    },
  },
  optionCard: {
    recommended: "Recommended",
    skip: "Skip for now",
  },
  nativeLinkMenu: {
    label: "Link actions",
    openInline: "Open in Sidebar",
    openExternal: "Open in Default Browser",
    copy: "Copy Link",
  },
  browserLinkPreferences: {
    openInControlUi: "Open links in Control UI browser",
  },
  filePreview: {
    label: "Support files",
    listLabel: "Files",
    searchPlaceholder: "Search files…",
    readOnly: "read-only",
    emptyTitle: "No files match",
    emptySubtitle: "Try another file name or content search.",
    copyFile: "Copy file",
    fileCount: "{count} files",
    filteredFileCount: "{count}/{total} files",
    noMatches: "No files match.",
    navigate: "navigate",
    kind: {
      text: "Text",
      shell: "Shell",
      file: "File",
    },
  },
  mcpApp: {
    title: "MCP App",
    unavailable: "MCP App unavailable: {error}",
    errors: {
      gatewayUnavailable: "MCP App gateway unavailable",
      mountUnavailable: "MCP App mount unavailable",
      sandboxTimedOut: "MCP App sandbox timed out",
      sandboxUnavailable: "MCP App sandbox unavailable",
      initializationTimedOut: "MCP App initialization timed out",
      requestFailed: "Request failed",
      invalidSandboxUrl: "MCP App sandbox URL is invalid",
    },
  },
  githubPreview: {
    coAuthors: "Co-authored by {logins}",
    loading: "Loading GitHub details…",
    unavailable: "GitHub preview unavailable",
    states: {
      merged: "Merged",
      draft: "Draft",
      open: "Open",
      closed: "Closed",
      notPlanned: "Not planned",
    },
    file: "{count} file",
    files: "{count} files",
    comment: "{count} comment",
    comments: "{count} comments",
    pullRequest: "pull request",
    issue: "issue",
    ariaLabel: "{state} {kind} {repo} #{number}: {title}, by {author}",
  },
  sessionHovercard: {
    ariaLabel: "Session information",
    agentNotepad: "Agent Notepad",
    attributionOther: "& {count} other",
    attributionOthers: "& {count} others",
    moreParticipants: "+{count}",
    moreParticipantsLabel: "{count} more participants",
    projectLabel: "Project",
    workspaceLabel: "Workspace",
    branchLabel: "Branch",
    runsOn: "Runs on {providerId} · {profileId}",
    more: "+{count} more",
    changedFile: "{count} file",
    changedFiles: "{count} files",
    pullRequestLabel: "Pull request #{number}, {state}",
    pullRequestAuthorLabel: "Opened by {login}",
    states: {
      open: "Open",
      draft: "Draft",
      merged: "Merged",
      closed: "Closed",
    },
    checks: {
      passing: "CI checks passing",
      failing: "CI checks failing",
      pending: "CI checks running",
    },
  },
  sessionProgressCard: {
    title: "Progress",
    composerTitle: "Task progress",
    shortCount: "{completed} of {total}",
    noteLabel: "Progress note",
    dismiss: "Dismiss progress card",
    dismissFailed: "Could not dismiss the progress card. Try again.",
    widgetLabel: "Session progress",
    widgetLoading: "Loading session progress…",
    widgetEmpty: "No progress card yet",
    widgetUnavailable: "Session progress is unavailable.",
    widgetAccessDenied: "Select a session you can access or change sharing for this session.",
    countLabel: "{completed} of {total} completed",
    activity: {
      updated: "Updated {time}",
      completed: "Completed {time}",
      failed: "Failed {time}",
      stopped: "Stopped {time}",
    },
    outcome: {
      completed: "Completed",
      failed: "Failed",
      stopped: "Stopped",
    },
    stepLabel: "{step}, {status}",
    status: {
      completed: "completed",
      failed: "failed",
      inProgress: "in progress",
      paused: "paused",
      pending: "pending",
      stopped: "stopped",
    },
    receipt: {
      updating: "Updating progress",
      failed: "Progress update failed",
      updated: "Progress updated — {completed}/{total} · {current}",
      noteUpdated: "Progress note updated",
      cleared: "Progress cleared",
    },
  },
  channels: {
    lastError: "Last error",
    refreshingStaleSnapshot:
      "Refreshing channel status in the background; showing the last successful snapshot.",
    hub: {
      connectedTitle: "Connected channels",
      addTitle: "Add a channel",
      addSubtitle: "Pick a service and follow the guided setup.",
      updatedAgo: "Updated {ago}",
      noneConnected: "No channels connected yet. Pick one below to get started.",
      partialSnapshot: "Some channel checks did not finish before the UI budget.",
      stateRunning: "Running",
      stateConfigured: "Configured",
      stateAttention: "Needs attention",
      setUp: "Set up",
      guidedSetup: "Guided step-by-step setup",
      openDetails: "Open details",
      lastMessageAgo: "Last message {ago}",
      browseAllTitle: "More channels…",
      browseAllSubtitle: "Browse every available channel, including installable plugins.",
      runSetup: "Run setup",
      adminRequired: "Browsing only. Channel setup requires operator.admin access.",
      saveBeforeSetup:
        "You have unsaved channel config changes. Save or reload them before running guided setup.",
    },
    pairing: {
      title: "DM access requests",
      subtitle: "Review people waiting to send direct messages to pairing-protected channels.",
      channelFilter: "Channel",
      accountFilter: "Account",
      allChannels: "All channels",
      allAccounts: "All accounts",
      approve: "Approve",
      dismiss: "Dismiss",
      approveAria: "Approve {sender} for {channel}, account {account}",
      dismissAria: "Dismiss {sender} for {channel}, account {account}",
      requested: "Requested {ago}",
      expires: "Expires {ago}",
      senderDetails: "Sender details",
      missingPermission:
        "This connection does not have operator.pairing access, so DM requests cannot be reviewed.",
      noAccounts: "No configured channel accounts use DM sender pairing.",
      noRequests: "No pending DM access requests.",
      noFilteredRequests: "No pending requests match these filters.",
      limits:
        "Requests expire after {minutes} minutes. Each channel account can hold up to {count} pending requests.",
      detailTitle: "DM access",
      detailSubtitle: "People must be approved before their direct messages reach the agent.",
      pendingCount: "{count} pending",
      noPending: "No pending requests",
      review: "Review requests",
      approveDialogTitle: "Approve DM access",
      dismissDialogTitle: "Dismiss DM access request",
      approveExplanation:
        "This lets the sender talk to the agent in direct messages. It does not grant group access.",
      dismissExplanation:
        "This removes the current request but does not block the sender. They can request access again later.",
      notifyRequester: "Notify the requester after approval",
      makeCommandOwner: "Also make this sender the first command owner",
      commandOwnerHelp:
        "Command owners can run privileged commands and approve dangerous actions. This option is only available while no owner is configured.",
      commandOwnerNeedsAdmin:
        "No command owner is configured. This connection needs operator.admin to assign the first owner.",
      approvedNotice: "DM access approved.",
      approvedOwnerNotice: "DM access approved and the first command owner was configured.",
      approvedNotificationFailedNotice:
        "DM access approved, but the requester notification could not be delivered.",
      approvedOwnerFailedNotice:
        "DM access approved, but the first command owner could not be configured.",
      approvedFollowupsFailedNotice:
        "DM access approved, but requester notification and command-owner setup both failed.",
      dismissedNotice: "DM access request dismissed. The sender can request access again.",
    },
    setup: {
      dialogLabel: "Set up {channel}",
      title: "Set up {channel}",
      genericTitle: "a channel",
      subtitle: "Guided channel setup",
      starting: "Starting setup…",
      working: "Working…",
      sessionExpired:
        "This setup session expired after the Gateway restarted. Close this dialog, then start channel setup again.",
      continue: "Continue",
      finish: "Finish",
      copyText: "Copy",
      openLink: "Open link",
      viewDocs: "View docs",
      doneTitle: "Channel configured",
      doneBody:
        "Configuration saved. The gateway reloads the channel automatically; check its card for live status.",
      doneNoChangesTitle: "No changes made",
      doneNoChangesBody: "Setup finished without configuring a channel. Nothing was saved.",
      whatsappScanTitle: "Link WhatsApp by scanning the QR code",
      whatsappScanHelp:
        "On your phone, open WhatsApp → Settings → Linked devices → Link a device, then scan this code.",
      whatsappQrLoading: "Generating QR code…",
      whatsappQrHint: "Click Show QR to generate a pairing code.",
      whatsappQrAlt: "WhatsApp QR",
      whatsappLinked: "WhatsApp is linked and ready.",
      regenerateQr: "New QR code",
      linkLater: "Link later",
    },
    config: {
      schemaUnavailable: "Schema unavailable. Use Raw.",
      channelSchemaUnavailable: "Channel config schema unavailable.",
      loadingSchema: "Loading config schema…",
    },
    generic: {
      subtitle: "Channel status and configuration.",
    },
    discord: {
      title: "Discord",
      subtitle: "Bot status and channel configuration.",
    },
    googleChat: {
      title: "Google Chat",
      subtitle: "Chat API webhook status and channel configuration.",
    },
    imessage: {
      title: "iMessage",
      subtitle: "macOS bridge status and channel configuration.",
    },
    signal: {
      title: "Signal",
      subtitle: "signal-cli status and channel configuration.",
    },
    slack: {
      title: "Slack",
      subtitle: "Socket mode status and channel configuration.",
    },
    telegram: {
      title: "Telegram",
      subtitle: "Bot status and channel configuration.",
    },
    whatsapp: {
      title: "WhatsApp",
      subtitle: "Link WhatsApp Web and monitor connection health.",
      phoneNumber: "Phone number",
      loggedOut: "Logged out.",
      logoutConfirmTitle: "Log out of WhatsApp account {accountId}?",
      logoutConfirmMessage:
        "Logging out of account {accountId} stops its listener and deletes its saved credentials.",
      logoutNotCleared:
        "No stored WhatsApp session was cleared. It may already be absent, or its auth directory may require manual cleanup.",
    },
    gatewayUrlConfirmation: {
      title: "Change Gateway URL",
      subtitle: "This will reconnect to a different gateway server",
      warning: "Only confirm if you trust this URL. Malicious URLs can compromise your system.",
    },
    nostr: {
      title: "Nostr",
      subtitle: "Decentralized DMs via Nostr relays (NIP-04).",
      profile: "Profile",
      editProfile: "Edit Profile",
      profilePicture: "Profile picture",
      noProfile: "No profile set.",
      noProfileHint: 'Click "Edit Profile" to add your name, bio, and avatar.',
      name: "Name",
      displayName: "Display Name",
      about: "About",
      advanced: "Advanced",
      profilePicturePreview: "Profile picture preview",
      account: "Account",
      username: "Username",
      usernameHelp: "Short username (e.g., satoshi)",
      bio: "Bio",
      bioPlaceholder: "Tell people about yourself...",
      bioHelp: "A brief bio or description",
      displayNameHelp: "Your full display name",
      avatarUrl: "Avatar URL",
      avatarHelp: "HTTPS URL to your profile picture",
      bannerUrl: "Banner URL",
      bannerHelp: "HTTPS URL to a banner image",
      website: "Website",
      websiteHelp: "Your personal website",
      nip05Identifier: "NIP-05 Identifier",
      nip05Help: "Verifiable identifier (e.g., you@domain.com)",
      lightningAddress: "Lightning Address",
      lightningHelp: "Lightning address for tips (LUD-16)",
      placeholders: {
        username: "satoshi",
        displayName: "Satoshi Nakamoto",
        avatarUrl: "https://example.com/avatar.jpg",
        bannerUrl: "https://example.com/banner.jpg",
        website: "https://example.com",
        nip05: "you@example.com",
        lightningAddress: "you@getalby.com",
      },
      notices: {
        timeout:
          "Request timed out after 30 seconds; the server may still have applied the change — check the profile before retrying.",
        operationFailed: "{prefix}: {error}",
        updateFailed: "Profile update failed",
        updateFailedStatus: "Profile update failed ({status})",
        publishFailed: "Profile publish failed on all relays.",
        published: "Profile published to relays.",
        importFailed: "Profile import failed",
        importFailedStatus: "Profile import failed ({status})",
        importedFromRelays: "Profile imported from relays. Review and publish.",
        imported: "Profile imported. Review and publish.",
      },
    },
  },
  lazyView: {
    errorTitle: "Panel failed to load",
    genericSubtitle: "Something went wrong while loading this panel.",
    staleTitle: "A new version is available",
    staleSubtitle: "OpenClaw was updated in the background. Reload to get the latest panel.",
    reloading: "Reloading…",
    retry: "Retry",
    stylesFailed: "Styles failed to load, so the page may look broken.",
  },
  communityInvite: {
    cardLabel: "Join the OpenClaw community on Discord",
    artAlt: "A lobster beside the Discord mark on a lit seafloor pedestal",
    title: "Come build with us",
    body: "Ask anything, show what you're making, and find out what everyone else is building.",
    bodyGreeting: "Or just say hi.",
    action: "Join us on Discord",
    dismissForever: "Dismiss and don't show again",
    dismissFailed: "Invitation dismissed, but your preference couldn't be saved.",
  },
  updates: {
    adminRequired: "Administrator access is required to change update settings or start an update.",
    campaign: {
      countdown: "Updating in {time}",
      waitingForIdle: "Waiting for active work · forced update in {time}",
      held: "Update held · resumes in {time}",
      applying: "Applying update…",
    },
    holdOneHour: "Hold 1 h",
    reviewUpdate: "Review update",
    run: {
      title: "Update progress",
      phases: "Update phases",
      steps: "Installation steps",
      details: "Step details",
      noDetails: "No step details recorded yet.",
      verification: "Verification checks",
      report: "Update report",
      restarting: "Gateway restarting…",
      reconnecting: "Reconnecting automatically. Update progress is saved on the gateway.",
      progress: "{completed} of {total} phases complete",
      phase: {
        requested: "Requested",
        staging: "Staging",
        validating: "Validating",
        repairing: "Repairing",
        activating: "Activating",
        restarting: "Restarting",
        verifying: "Verifying",
        finished: "Finished",
      },
      step: {
        completed: "Complete",
        in_progress: "In progress",
        pending: "Pending",
        failed: "Failed",
        skipped: "Skipped",
      },
      oracle: {
        service: "Service",
        version: "Version",
        plugins: "Plugins",
        channels: "Channels",
        inference: "Inference",
      },
      oracleState: {
        pass: "Passed",
        warn: "Not verified",
        fail: "Failed",
        pending: "Pending",
      },
    },
    confirm: {
      action: "Update and restart",
    },
    target: {
      version: "v{version}",
      commitBehind: "{count} commit behind",
      commitsBehind: "{count} commits behind",
    },
    sidebar: {
      campaignTarget: "{status} · {target}",
      updating: "Updating Gateway…",
      availableTitle: "Update available",
      availableSummary: "A newer revision is ready.",
      blockedTitle: "Update blocked",
      blockedSummary: "The selected revision could not be applied to this checkout.",
      blockedWaiting: "Blocked · waiting for active work · {target}",
      waitingForIdle: "Waiting for active work",
    },
    dialog: {
      disconnected:
        "The Gateway disconnected during the update. This page reconnects on its own. If it stays disconnected, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair.",
      restarting:
        "The Gateway is restarting. This page disconnects and reconnects on its own. If it stays disconnected, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair.",
    },
    channel: {},
    installKind: {},
    page: {},
    coalescedRestart:
      "Update installed. A gateway restart is already in progress; status will refresh after it reconnects.",
    error: "Update error: {error}",
    status: "Update {status}: {reason}. {guidance}",
    verificationFailedWithIdentity:
      "Update finished, but the running install does not match the expected revision. Expected {expected}, running {actual}. Run `openclaw triage` on the Gateway host before retrying.",
    handoffTimeout:
      "Update completion was not confirmed. Run `openclaw triage` on the Gateway host and inspect the result before retrying.",
    outcomeUnknown:
      "The update outcome is unknown. Run `openclaw triage` on the Gateway host and inspect the result before retrying.",
    triage: {
      hostHint:
        "If Ask OpenClaw is unavailable, run `openclaw triage` on the Gateway host to open a local coding agent for diagnosis and repair. Diagnose the cause before retrying.",
    },
    report: {},
    failureReasons: {
      dirty: "Commit or stash changes, then retry.",
      noUpstream: "Set an upstream branch, then retry.",
      notGitInstall:
        "Not a git checkout. Run `openclaw update` from the CLI for a global reinstall.",
      notOpenclawRoot:
        "Run the update from an OpenClaw checkout or use the CLI global reinstall path.",
      depsInstallFailed: "Dependency install failed. Fix the install error and retry.",
      buildFailed: "Build failed. Fix the build error and retry.",
      buildDirty:
        "The selected revision's build changed checkout files. Retry with a revision that includes its generated artifacts.",
      uiBuildFailed: "The control UI rebuild failed. Fix the UI build error and retry.",
      globalInstallFailed:
        "The global package install did not verify on disk. Retry or reinstall from the CLI.",
      restartDisabled:
        "The update was not applied because gateway restarts are disabled. Enable restarts in config, then retry.",
      restartUnavailable:
        "This global install cannot be safely replaced while restarts are disabled and no supervisor is present.",
      restartUnhealthy:
        "The replacement process never became healthy. The previous process stayed up so you can recover.",
      restartRevisionMismatch:
        "The restarted Gateway is running a different revision. Check the service install root and retry.",
      restartRevisionUnavailable:
        "The restarted Gateway could not report its revision. Check the service install root and logs before retrying.",
      alreadyCurrent: "This checkout is already at its tracked upstream revision.",
      managedServiceHandoffAlreadyRunning:
        "Another managed update is already running. Wait for it to complete, then refresh update status.",
      managedServiceHandoffUnavailable:
        "Stop the foreground Gateway, run `openclaw update`, then launch it again. For automatic updates, install a managed Gateway service.",
      doctorFailed: "Doctor repair failed. Run `openclaw doctor --non-interactive` and retry.",
      managedServiceHandoffFailed:
        "The update helper stopped before finishing. Run `openclaw update` in the terminal to see why.",
      managedServiceHandoffSpawnFailed:
        "The Gateway could not start the update helper. Run `openclaw update` in the terminal instead.",
      managedServiceHandoffParentTimeout:
        "The Gateway stayed up too long for the update helper. Start the update again, or run `openclaw update`.",
      default: "See the gateway logs for the exact failure and retry once the cause is fixed.",
    },
    failedAtStep: "The update failed at {step}: {cause}.",
    succeededVersion: "Gateway updated to v{version}.",
    succeededCommit: "Gateway updated · now on {sha}.",
    succeeded: "Gateway updated and restarted.",
  },
  devices: {
    readOnly: {
      pairingRequired: "Browsing only. Device changes require operator.pairing access.",
      adminRequired:
        "Browsing only. Exec approvals and node bindings require operator.admin access.",
      pairingAndAdminRequired:
        "Browsing only. Device changes require operator.pairing; exec approvals and node bindings require operator.admin.",
    },
    pairing: {
      button: "Pair device",
      adminRequired: "Administrator access is required to create setup codes.",
      title: "Pair a device",
      subtitle: "Create a secure setup for a mobile app or node host.",
      noApp: "Don't have the app yet?",
      getApps: "Get the apps",
      generating: "Creating a secure setup code…",
      accessTitle: "Setup type",
      fullAccess: "Full access (recommended)",
      fullAccessHint:
        "Device capabilities plus complete Gateway controls, including settings and upgrades.",
      limitedAccess: "Limited access",
      limitedAccessHint:
        "Device capabilities, chat, and approvals without administrative controls.",
      nodeAccess: "Node host",
      nodeAccessHint: "Connect a computer as a command and capability host.",
      generateCode: "Create setup code",
      transportLimitedTitle: "Limited for network safety",
      transportLimitedHint:
        "This Gateway URL uses plaintext ws://. Use wss:// or Tailscale Serve, then create a new code for full access.",
      failed: "Could not create a setup code.",
      statusFailed: "Could not verify whether pairing completed.",
      loadFailed: "Could not load the pairing dialog. Check your connection and try again.",
      qrAlt: "OpenClaw mobile pairing QR code",
      qrUnavailable: "QR unavailable. Copy the setup code instead.",
      copySetupCode: "Copy setup code",
      nodeExpiresIn: "This setup link expires in {time}.",
      nodeExpired: "This setup link has expired. Create a new one.",
      newCode: "New code",
      showSetupCode: "Show setup code",
      pending: "Device requests waiting for review: {count}",
      review: "Review",
      waiting: "Official OpenClaw mobile apps connect automatically after scanning.",
      pairedTitle: "Device paired",
      deliveryUncertainTitle: "Pairing delivery could not be confirmed",
      deliveryUncertainHint:
        "The setup code is retired, but the device may not have received its credential. Check Manage devices, remove the device if needed, then create a new code.",
      fullAccessSummary: "Full access",
      nodeAccessSummary: "Node access",
      done: "Done",
      expiredTitle: "Setup code expired",
      generateNewCode: "Generate new code",
      nodeWaiting: "Run the command on the device, then review its pairing request here.",
      help: "Pairing help",
      helpNewTab: "Pairing help (opens in a new tab)",
      manageDevices: "Manage devices",
    },
    binding: {
      loadConfigHint: "Load config to edit bindings.",
      formModeHint: "Switch the Config tab to Form mode to edit bindings here.",
      execNodeBinding: "Exec node binding",
      execNodeBindingSubtitle: "Pin agents to a specific node when using exec host=node.",
      defaultBinding: "Default binding",
      defaultBindingHint: "Used when agents do not override a node binding.",
      node: "Node",
      anyNode: "Any node",
      noNodes: "No nodes with system.run available.",
      noAgents: "No agents found.",
      defaultAgent: "default agent",
      agent: "agent",
      usesDefault: "uses default ({node})",
      override: "override: {node}",
      binding: "Binding",
      useDefault: "Use default",
      any: "any",
      unavailable: "Unavailable",
    },
    inventory: {
      title: "Paired devices",
      connected: "connected",
      desktop: "Desktop",
      actions: "Actions",
      actionsName: "Actions for {name}",
      openDesktop: "Open desktop",
      copyDeviceId: "Copy device ID",
      deviceIdCopied: "Device ID copied",
      removeAction: "Remove…",
      editAlias: "Edit alias",
      lastKnown: "last known {time}",
      deviceIdLabel: "Device ID",
      remoteIpLabel: "Remote IP",
      scopesLabel: "Scopes",
      requestedAccessLabel: "Requested access",
      approvedAccessLabel: "Approved access",
      tokenRole: "Role",
      tokenStatus: "Status",
      tokenAge: "Age",
      desktopOpenWindow: "Open this desktop in a new window",
      desktopEnableHint:
        "Enable desktop.host.enabled: true in the node config and add desktop.stream to gateway.nodes.commands.allow, then restart both. The node reconnects with a pending reapproval for desktop.stream; approve it here or with openclaw nodes approve.",
      uptime: "up {time}",
      loadLabel: "load {load}",
      loadTitle: "Load averages (1 / 5 / 15 min): {averages} on {cores} cores",
      memoryTitle: "Memory: {used} used / {total} total",
      diskLabel: "{available} free",
      diskTitle: "Disk: {available} available / {total} total",
      summaryConnected: "{connected} of {total} connected",
      summaryPending: "{count} awaiting approval",
      cleanupStale: "Clean up {count} stale",
      pendingApproval: "Pending approval",
      empty: "No paired devices.",
      connectedWithoutPairing: "Connected without pairing",
      olderPairing: "{count} older pairing of {name}",
      olderPairings: "{count} older pairings of {name}",
      autoPaired: "auto-paired",
      versionDrift: "version drift",
      versionDriftTitle:
        "Device {nodeVersion}; Gateway {gatewayVersion}. Update the older component to align the fleet.",
      workerVersion: "Worker {version}",
      workerMissing: "worker missing",
      workerMissingTitle:
        "The Gateway-managed worker bundle is missing. Start a new session on this device to reinstall it.",
      manualWake: "manual wake required",
      manualWakeTitle:
        "The Gateway cannot wake an offline Windows device. Start the machine or restore its network connection.",
      approvalNeeded: "approval needed",
      inputAgo: "input {time} ago",
      seen: "seen {time}",
      approved: "approved {time}",
      details: "Details",
      deviceId: "Device ID: {id}",
      tokens: "Tokens",
      capabilities: "Capabilities",
      commands: "Commands",
      offline: "offline",
      approve: "Approve",
      reject: "Reject",
      remove: "Remove",
      removePromptTitle: "Remove {name}?",
      removePromptBody: "This device must pair again before it can reconnect.",
      removeStalePromptTitle: "Remove {count} stale pairings?",
      removeStalePromptTitleOne: "Remove 1 stale pairing?",
      removeStalePromptBody: "Affected clients re-pair silently on their next connection.",
      renameTitle: "Rename {name}",
      renamePrompt: "Shown instead of the device's hostname on this Gateway.",
      rejectDevicePromptTitle: "Reject this device pairing request?",
      rejectNodePromptTitle: "Reject this node pairing request?",
      rejectPromptBody: "The client must send a new pairing request before it can connect.",
      revokePromptTitle: "Revoke the {role} token?",
      revokePromptBody: "This token stops working immediately and cannot be restored.",
      rotatePromptTitle: "New {role} token",
      rotatePromptBody:
        "Copy this token now and store it securely. It is shown once and cannot be recovered.",
      rotateAcknowledge: "I saved this token",
      rotateDismissHint: "This dialog stays open until you confirm the token is saved.",
      rotateWithheldTitle: "Token rotated · {device}",
      rotateWithheldNext:
        "It will reconnect with the new token automatically — nothing else to do.",
      rotateWithheldException: "If it doesn't reconnect on its own, pair it again.",
      rotateWithheldNote: "For security, the new token is only revealed on the device itself.",
      gateway: "gateway",
      unpaired: "unpaired",
      unknownClient: "unknown client",
      revoked: "revoked",
      active: "active",
      rotate: "Rotate",
      revoke: "Revoke",
      none: "none",
      rolesAndScopes: "roles: {roles} · scopes: {scopes}",
      scopeUpgrade: "scope upgrade requires approval",
      roleUpgrade: "role upgrade requires approval",
      reapproval: "reconnect details changed; approval required",
      newPairing: "new device pairing request",
      repair: "repair",
      requestedAt: "{note} · requested {time}",
    },
    // Lazy: en-devices.ts registers this subtree when the Devices page renders chips.
    capabilities: {},
    execApprovals: {
      title: "Exec approvals",
      subtitlePrefix: "Allowlist and approval policy for",
      loadHint: "Load exec approvals to edit allowlists.",
      hostNativePolicy: "Host-native policy",
      hostNativeHint: "Read-only here. Edit from the companion app or CLI.",
      native: "Native",
      defaultAction: "Default action",
      rule: "{count} rule",
      rules: "{count} rules",
      allShells: "all shells",
      off: "off",
      on: "on",
      target: "Target",
      targetHint: "Gateway edits local approvals; node edits the selected node.",
      host: "Host",
      gateway: "Gateway",
      node: "Node",
      selectNode: "Select node",
      noNodes: "No nodes advertise exec approvals yet.",
      scope: "Scope",
      defaults: "Defaults",
      security: "Security",
      defaultSecurity: "Default security mode.",
      defaultValue: "Default: {value}.",
      mode: "Mode",
      useDefaultValue: "Use default ({value})",
      ask: "Ask",
      defaultPrompt: "Default prompt policy.",
      askFallback: "Ask fallback",
      promptUnavailable: "Applied when the UI prompt is unavailable.",
      fallback: "Fallback",
      autoAllowSkills: "Auto-allow skill CLIs",
      autoAllowSkillsHint: "Allow skill executables listed by the Gateway.",
      usingDefault: "Using default ({value}).",
      override: "Override ({value}).",
      useDefault: "Use default",
      allowlist: "Allowlist",
      allowlistHint: "Case-insensitive glob patterns.",
      addPattern: "Add pattern",
      emptyAllowlist: "No allowlist entries yet.",
      newPattern: "New pattern",
      lastUsed: "Last used: {time}",
      pattern: "Pattern",
      remove: "Remove",
      options: {
        deny: "Deny",
        allowlist: "Allowlist",
        full: "Full",
        off: "Off",
        onMiss: "On miss",
        always: "Always",
      },
    },
  },
  worktrees: {
    adminRequired: "Browsing only. Worktree changes require operator.admin access.",
    newWorktree: "New worktree",
    ownerManual: "Manual",
    ownerSession: "Session",
    ownerWorkboard: "Workboard",
    title: "Managed Worktrees",
    subtitle: "Isolated repository checkouts owned by OpenClaw.",
    cleanNow: "Clean up now",
    name: "Name",
    namePlaceholder: "auto",
    baseBranch: "Base branch",
    repo: "Repository",
    empty: "No managed worktrees.",
    restorable: "Restorable",
    restore: "Restore",
    confirmDelete: "Snapshot and delete {name}?",
    confirmForceDelete: "Snapshot failed: {error}\n\nDelete without a snapshot?",
  },
  agentChip: agentEn.agentChip,
  agentScope: agentEn.agentScope,
  newSession: {
    title: "New session",
    hint: "Pick where this session works, then say what to do.",
    agent: "Agent",
    agents: "Agents",
    where: "Where",
    environments: "Environments",
    gateway: "Gateway · local",
    gatewayNamed: "Gateway · {name}",
    cloudWorker: "Cloud · {profile}",
    cloudWorkerMachine: "{profile} · {machine}",
    cloudWorkerProvider: "Cloud worker provider: {provider}",
    cloudRuntimeUnsupported: "The {runtime} runtime does not support cloud workers.",
    cloudProfileRuntimeUnsupported:
      "The {runtime} runtime cannot use this cloud worker. Choose a compatible cloud worker or run locally.",
    deviceRuntimeUnsupported: "This runtime does not support paired devices",
    cloudRecoveryUnavailable:
      "Cloud workers are unavailable because this connection does not support task recovery. Reconnect or update the Gateway.",
    placementStartFailed: "The session was created, but runner startup failed: {error}",
    placementReloadBlocked: "Recovery needs a reload. Unsaved starts will be lost.",
    discardUnsavedAndReload: "Discard unsaved starts and reload",
    placementSyncsFolder: "Syncs {folder} to the selected runner",
    placementClonesRepository:
      "Clones {folder} on the selected runner. No Gateway checkout is created.",
    folder: "Folder",
    folderPlaceholder: "Agent workspace",
    yourDevices: "Your devices",
    autoDevice: "Auto",
    autoDeviceSub: "Least-busy device",
    autoDeviceSubEligible: "First eligible device",
    neverConnected: "Never connected",
    offlineFor: "Offline for {duration}",
    lastSeen: "Last seen {time}",
    nodeUpdateRequired:
      "Update required: run {updateCommand}, then reconnect. For a headless node, run {restartCommand}.",
    nodeCommandPendingApproval:
      "Ask an administrator to approve the pending {command} request, or pick another device.",
    nodeCommandUndeclared:
      "Make {command} available on this device, then reconnect, or pick another device.",
    nodeCommandUnauthorized:
      "Authorize {command} in the Gateway node command policy, or pick another device.",
    capabilityCamera: "Camera",
    capabilityLocation: "Location",
    capabilityTalk: "Talk",
    capabilityScreenCapture: "Screen capture",
    capabilityCanvas: "Canvas",
    capabilityVoice: "Voice",
    environmentDisposable: "Disposable",
    environmentPersistent: "Persistent",
    projects: "Projects",
    projectsAdminHint: "Admins can register projects from Browse folders",
    projectSearchPlaceholder: "Search projects or paste a Git URL",
    githubProjects: "GitHub",
    githubTokenHint:
      "No Control UI GitHub credential or shared Gateway environment token is configured; public GitHub results only.",
    cloneProject: "Clone",
    registerProject: "Register as project",
    cloud: "Cloud",
    machine: "Machine",
    machineShape: "{cpu} vCPU · {memory} GB",
    machineCpu: "{cpu} vCPU",
    machineMemory: "{memory} GB",
    machineDefault: "Default",
    recentFolders: "Recent",
    runsOn: "Runs on {place}",
    browse: "Browse folders",
    browseRequiresAdmin:
      "To browse outside agent workspaces, open Inbox, select Limited access, request admin, then approve in Devices.",
    connectMachine: "Connect a machine…",
    browserUp: "Parent folder",
    browserUse: "Use this folder",
    browserEmpty: "No subfolders",
    browserNoMatches: "No matching folders",
    browserLoadFailed: "Couldn't list that folder.",
    hiddenFolder: "Hidden folder",
    worktree: "Worktree",
    checkingGit: "Checking Git availability…",
    gitCheckUnavailable: "Couldn't verify Git for this folder. Choose it again to retry.",
    worktreeUnavailable: "Selected folder is not a Git checkout",
    worktreeBaseRef: "From",
    worktreeName: "Name",
    worktreeNamePlaceholder: "Named from the session title",
    worktreeBranchNote: "Creates branch openclaw/<name> in a separate checkout.",
    worktreeNameInvalid: "Use lowercase letters, digits, and dashes.",
    incognito: "Incognito",
    incognitoDescription: "Keep this session only until the Gateway restarts",
    draft: "Draft",
    draftDescription: "Keep this session to yourself until you publish it",
    messagePlaceholder: "What should this session work on?",
    dictate: "Dictate",
    readingAttachment: "Reading attachment",
    start: "Start session",
    startInTerminal: "Start in terminal",
    nativeTerminalHint:
      "Start the native CLI on the selected machine using its own account and configuration. This does not create an OpenClaw Chat.",
    chooseNativeHost: "Choose a native CLI host",
    nativeHostsUnavailable:
      "No native CLI is available. Install it on the Gateway or connect a machine with CLI access, then reconnect to the Gateway.",
    nativeTerminalPrompt: "Optional initial prompt for the native CLI",
    terminalNodeFolder: "Existing absolute folder on this node",
    terminalAttachmentsUnsupported:
      "Remove attachments before starting a native CLI. Add files from the terminal after it opens.",
    starting: "Starting…",
    createFailed: "Couldn't create the session.",
    createOutcomeUnknown:
      "The Gateway changed while this session was starting. Check recent sessions before starting this task again.",
    cliAgentsGroup: "CLI agents",
    cliAgentsUnavailable: "CLI agents unavailable",
    placementSetupInterrupted:
      "This session's runner setup was interrupted. Check recent sessions before starting this task again.",
    catalogUnavailable: "This session target is unavailable.",
    placementNotReady: "The selected runner isn't ready yet. Try again in a moment.",
    nodeUnavailable: "The selected device is unavailable. Pick another place.",
    terminalCapabilityOverridesUnsupported:
      "Clear session capability overrides before starting in a terminal.",
    what: "What",
    checkout: "Checkout",
    checkoutCurrent: "Current checkout",
    checkoutCurrentNote: "Works in the selected folder on its current branch.",
    checkoutWorktree: "New worktree",
    checkoutWorktreeSub: "Isolated copy of the repo",
    checkoutWorktreeFrom: "New worktree from {branch}",
    checkoutRepository: "Remote checkout",
    checkoutRepositoryFrom: "Remote checkout from {branch}",
    checkoutRemoteLocked: "Devices and cloud run in a worktree",
    local: "Local",
  },
  dashboardsPage: {
    emptyTitle: "No dashboards yet",
    emptyDescription: "Dashboards created in your tasks will appear here.",
    loadError: "Could not load dashboards: {error}",
    openFocusMode: "Open dashboard in focus mode",
    live: "Live",
    byAuthor: "By {author}",
    updated: "Updated {time}",
    updatedUnknown: "Update time unknown",
    totalLabel: "dashboards",
    searchLabel: "Search dashboards",
    searchPlaceholder: "Search dashboards…",
    authorFilter: "Author",
    allAuthors: "All authors",
    sortLabel: "Sort",
    sortUpdated: "Recently updated",
    sortTitle: "Title A–Z",
    resultCount: "{count} dashboards",
    noResultsTitle: "No matching dashboards",
    noResultsDescription: "Try another search or author.",
  },
  dashboardDocument: {
    close: "Close dashboard",
    missingSession: "No dashboard session was specified.",
    notFound: "This session could not be found.",
    unavailable: "Session dashboards are unavailable for this connection.",
    loadFailed:
      "Could not load this dashboard: {error}. Check the Gateway connection and try again.",
  },
  sessionsView: {
    subagentPrefix: "Subagent:",
    automationPrefix: "Automation:",
    actionRequiresConnection: "Connect to the Gateway to change sessions.",
    actionUnavailable: "This Gateway does not support this session action.",
    actionRequiresRead: "This action requires operator.read access.",
    actionRequiresWrite: "This action requires operator.write access.",
    actionRequiresAdmin: "This action requires operator.admin access.",
    deletePreservedReasons: {
      "owner-mismatch": "owned elsewhere",
      busy: "live run or cleanup active",
      "foreign-lock": "foreign Git lock",
      "snapshot-failed": "OpenClaw could not create a safety snapshot",
      "cleanup-failed": "cleanup failed",
    },
    draftCleanupFailed: "Session deleted; browser draft remains. Clear site data.",
    title: "Sessions",
    subtitle: "Browse sessions and manage per-session overrides.",
    store: "Store: {path}",
    active: "Updated within",
    limit: "Limit",
    createdBy: "Created by {name}",
    ownedBy: "Owned by {name}",
    archivedBy: "Archived by {name}",
    archiveReason: "Archive reason",
    archiveReasonManual: "Archived manually",
    archiveReasonAgeRetention: "Automatically archived by age retention",
    archiveReasonActiveSessionCap:
      "Automatically archived because the active-session limit was reached",
    archiveReasonStaleDashboard: "Automatically archived after dashboard inactivity",
    archiveReasonRestartRecovery: "Archived when a replacement session was recovered",
    viewingNow: "viewing now",
    owners: "Owners",
    allOwners: "All owners",
    involvingMe: "Involving me",
    specificOwner: "Specific owner",
    specificOwnerAvailable: "Specific owner: {count} available",
    specificOwnerSelected: "Specific owner: {name}",
    ownerYou: "{name} (You)",
    withParticipant: "with {name}",
    withMoreParticipants: "+{count} more",
    assignToMe: "Me",
    assignTo: "Assign to…",
    filterControls: "Session filters",
    filters: "Filters",
    sourceFilters: "Session source filters",
    global: "Global",
    unknown: "Unknown",
    sessionState: "Session state",
    all: "All",
    sessionArchived: "Session archived",
    sessionsArchived: "Archived {count} sessions",
    deleteAllArchived: "Delete all archived…",
    deleteAllArchivedConfirm:
      "Delete {count} archived sessions and their transcripts? Any attached workers will be stopped safely first.",
    activeTooltip: "Loads sessions updated in the last {count} minutes.",
    limitTooltip: "Max sessions to load.",
    globalTooltip: "Include global sessions.",
    unknownTooltip: "Include unknown sessions.",
    archivedOnlyTooltip: "Show only archived sessions.",
    minutesPlaceholder: "min",
    searchPlaceholder: "Filter by key, agent, label, kind…",
    transcriptSearchTitle: "Search transcripts",
    transcriptSearchDescription:
      "Find exact words or phrases in user and assistant messages across the default agent's sessions.",
    transcriptSearchInputLabel: "Search session transcripts",
    transcriptSearchPlaceholder: "Search exact words or phrases…",
    transcriptSearchAction: "Search",
    transcriptSearchClear: "Clear",
    transcriptSearchRetry: "Retry",
    transcriptSearchSearching: "Searching transcripts…",
    transcriptSearchUnavailable: "Transcript search requires a newer Gateway.",
    transcriptSearchError: "Transcript search failed",
    transcriptSearchIndexing:
      "The transcript index is still updating. Retry to include recent messages.",
    transcriptSearchEmpty: "No transcript messages match that search.",
    transcriptSearchMatches: "Transcript matches: {count}",
    transcriptSearchTruncated: "Showing the first 25 matches.",
    user: "User",
    assistant: "Assistant",
    selected: "{count} selected",
    deleteSelected: "Delete",
    selectAllOnPage: "Select all on page",
    selectSession: "Select session",
    optionalPlaceholder: "(optional)",
    key: "Key",
    label: "Label",
    kind: "Kind",
    updated: "Updated",
    tokens: "Tokens",
    compaction: "Compaction",
    goal: "Goal",
    goalNote: "Goal note",
    thinking: "Thinking",
    fast: "Fast",
    verbose: "Verbose",
    reasoning: "Reasoning",
    actions: "Actions",
    addToWorkboard: "Add to Workboard",
    openWorkboardCard: "Open Workboard card",
    opensAsDashboard: "Opens as dashboard",
    approvalNeeded: "Approval needed",
    messageNeedsAttention: "{count} message needs attention",
    messagesNeedAttention: "{count} messages need attention",
    unsentDraft: "Unsent draft",
    noSessions: "No sessions found.",
    noActiveSessions: "No active sessions.",
    noArchivedSessions: "No archived sessions.",
    noSessionsMatchFilters: "No sessions match your filters.",
    pagination: "{start}-{end} of {total} rows",
    pageSize: "Rows per page",
    rowsPerPage: "{count} per page",
    showAll: "Show all",
    inherit: "inherit",
    offExplicit: "off (explicit)",
    auto: "auto",
    on: "on",
    off: "off",
    full: "full",
    stream: "stream",
    manual: "manual",
    autoThreshold: "auto-threshold",
    overflowRetry: "overflow retry",
    timeoutRetry: "timeout retry",
    tokenRange: "{before} to {after} tokens",
    tokensBefore: "{count} tokens before",
    tokenDeltaUnavailable: "token delta unavailable",
    contextUsage: "{percent}% of context used ({used} / {context} tokens)",
    contextUsageApprox: "~{percent}% of context used ({used} / {context} tokens, approximate)",
    checkpoints: "{count} Checkpoints",
    checkpoint: "{count} Checkpoint",
    showSessionDetails: "Show session details for {count}",
    hideSessionDetails: "Hide session details for {count}",
    sessionDetails: "Session details",
    overrides: "Overrides",
    compactionHistory: "Compaction history",
    status: "Status",
    statusLive: "Live",
    statusIdle: "Idle",
    statusUnknown: "Unknown",
    statusQueued: "Queued",
    statusRunning: "Running",
    statusDone: "Done",
    statusFailed: "Failed",
    statusKilled: "Killed",
    statusTimeout: "Timed out",
    waitingForAnswer: "Waiting for your answer",
    waitingForApproval: "Waiting for approval",
    runFailedReason: "Run failed: {reason}",
    runErrorTimedOut: "Timed out",
    runErrorUnknown: "Unknown error",
    attentionRequired: "Session needs attention",
    openSession: "Open session",
    model: "Model",
    provider: "Provider",
    runtime: "Runtime",
    runDuration: "Run duration",
    lastActive: "Last active {time}",
    surface: "Surface",
    subject: "Subject",
    room: "Room",
    space: "Space",
    sessionId: "Session ID",
    activeRun: "Active run",
    showChildSessions: "Show {count} child sessions for {session}",
    hideChildSessions: "Hide {count} child sessions for {session}",
    childSessions: "Child sessions",
    showMoreChildren: "Show {count} more",
    archived: "Archived",
    pinned: "Pinned",
    unread: "Unread",
    automationAttached: "Automation attached",
    incognito: "Incognito session",
    cloudWorkerPlacement: "Placement: {state}",
    placementWorkspaceConflict: "{placement} · 1 workspace conflict",
    placementWorkspaceConflicts: "{placement} · {count} workspace conflicts",
    cloudWorkerDiskWarning: "Cloud session disk space is low",
    cloudWorkerDiskCritical: "Cloud session disk space is critically low",
    cloudWorkerDescendantConflict: "Cloud worker child: 1 workspace conflict",
    cloudWorkerDescendantConflicts: "Cloud worker children: {count} workspace conflicts",
    renameSession: "Rename session",
    renameSessionPrompt: "Rename session",
    renameSessionMenu: "Rename…",
    setIconMenu: "Set icon",
    setColorMenu: "Color",
    setIconColorMenu: "Icon & color",
    sessionColor: "Session color: {color}",
    colors: {
      red: "Red",
      blue: "Blue",
      green: "Green",
      yellow: "Yellow",
      purple: "Purple",
      orange: "Orange",
      pink: "Pink",
      cyan: "Cyan",
    },
    iconEmojiSection: "Emoji",
    iconGlyphSection: "Icons",
    customEmojiCell: "Custom emoji…",
    customEmojiTitle: "Custom emoji",
    customEmojiSet: "Set",
    customEmojiHint: "Any emoji works. Press {shortcut} for the system emoji picker.",
    customEmojiHintNoShortcut: "Any emoji works.",
    removeIcon: "Remove icon",
    pinSession: "Pin session",
    pinRootSessionsOnly: "Only root sessions can be pinned; pin the parent session instead.",
    unpinSession: "Unpin session",
    markUnread: "Mark as unread",
    markRead: "Mark as read",
    markUnreadCount: "Mark {count} as unread",
    markReadCount: "Mark {count} as read",
    forkSession: "Fork conversation",
    forkFromLastCompleted: "Fork from last completed message",
    forkedSession: "Forked session",
    copySessionId: "Session ID",
    copySessionLink: "Session link",
    copySessionPreviewLink: "Preview link",
    copyMarkdown: "Conversation as Markdown",
    openNewTab: "New tab",
    openNewWindow: "New window",
    splitBelow: "Split below",
    workspaceEditors: "Workspace · editor",
    resetAppearance: "Reset to default",
    copyTranscriptChanged: "Conversation changed while copying. Try again.",
    openWindowBlocked: "Allow pop-ups for this site, then try again.",
    splitUnavailable: "Open a conversation on a wide screen to use split view.",
    openPullRequest: "Open PR",
    openInEditorMenu: "Open in",
    archiveSession: "Archive session",
    archiveSessionCount: "Archive {count}",
    restoreSession: "Restore session",
    restoreSessionCount: "Restore {count}",
    moveSession: "Move session…",
    moveSessionTitle: "Move session",
    moveSessionDescription: 'Choose where "{session}" should continue.',
    moveSessionNoReplayWarning:
      "OpenClaw safely reconciles the current workspace before moving. Active work is never replayed.",
    moveSessionActiveRunWarning:
      "The active turn will be interrupted. Partial output is not replayed; send the next turn again after the move.",
    moveSessionAction: "Move session",
    moveSessionCatalogFailed: "Could not load available destinations.",
    moveSessionFailed: "Move failed",
    moveSessionGatewayTarget: "Gateway",
    movingSession: "Moving to {target}…",
    movingSessionGeneric: "Moving session…",
    stopCloudWorker: "Stop cloud worker…",
    stopCloudWorkerConfirm: 'Stop the cloud worker for "{session}"?',
    stopCloudWorkerConfirmAction: "Stop worker",
    initialTurnPausedByWorkerStop:
      "Worker stop requested. Review the initial message before retrying.",
    stopCloudWorkerStale:
      'Gateway connection replaced before the cloud worker for "{session}" was stopped. Try again.',
    deleteSessionMenu: "Delete…",
    deleteSessionCount: "Delete {count}…",
    deleteSessionConfirm:
      'Delete "{session}" and its transcript? Any attached worker will be stopped safely first.',
    deleteSessionStale: 'Gateway connection replaced before "{session}" was deleted. Try again.',
    deleteSessionsConfirm:
      "Delete {count} sessions and their transcripts? Any attached workers will be stopped safely first.",
    deleteSessionsStale:
      "Gateway connection replaced before {count} sessions were deleted. Try again.",
    deleteSelectedConfirmOne:
      "Delete 1 session?\n\nStop any attached worker safely, then delete the session entry and archive its transcript.",
    deleteSelectedConfirm:
      "Delete {count} sessions?\n\nStop any attached workers safely, then delete the session entries and archive their transcripts.",
    groupBy: "Group by",
    groupByNone: "None",
    groupByCategory: "Custom groups",
    groupByPerson: "Person",
    showSessionPreview: "Show message preview",
    hideEmptyGroups: "Hide empty groups",
    showCronSessions: "Show automation sessions",
    showSystemSessions: "Show system sessions",
    groupByChannel: "Channel",
    groupByKind: "Kind",
    groupByAgent: "Agent",
    groupByDate: "Date",
    group: "Group",
    ungrouped: "Ungrouped",
    newGroup: "New group",
    newGroupPrompt: "New group name",
    newGroupTitle: "New group",
    newGroupCreate: "Create group",
    newGroupFailed: "Could not create the group.",
    newGroupStale: "Gateway connection replaced before the group was saved. Try again.",
    newGroupMoveSkipped:
      "Group created, but the move was skipped because the list changed. Move from the row menu.",
    moveToGroup: "Move session to a group",
    moveToGroupMenu: "Move to group",
    moveToGroupMenuCount: "Move {count} to group",
    removeFromGroup: "Remove from group",
    moveBackToGroups: "Move back to Groups",
    groupMenu: "Group options for {group}",
    newSessionInGroup: "New session in {group}",
    groupDefaultsMenu: "New session defaults",
    groupDefaultsTitle: 'New session defaults for "{group}"',
    groupDefaultsDescription: "Choose where new sessions in this group start.",
    groupDefaultsCwd: "Working directory",
    groupDefaultsCwdPlaceholder: "Use the agent workspace",
    groupDefaultsCwdHint: "Leave empty to use the selected agent's workspace.",
    groupDefaultsMode: "Environment",
    groupDefaultsLocal: "Current checkout",
    groupDefaultsWorktree: "New worktree",
    groupDefaultsWorktreeHint: "Runs each session in an isolated Git worktree.",
    groupDefaultsFailed: "Could not save the group defaults.",
    groupDefaultsStale: "Gateway connection replaced before the defaults were saved. Try again.",
    renameGroupMenu: "Rename group",
    renameGroupTitle: 'Rename group "{group}"',
    groupNameLabel: "Group name",
    deleteGroupMenu: "Delete group",
    deleteGroupTitle: 'Delete group "{group}"',
    deleteGroupConfirm: "The group is removed. Its sessions move back to the session list.",
    deleteGroupStale: 'Gateway connection replaced before "{group}" was deleted. Try again.',
    dragSessionHint: "Drag session to move between groups",
    dateToday: "Today",
    dateYesterday: "Yesterday",
    dateThisWeek: "This week",
    dateOlder: "Older",
    dateNoActivity: "No activity",
    groupRowCount: "{count} sessions",
    groupRowCountOne: "{count} session",
    loadingCheckpoints: "Loading checkpoints…",
    noCheckpoints: "No compaction checkpoints recorded for this session.",
    noSummary: "No summary captured.",
    branchFromCheckpoint: "Branch from checkpoint",
    branchCheckpointConfirm: "Create a new child session from this compacted checkpoint?",
    restoreCheckpoint: "Restore checkpoint",
    restoreCheckpointConfirm:
      "Restore this session to the selected compacted checkpoint?\n\nThis replaces the current active transcript for the session key.",
  },
  agents: {
    noAgents: "No agents",
    createdBy: "Created by {id}",
    defaults: {
      title: "Agent defaults",
      description: "Defaults every agent inherits unless overridden.",
    },
    copyId: "Copy ID",
    default: "Default",
    setDefault: "Set Default",
    ...agentEn.pinning,
    selectTitle: "Select an agent",
    selectSubtitle: "Pick an agent to inspect its workspace and tools.",
    tabs: {
      overview: "Overview",
      files: "Files",
      tools: "Tools",
      skills: "Skills",
      channels: "Channels",
      cronJobs: "Automations",
      memory: "Memory",
    },
    context: {
      title: "Agent Context",
      workspace: "Workspace",
      openFilesTab: "Open Files tab",
      primaryModel: "Primary Model",
      runtime: "Runtime",
      thinkingDefault: "Thinking Default",
      identityName: "Identity Name",
      identityAvatar: "Identity Avatar",
      skillsFilter: "Skills Filter",
      default: "Default",
      configurationSubtitle: "Workspace, identity, and model configuration.",
      schedulingSubtitle: "Workspace and scheduling targets.",
    },
    identity: agentEn.identity,
    overview: {
      title: "Overview",
      subtitle: "Workspace paths and identity metadata.",
      selectedSkills: "{count} selected",
      allSkills: "all skills",
      unsavedConfig: "You have unsaved config changes.",
      modelSelection: "Model Selection",
      primaryModel: "Primary model",
      primaryModelDefault: "Primary model (default)",
      notSet: "Not set",
      inheritDefault: "Inherit default",
      inheritDefaultModel: "Inherit default ({model})",
      fallbacks: "Fallbacks",
    },
    toolCatalog: {
      groups: {
        files: "Files",
        runtime: "Runtime",
        web: "Web",
        memory: "Memory",
        sessions: "Sessions",
        ui: "UI",
        messaging: "Messaging",
        automation: "Automation",
        nodes: "Nodes",
        agents: "Agents",
        media: "Media",
      },
      descriptions: {
        read: "Read file contents",
        write: "Create or overwrite files",
        edit: "Make precise edits",
        applyPatch: "Patch files (OpenAI)",
        exec: "Run shell commands",
        process: "Manage background processes",
        webSearch: "Search the web",
        webFetch: "Fetch web content",
        memorySearch: "Semantic search",
        memoryGet: "Read memory files",
        sessionsList: "List sessions",
        sessionsHistory: "Session history",
        sessionsSend: "Send to session",
        sessionsSpawn: "Spawn sub-agent",
        sessionStatus: "Session status",
        browser: "Control web browser",
        canvas: "Control canvases",
        message: "Send messages",
        cron: "Schedule tasks",
        gateway: "Gateway control",
        nodes: "Nodes + devices",
        agentsList: "List agents",
        image: "Image understanding",
      },
      profiles: {
        minimal: "Minimal",
        coding: "Coding",
        messaging: "Messaging",
        full: "Full",
      },
    },
    skillsPanel: {
      title: "Skills",
      subtitle: "Per-agent skill allowlist and workspace skills.",
      loadConfig: "Load the gateway config to set per-agent skills.",
      customAllowlist: "This agent uses a custom skill allowlist.",
      inheritedAllowlist: "This agent inherits the default skill allowlist.",
      allEnabled: "All skills are enabled. Disabling any skill will create a per-agent allowlist.",
      loadAgent: "Load skills for this agent to view workspace-specific entries.",
      filter: "Filter",
      searchPlaceholder: "Search skills",
      shown: "{count} shown",
      empty: "No skills found.",
      missing: "Missing: {items}",
      reason: "Reason: {items}",
      updateError: "Could not update the agent skill allowlist.",
    },
    channels: {
      title: "Channels",
      subtitle: "Gateway-wide channel status snapshot.",
      lastRefresh: "Last refresh: {time}",
      loadHint: "Load channels to see live status.",
      empty: "No channels found.",
      connectedCount: "{connected}/{total} connected",
      noAccounts: "no accounts",
      configuredCount: "{count} configured",
      notConfigured: "not configured",
      enabledCount: "{count} enabled",
      setupGuide: "Setup guide",
    },
    cronPanel: {
      schedulerTitle: "Scheduler",
      schedulerSubtitle: "Gateway automations status.",
      jobs: "Jobs",
      nextWake: "Next wake",
      agentJobsTitle: "Agent Automations",
      agentJobsSubtitle: "Scheduled jobs targeting this agent.",
      noJobs: "No jobs assigned.",
      runNow: "Run Now",
    },
    files: {
      emptyDraft: "Empty draft",
      minRead: "{count} min read",
      markdownPreview: "Markdown Preview",
      extensionPreview: "{ext} Preview",
      preview: "Preview",
      expandPreview: "Expand preview",
      collapsePreview: "Collapse preview",
      editFile: "Edit file",
      closePreview: "Close preview",
      coreFilesTitle: "Core Files",
      coreFilesSubtitle: "Bootstrap persona, identity, and tool guidance.",
      workspace: "Workspace",
      loadHint: "Load the agent workspace files to edit core instructions.",
      empty: "No files found.",
      missing: "missing",
      selectFile: "Select a file to edit.",
      willCreateOnSave: "Will Create on Save",
      liveDraftPreview: "Live Draft Preview",
      savedPreview: "Saved Preview",
      updated: "Updated {time}",
      notCreatedYet: "Not Created Yet",
      updatedUnknown: "Updated Unknown",
      missingHint: "This file is missing. Saving will create it in the agent workspace.",
      addFile: "Add file…",
      createHint: "This file does not exist yet. Saving will create it in the agent workspace.",
      content: "Content",
      words: "{count} words",
      lines: "lines",
    },
  },
  debug: {
    overlay: {
      title: "System busyness",
    },
  },
  configForm: {
    redactedPlaceholder: "[redacted - click reveal to view]",
    sectionHelp: "Help for {section}",
    advancedDivider: "Advanced",
    advancedSettings: "Advanced settings",
    hideValue: "Hide value",
    revealValue: "Reveal value",
    disableStreamToReveal: "Disable stream mode to reveal value",
    storedSecretNotRevealable:
      "Stored secrets are never sent to the browser; enter a new value to replace it",
    unsupportedType: "Unsupported type: {type}. Use Raw mode.",
    structuredSecretRaw: "Structured value (SecretRef) - use Raw mode to edit",
    structuredSecretFile: "Structured value (SecretRef) - edit the config file directly",
    defaultValue: "Default: {value}",
    usingDefault: "Using default: {value}",
    select: "Select...",
    enumOn: "On",
    enumOff: "Off",
    enumAuto: "Auto",
    nullValue: "null",
    jsonValue: "JSON value",
    invalidJson: "Enter valid JSON before leaving this field.",
    invalidString: "Enter a value that matches this setting's constraints.",
    invalidNumber: "Enter a value within the allowed range and step.",
    draftRejected: "This setting could not be saved. Your draft is still here.",
    unsupportedArray: "Unsupported array schema. Use Raw mode.",
    itemCountOne: "{count} item",
    itemCount: "{count} items",
    add: "Add",
    noItems: 'No items yet. Click "Add" to create one.',
    removeItem: "Remove item",
    customEntries: "Custom entries",
    addEntry: "Add Entry",
    noCustomEntries: "No custom entries.",
    key: "Key",
    removeEntry: "Remove entry",
    renameRedactedBlocked:
      "This entry holds a stored secret. Add the new key with its value, then remove this one.",
    schemaUnavailable: "Schema unavailable.",
    unsupportedSchema: "Unsupported schema. Use Raw.",
    unsupportedNode: "Unsupported schema node. Use Raw mode.",
    noSettingsMatch: 'No settings match "{query}"',
    noSettingsInSection: "No settings in this section",
    sections: {
      env: {
        label: "Environment Variables",
        description: "Environment variables passed to the gateway process",
      },
      update: {
        label: "Updates",
        description: "Auto-update settings and release channel",
      },
      agents: {
        label: "Agents",
        description: "Agent configurations, models, and identities",
      },
      auth: {
        label: "Authentication",
        description: "API keys and authentication profiles",
      },
      channels: {
        label: "Channels",
        description: "Messaging channels (Telegram, Discord, Slack, etc.)",
      },
      messages: {
        label: "Messages",
        description: "Message handling and routing settings",
      },
      commands: {
        label: "Commands",
        description: "Custom slash commands",
      },
      hooks: {
        label: "Hooks",
        description: "Webhooks and event hooks",
      },
      skills: {
        label: "Skills",
        description: "Skill packs and capabilities",
      },
      tools: {
        label: "Tools",
        description: "Tool configurations (browser, search, etc.)",
      },
      gateway: {
        label: "Gateway",
        description: "Gateway server settings (port, auth, binding)",
      },
      wizard: {
        label: "Setup",
        description: "Discovery preferences for setup and read-only setup history.",
      },
      meta: {
        label: "Metadata",
        description: "Gateway metadata and version information",
      },
      logging: {
        label: "Logging",
        description: "Log levels and output configuration",
      },
      browser: {
        label: "Browser",
        description: "Browser automation settings",
      },
      ui: {
        label: "UI",
        description: "User interface preferences",
      },
      models: {
        label: "Models",
        description: "AI model configurations and providers",
      },
      bindings: {
        label: "Bindings",
        description: "Key bindings and shortcuts",
      },
      broadcast: {
        label: "Broadcast",
        description: "Broadcast and notification settings",
      },
      tts: {
        label: "Voice",
        description: "Text-to-speech output, voices, and personas",
      },
      session: {
        label: "Session",
        description: "Session management and persistence",
      },
      cron: {
        label: "Automations",
        description: "Scheduled tasks and automation",
      },
      discovery: {
        label: "Discovery",
        description: "Service discovery and networking",
      },
      talk: {
        label: "Talk",
        description: "Voice and speech settings",
      },
      plugins: {
        label: "Plugins",
        description: "Plugin management and extensions",
      },
      diagnostics: {
        label: "Diagnostics",
        description: "Instrumentation, OpenTelemetry, and cache-trace settings",
      },
      cli: {
        label: "CLI",
        description: "CLI banner and startup behavior",
      },
      secrets: {
        label: "Secrets",
        description: "Secret provider configuration",
      },
      acp: {
        label: "ACP",
        description: "Agent Communication Protocol runtime and streaming settings",
      },
      mcp: {
        label: "MCP",
        description: "Model Context Protocol server definitions",
      },
    },
  },
  // Historical namespace from the pre-restructure "quick settings" page; its
  // keys now serve Models, Privacy & Security, Appearance, and Profile.
  // Renaming would force retranslation of every key, so the name stays.
  quickSettings: {
    language: "Language",
    model: {
      title: "Model & Thinking",
      model: "Model",
      thinking: "Thinking",
      fastMode: "Fast Mode",
      default: "Default",
      modelPolicy: "Model policy",
      thinkingLevels: {
        off: "Off",
        low: "Low",
        medium: "Medium",
        high: "High",
      },
      fastModes: {
        auto: "Auto",
        fast: "Fast",
        standard: "Standard",
        on: "On",
        off: "Off",
      },
    },
    channels: {
      title: "Channels",
      connect: "Connect →",
    },
    appearance: {
      lobsterVisits: "Lobster visits",
      lobsterVisitsOn: "Drops by occasionally",
      lobsterVisitsOff: "Never visits",
      lobsterSounds: "Lobster sounds",
      lobsterSoundsOn: "Tiny blubs when touched",
      lobsterSoundsOff: "Silent",
      lobsterdex: "Lobsterdex",
      lobsterdexSeen: "{seen}/{total} visited",
      lobsterdexFirstVisited: "{name} · first visited {date}",
      lobsterdexCardFirstVisited: "First visited {date}",
      lobsterdexCardShinySeen: "✦ Shiny spotted {date}",
      lobsterdexCardCopyLink: "Copy link",
      lobsterdexOpen: "Open Lobsterdex",
    },
    security: {
      intro: "Review gateway access, tool policy, device authentication, and approvals.",
      title: "Security",
      gatewayAuth: "Gateway auth",
      execPolicy: "Exec policy",
      browserEnabled: "Browser enabled",
      toolProfile: "Tool profile",
    },
    system: {
      gatewayHost: "Gateway Host",
      up: "Up {duration}",
      cpu: "CPU",
      memory: "Memory",
      disk: "Disk",
      usage: "{label} usage",
      loadAverage: "Load average: {values}",
      core: "{count} core",
      cores: "{count} cores",
      load: "load",
      used: "used",
      freeOf: "{free} free of {total}",
      runtime: "Node {version} · PID {pid}",
    },
    personal: {
      browserOnly: "Stored in this browser only.",
    },
  },
  configPage: {},
  configView: {
    adminRequired: "Configuration changes require operator.admin access.",
    channelSettings: "Channel settings",
    categories: {},
    sections: {},
    themes: {},
    textSizes: {},
    profileSyncedHint: "Saved to your profile — follows you on every device.",
    syncedHint: "Synced across your devices through the gateway.",
    syncPendingHint: "Waiting to sync through the gateway.",
    notifications: {},
    appearance: {},
    chatPrefs: {},
    sidebarPrefs: {},
    sessionObserver: {},
    connection: {},
    root: "<root>",
    itemCount: "[{count} item]",
    itemCountPlural: "[{count} items]",
    form: "Form",
    raw: "Raw",
    formUnsafeTitle: "Form view can't safely edit some fields",
    rawTitle: "Edit raw JSON/JSON5 config",
    rawUnavailableTitle: "Raw mode unavailable for this snapshot",
    open: "Open",
    fileOpenedOnGateway: "Configuration file opened on Gateway host.",
    applying: "Applying…",
    autoSaveSaving: "Saving…",
    autoSaveSaved: "Saved",
    autoSavePaused: "Autosave paused after reconnect",
    saveNow: "Save",
    autoSaveFailed: "Save failed",
    autoSaveConflict: "Settings changed elsewhere",
    retry: "Retry",
    applyChanges: "Apply changes",
    rawDiscard: "Discard",
    reloadBlocked: "Finish saving or discard pending config changes before reloading.",
    rawDraftBlocksApply:
      "Unsaved raw config edits — save or discard them in the Raw editor before restarting.",
    rawDraftPendingFormTitle:
      "Unsaved raw config edits — save or discard them before switching to Form.",
    rawDraftBlocksFormEdit:
      "Unsaved raw config edits could not be parsed; resolve them in the Raw editor before changing settings.",
    invalidConfig: "Your configuration is invalid. Some settings may not work as expected.",
    dismissWarning: "Don't remind again",
    viewPendingChangesRaw: "View pending changes",
    rawDiffUnavailable: "Changes detected (JSON diff not available)",
    hideEnvValues: "Hide env values",
    revealEnvValues: "Reveal env values",
    peek: "Peek",
    loadingSchema: "Loading schema…",
    formUnsafeCount: "1 setting in this config can only be edited as text: {paths}",
    formUnsafeCountPlural: "{count} settings in this config can only be edited as text: {paths}",
    formUnsafeMore: "+{count} more",
    openRawEditor: "Open Raw editor",
    rawConfig: "Raw config (JSON/JSON5)",
    secretCount: "{count} secret",
    secretCountPlural: "{count} secrets",
    redacted: "redacted",
    visible: "visible",
    revealSensitive: "Reveal sensitive values",
    hideSensitive: "Hide sensitive values",
    toggleRawRedaction: "Toggle raw config redaction",
    sensitiveHidden:
      "{count} sensitive value hidden. Use the reveal button above to edit the raw config.",
    sensitiveHiddenPlural:
      "{count} sensitive values hidden. Use the reveal button above to edit the raw config.",
  },
  execApproval: {
    scope: {
      standingGrant:
        'Always allow runs this exact command for "{automation}" without asking, until revoked (revocable)',
      standingGrantDays:
        'Always allow runs this exact command for "{automation}" without asking, for {count} days (revocable)',
      messageSend: "Sends to {count} recipient(s) via {target}",
      payment: "Pays {amount} {currency} to {target}",
      externalPost: "Posts externally to {target}",
    },
    expiresIn: "expires in {time}",
    expired: "expired",
    execApprovalNeeded: "Exec approval needed",
    pluginApprovalNeeded: "Plugin approval needed",
    requestedBySession: "Approval requested by session {session}",
    pending: "{count} pending",
    otherPending: "Other pending requests",
    reviewRequest: "Review approval from {agent}: {command}",
    decisionRequest: "{decision}: {command}",
    agentPendingOne: "{count} pending approval",
    agentPending: "{count} pending approvals",
    allowOnce: "Allow once",
    alwaysAllow: "Always allow",
    alwaysAllowHere: "Always allow here",
    allowAlwaysUnavailable: "Allow Always is unavailable for this command.",
    reviewOnly: "Review only. Sign in with approval access to record a decision.",
    deny: "Deny",
    details: "Details",
    labels: {
      host: "Host",
      session: "Session",
      cwd: "CWD",
      resolved: "Resolved",
      security: "Security",
      ask: "Ask",
    },
  },
  approvalPage: {
    brandName: "OpenClaw",
    eyebrow: "Operator approval",
    loadingTitle: "Loading approval",
    loadingDescription: "Checking the current approval state with the Gateway.",
    unavailableTitle: "Approval unavailable",
    unavailableDescription:
      "This approval could not be found or this device is not authorized to review it.",
    connectionErrorTitle: "Connection interrupted",
    connectionErrorDescription:
      "OpenClaw cannot confirm or record a decision while disconnected. Reconnect to check the current status.",
    retry: "Retry",
    execTitle: "Command approval",
    pending: "Waiting for your decision",
    pendingDescription: "Review the request carefully. The first answer from any surface wins.",
    approvedHere: "Approved here",
    deniedHere: "Denied here",
    resolvedElsewhere: "Resolved elsewhere",
    resolvedElsewhereDescription:
      "Another surface or an earlier attempt recorded the decision first.",
    approved: "Approved",
    denied: "Denied",
    expired: "Expired",
    cancelled: "Cancelled",
    allowedOnceDescription: "The operation was approved for this request only.",
    allowedAlwaysDescription: "The operation was approved with the always-allow decision.",
    deniedDescription: "The operation was denied and will not continue.",
    expiredDescription: "No decision arrived before the deadline, so the operation was denied.",
    cancelledDescription: "The requesting run ended before a decision could be used.",
    summaryLabel: "Summary",
    commandLabel: "Command",
    requestLabel: "Request details",
    nodeLabel: "Node",
    expiresLabel: "Expires",
    resolvedLabel: "Resolved",
    actionsLabel: "Approval decisions",
    resolvingDecision: "Recording {decision}…",
    safeToClose: "The decision is recorded. You can close this page.",
    openControlUi: "Open Control UI",
  },
  agentTools: {
    githubVerify: "Verify",
    githubConnection: "Gateway connection",
    githubDisconnected: "Disconnected",
    githubAccessRequired: "Access required",
    githubReadRequired: "GitHub identity status requires operator.read access.",
    githubAdminRequired:
      "Connecting, replacing, or removing a GitHub identity requires operator.admin access.",
    githubEffectiveAuthor: "Effective Git Author",
    githubEffectiveCredential: "Effective credential",
    githubEffectiveAccessExpiry: "Effective access expiry",
    githubEffectiveRefresh: "Effective refresh token",
    githubEffectiveScopes: "Effective OAuth scopes",
    githubNoAccount: "No verified account",
    githubAuthorUnset: "Not set",
    githubErrorTitle: "Error",
    githubStateVerified: "Verified",
    githubStateUnverified: "Not verified",
    githubStateRateLimited: "Rate limited",
    githubStateUnavailable: "No credentials",
    githubStateConfiguredUnavailable: "Configured, but unavailable",
    githubAuthorization: "GitHub authorization",
    githubStarting: "Requesting code…",
    githubCancelling: "Requesting cancellation…",
    githubFinishing: "Authorization is already finishing…",
    githubCancelFailed: "Cancellation could not be confirmed",
    githubCancelFailedHint:
      "Authorization is still active. Wait for it to finish or try cancellation again.",
    githubRetryCancel: "Try cancel again",
    githubCodeReady: "Code ready",
    githubWaiting: "Waiting for approval…",
    githubSlowDown: "GitHub asked us to wait longer…",
    githubNetworkRetry: "Connection interrupted; retry scheduled",
    githubAuthorizationHint: "Open GitHub yourself, then enter the one-time code shown here.",
    githubDeviceCode: "One-time code",
    githubDeviceCodeHint: "This code authorizes only the selected identity scope.",
    githubExpires: "Code expires",
    githubOpen: "Open github.com/login/device",
    githubCopyCode: "Copy code",
    githubAuthorizationFailed: "GitHub authorization failed",
    githubExpired: "The one-time code expired. Connect again to request a new code.",
    githubDenied: "GitHub authorization was denied. Connect again when you are ready.",
    githubIncorrectCode: "GitHub rejected this device code. Connect again to request a new code.",
    githubConnect: "Connect GitHub",
    githubConnectHint: "Authorize GitHub without pasting a long-lived credential into the browser.",
    githubPatFallback: "Personal access token",
    githubPatFallbackHint: "Use a fine-grained PAT only when browser authorization is unsuitable.",
    githubUsePat: "Use a PAT instead",
    githubToken: "Fine-grained PAT",
    githubTokenDesc:
      "Stored in a private managed GitHub CLI profile; only the setup handoff is removed.",
    githubTokenToggle: "Toggle token visibility",
    githubPasteToken: "Paste a fine-grained personal access token first.",
    githubAuthorName: "Author Name",
    githubAuthorEmail: "Author Email",
    githubConfigure: "Save Identity",
    githubConfiguredHere: "Configured here",
    githubInheritedHere: "Inherited",
    githubKindNative: "Native GitHub CLI",
    githubKindPat: "Managed personal access token",
    githubKindOAuth: "Managed GitHub authorization",
    githubRefreshAvailable: "Available",
    githubRefreshExpired: "Expired — reconnect required",
    githubRefreshUnavailable: "Unavailable — reconnect required",
    githubRefreshRefreshing: "Refreshing…",
    githubRefreshFailed: "Refresh failed — retrying",
    githubUseSystemNewRuns: "Use system for new runs",
    githubUseNativeNewRuns: "Use native for new runs",
    githubUseSystemConfirmTitle: "Use the system GitHub identity for new runs?",
    githubUseNativeConfirmTitle: "Use the native GitHub identity for new runs?",
    githubUseSystemConfirmMessage:
      "New runs for this agent will use the System identity. Active runs keep their current identity until they exit or restart. Revoke the GitHub authorization or PAT separately on GitHub if needed.",
    githubUseNativeConfirmMessage:
      "New runs without an agent override will use the native GitHub identity. Active runs keep their current identity until they exit or restart. Revoke the GitHub authorization or PAT separately on GitHub if needed.",
    githubAgentMutationHint: "Authorization and removal below apply to This Agent for new runs.",
    githubSystemMutationHint: "Authorization and removal below apply to System for new runs.",
    connectedSource: "Connected: {id}",
    connected: "Connected",
    channelSource: "Channel: {id}",
    channel: "Channel",
    builtIn: "Built-In",
    plugin: "Plugin: {id}",
    optional: "Optional",
    liveNow: "Live Now",
    disabledByOverride: "Disabled by agent override.",
    enabledByProfile: "Enabled by the current profile.",
    enabledByOverride: "Enabled by agent override.",
    notIncluded: "Not included in the current profile.",
    overrideOff: "Override Off",
    enabled: "Enabled",
    overrideOn: "Override On",
    profileOff: "Profile Off",
    notLive: "Not Live",
    otherAgent: "Other Agent",
    title: "Tool Access",
    subtitle: "Profile + per-tool overrides for this agent.",
    enabledSummary: "{enabled}/{total} enabled.",
    enableAll: "Enable All",
    disableAll: "Disable All",
    loadConfig: "Load the gateway config to adjust tool profiles.",
    explicitAllowlist:
      "This agent is using an explicit allowlist in config. Tool overrides are managed in the Config tab.",
    globalAllowlist:
      "Global tools.allow is set. Agent overrides cannot enable tools that are globally blocked.",
    loadingCatalog: "Loading runtime tool catalog…",
    catalogFallback: "Could not load runtime tool catalog. Showing built-in fallback list instead.",
    availableNow: "Available Right Now",
    availableNowSubtitle: "What this agent can use in the current chat session.",
    noSession: "no session",
    switchAgent: "Switch chat to this agent to view its live runtime tools.",
    loadingAvailable: "Loading available tools…",
    availableError: "Could not load available tools for this session.",
    noAvailable: "No tools are available for this session right now.",
    moreLiveTitle: "{count} more live tools are available in the groups below.",
    moreLive: "+{count} more live tools",
    quickPresets: "Quick Presets",
    catalogTitle: "Tool Catalog",
    inherit: "Inherit",
    profile: "Profile",
    source: "Source",
    live: "Live",
    status: "Status",
    profileSourceAgent: "agent override",
    profileSourceGlobal: "global default",
    profileSourceDefault: "default",
    statusSaving: "saving…",
    statusUnsaved: "unsaved",
    statusSaved: "saved",
    toolPreview: "Tool preview",
    more: "+{count} more",
    toolsOne: "{count} Tool",
    tools: "{count} Tools",
    enabledToolsOne: "{count} Enabled Tool",
    enabledTools: "{count} Enabled Tools",
    liveToolsOne: "{count} Live Tool",
    liveTools: "{count} Live Tools",
    access: "Access",
    session: "Current Session",
    disableNamed: "Disable {name}",
    enableNamed: "Enable {name}",
    defaultPresets: "Default Presets",
    availableVia: "Available now via {source}.",
    unavailableSession: "Not available in this chat session right now.",
    inspectAgent: "Switch chat to this agent to inspect live availability.",
    linkTool: "Link to This Tool",
  },
  skillGroups: {
    workspace: "Workspace Skills",
    builtIn: "Built-in Skills",
    installed: "Installed Skills",
    extra: "Extra Skills",
    other: "Other Skills",
  },
  skillsPage: {
    title: "Skills",
    tabs: {
      all: "All",
      ready: "Ready",
      needsSetup: "Needs Setup",
      disabled: "Disabled",
    },
    defaultAgent: "{name} (default)",
    filterPlaceholder: "Filter installed skills",
    shown: "{count} shown",
    clawHub: "ClawHub",
    clawHubSubtitle: "Search and install skills from the registry",
    searchClawHub: "Search ClawHub skills…",
    searching: "Searching…",
    disconnected: "Not connected to gateway.",
    empty: "No skills found.",
    noClawHubResults: "No skills found on ClawHub.",
    notScannedByClawHub: "Not scanned by ClawHub",
    install: "Install",
    installed: "Installed",
    installing: "Installing…",
    close: "Close",
    by: "By",
    latest: "Latest: v{version}",
    platforms: "Platforms: {platforms}",
    installNamed: "Install {name}",
    notFound: "Skill not found.",
    openDetails: "Open {name} details",
    enabledNamed: "{name} enabled",
    invalidLink: "ClawHub link invalid",
    overview: "Overview",
    skillCard: "Skill Card",
    missingRequirements: "Missing requirements",
    reason: "Reason: {reasons}",
    disabled: "Disabled",
    enabled: "Enabled",
    apiKey: "API key",
    getKey: "Get your key:",
    saveKey: "Save key",
    source: "Source:",
    refreshing: "Refreshing…",
    fullSecurityReport: "Full security report",
    loadingSkillCard: "Loading Skill Card…",
    skillCardNotLoaded: "Skill Card not loaded.",
    verdict: {
      unavailable: "Unavailable",
      clean: "Clean",
      pending: "Pending",
      blocked: "Blocked",
      review: "Review",
    },
  },
  skillStatus: {
    bundled: "bundled",
    eligible: "eligible",
    blocked: "blocked",
    disabled: "disabled",
    blockedAllowlist: "blocked by allowlist",
    blockedAgentFilter: "blocked by agent filter",
  },
  nav: {
    owner: "Owner",
    back: "Back",
    forward: "Forward",
    settings: "Settings",
    askOpenClaw: "Ask OpenClaw",
    settingsGroupDevice: "This Mac",
    settingsGroupThisIPhone: "This iPhone",
    settingsGroupThisIPad: "This iPad",
    settingsGroupThisDevice: "This device",
    settingsGroupConnections: "Connections",
    settingsGroupAgents: "Agents & Tools",
    settingsGroupSecurity: "Privacy & Security",
    settingsGroupSystem: "System",
    settingsSearchLabel: "Search settings",
    settingsSearchPlaceholder: "Search settings…",
    settingsSearchNoResults: "No matching settings.",
    settingsSearchClear: "Clear settings search",
    settingsLoadFailed: "Settings navigation could not load.",
    exitSettings: "Back to app",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    resize: "Resize sidebar",
    more: "More",
    home: "Home",
    pages: "Pages",
    customize: "Edit pinned items",
    customizeReset: "Reset pinned items",
    workboardGroup: "WorkBoard",
  },
  terminal: {
    title: "Terminal",
    toggle: "Toggle terminal",
    open: "Open terminal",
    openWindow: "Open terminal in new window",
    hide: "Hide terminal",
    resize: "Resize terminal panel",
    newSession: "New terminal session",
    addFiles: "Add files to terminal",
    dropFiles: "Drop files to add them",
    uploadProgress: "Uploading {current} of {total}",
    uploadFailed: "Upload failed",
    retryUpload: "Retry",
    closeSession: "Close terminal session",
    sessions: "Terminal sessions",
    refreshSessions: "Refresh",
    loadingSessions: "Loading sessions…",
    noSessions: "No live terminal sessions",
    currentSession: "current",
    sessionAttached: "attached",
    attachSession: "Attach session",
    attachFailed: "Could not attach terminal session",
    startCancelled: "Terminal start was cancelled. Try again.",
    panelUnavailable: "Terminal panel is unavailable. Reconnect and try again.",
    connecting: "Connecting to session…",
    connectionTimedOut: "Session did not connect within 30 seconds.",
    refreshRequired: "Control UI updated. Reload this page to continue the terminal action.",
    tabLabel: "shell {n}",
    tabHint: "{agent} · {cwd}",
    agentOwnedBadge: "agent",
    exited: "exited",
    exitedCode: "exited ({code})",
    exitedSignal: "exited (signal {signal})",
    detached: "detached",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    dockMain: "Fill main content area",
    dockMode: "Terminal panel position",
    unavailable: "The terminal is not available on this gateway.",
    unusableSession:
      "The Gateway returned an unusable terminal session (missing {field}). The Gateway is likely older than this Control UI — update it, then retry.",
    uploadTooLarge: "File exceeds the 16 MiB terminal upload limit: {file}",
    uploadUnsafeCmdPath: "Cannot safely insert an uploaded path containing % or ! into cmd.exe",
    uploadUnsupportedShell: "Cannot safely insert an uploaded path into unsupported shell: {shell}",
  },
  browser: {
    profile: "Browser profile: {profile}",
    navigationBlocked:
      "The current browser navigation rules block this address. Select another tab or enter an allowed address.",
    navigationCheckFailed: "OpenClaw couldn’t verify this tab’s address. Refresh to try again.",
    title: "Browser",
    open: "Open",
    openPanel: "Open browser panel",
    moreActions: "More actions",
    copyUrl: "Copy URL",
    openNewTab: "Open in new tab",
    toggle: "Toggle browser panel",
    close: "Close browser panel",
    resize: "Resize browser panel",
    newTab: "New tab",
    closeTab: "Close tab",
    untitledTab: "New tab",
    back: "Back",
    forward: "Forward",
    reload: "Reload",
    openExternal: "Open in your browser",
    urlPlaceholder: "Enter a URL and press Enter",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    annotate: "Annotate page",
    inspect: "Inspect element",
    annotateSend: "Send to chat",
    annotateUndo: "Undo",
    annotateClear: "Clear",
    annotateDone: "Exit annotate mode",
    annotateHint: "Draw on the page, then send the markup to your chat.",
    inspectName: "Name",
    inspectRole: "Role",
    inspectFocusable: "Focusable",
    loading: "Loading page…",
    notRunning: "The gateway browser is not running.",
    start: "Start browser",
    noChatTarget: "Open a chat session first so the annotation has somewhere to go.",
    annotationLimitReached:
      "Remove a browser annotation before retrying (maximum 4 cards and 8,000 characters of generated context).",
    inspectUnavailable: "Element inspection is disabled (browser.evaluateEnabled=false).",
    annotationSent: "Annotation added to the chat composer.",
    errors: {
      requestFailed: "Browser request failed: {error}",
      screenshotPathMissing: "Browser screenshot did not return a media path.",
      screenshotFetchTimedOut: "Screenshot fetch timed out.",
      screenshotFetchFailed: "Screenshot fetch failed ({status}).",
      screenshotReadFailed: "Screenshot read failed.",
      screenshotDecodeFailed: "Screenshot decode failed.",
      canvasUnavailable: "Canvas 2D context unavailable.",
    },
    annotatePrompt: {
      browserTarget: "Browser target: {target}",
      // introTitled/elementDetail (not intro/element): translated keys never
      // retranslate on source-wording changes, so the provenance-label rewrite
      // required fresh key names to propagate to all locales.
      introTitled:
        'I annotated the page at {url} (page-reported title: "{title}") — the attached screenshot shows my markup.',
      introUntitled: "I annotated the page at {url} — the attached screenshot shows my markup.",
      region:
        "Marked region {index}: centered around {x}% across / {y}% down, spanning about {width}% × {height}% of the view.",
      moreRegions: "…plus {count} more marked region(s), all visible in the screenshot.",
      elementDetail:
        "Marked element (page-reported): {descriptor} — {width}×{height}px at ({x}, {y}).",
      outro: "Please look at the marked area and tell me what you make of it.",
    },
  },
  desktop: {
    title: "Desktop",
    openWindow: "Open desktop in new window",
    unavailable: "Desktop viewing is unavailable for this connection.",
    toggle: "Toggle desktop panel",
    reconnect: "Reconnect",
  },
  routeTitles: {
    modelProviders: "Models",
    notifications: "Notifications",
    advanced: "Advanced",
  },
  tabs: {
    device: "This Mac",
    devicePermissions: "Permissions",
    agents: "Agents",
    activity: "Activity",
    meetings: "Meetings",
    apps: "Apps",
    portals: "Portals",
    approvals: "Approvals",
    workboard: "Workboard",
    worktrees: "Worktrees",
    channels: "Channels",
    connection: "Gateway",
    sessions: "Sessions",
    usage: "Usage",
    cron: "Automations",
    tasks: "Tasks",
    skills: "Skills",
    plugins: "Plugins",
    skillWorkshop: "Skill Workshop",
    devices: "Devices",
    cloudWorkers: "Cloud workers",
    chat: "Chat",
    dashboards: "Dashboards",
    custodian: "OpenClaw",
    profile: "Profile",
    communications: "Communications",
    appearance: "Appearance",
    lobsterdex: "Lobsterdex",
    automation: "Automation",
    mcp: "MCP",
    memory: "Memory",
    talk: "Talk",
    infrastructure: "Infrastructure",
    labs: "Labs",
    updates: "Updates",
    about: "About",
    aiAgents: "Agent Defaults",
    modelSetup: "Model Setup",
    memoryImport: "Import Memory",
    security: "Privacy & Security",
    secrets: "Secrets",
    debug: "Debug",
    logs: "Logs",
    plugin: "Plugin",
  },
  subtitles: {
    device: "App settings and capabilities on this Mac.",
    devicePermissions: "Manage macOS access, location, and computer presence.",
    agents: "Workspaces, tools, identities.",
    activity: "Recent sessions across people using this gateway.",
    meetings: "Meeting notes and transcripts across this gateway.",
    apps: "Companion apps for phone, watch, desktop, and browser.",
    portals: "Live previews from agent-run applications.",
    approvals: "Recent exec, plugin, and system-agent approvals.",
    workboard: "Agent work queue and session handoff.",
    worktrees: "Isolated agent task checkouts and recovery snapshots.",
    channels: "Channels and settings.",
    connection: "Gateway endpoint, credentials, and handshake status.",
    sessions: "Active sessions and defaults.",
    usage: "API usage and costs.",
    cron: "Scheduled tasks and recurring agent runs.",
    tasks: "Background tasks: subagents, automation runs, CLI.",
    skills: "Skills and API keys.",
    plugins: "Install and manage optional capabilities.",
    skillWorkshop:
      "The skills your agent uses now, suggestions waiting for review, and past decisions.",
    devices: "Paired devices, pairing approvals, and exec bindings.",
    cloudWorkers: "Profiles and machine sizes for cloud sessions.",
    chat: "Gateway chat for quick interventions.",
    dashboards: "Tasks with saved dashboards.",
    custodian: "System setup and care.",
    config: "Legacy settings route; opens Appearance.",
    profile: "Your display name, avatar, and identity on this gateway.",
    communications: "Messages, text-to-speech, and meeting capture settings.",
    appearance: "Theme and UI settings.",
    lobsterdex: "Every lobster palette that has visited this browser.",
    automation: "Commands, hooks, automations, and plugins.",
    mcp: "MCP servers, auth, tools, and diagnostics.",
    memory: "Memory engine, search, and dreaming.",
    talk: "Realtime voice: provider, model, and speaker voice.",
    infrastructure: "Gateway, browser, node host, discovery, and ACP settings.",
    labs: "Experimental agent and tool capabilities.",
    updates: "Release channel, automatic updates, and current update status.",
    about: "Control UI and connected Gateway build identity.",
    aiAgents: "Global agent defaults: skills, tools, and session.",
    modelSetup: "Connect a verified AI model",
    modelProviders: "Default models, behavior, provider access, usage, and cost.",
    memoryImport: "Bring Codex and Claude Code memory into an agent workspace.",
    notifications: "Browser push notifications from your gateway.",
    security: "Gateway auth, exec policy, tool profile, and approvals.",
    advanced: "Every remaining config section, plus the raw file editor.",
    debug: "Snapshots, events, RPC.",
    logs: "Live gateway logs.",
    plugin: "Plugin-provided panel.",
  },
  meetingCapture: {
    title: "Meeting capture",
    description: "Choose which sources can save meeting notes on this Gateway.",
    sources: "Auto-start sources",
  },
  cloudWorkersPage: {},
  portalsPage: {
    listLabel: "Active portals",
    portLabel: "Port {port}",
    openNewTab: "Open in new tab",
    closePortal: "Close {title}",
    previewTitle: "{title} portal preview",
    loading: "Loading portals…",
    emptyHint: "Ask the agent to start a portal:",
    promptShow: "Show me in a portal.",
    promptStart: "Start the application in a portal.",
    promptMakeAvailable: "Make the server available in a portal.",
    unsupported: "This gateway does not support portals.",
    loadFailed: "Could not load portals: {error}",
    closeFailed: "Could not close the portal: {error}",
    unreachableTitle: "Portal not reachable from this browser",
    unreachableBody:
      "The Gateway is likely being accessed through a proxy or tunnel that exposes only its main port. Open this URL from a browser on the Gateway host.",
    writeAccessRequiredTitle: "Write access required",
    writeAccessRequiredBody: "This portal requires an operator with write access.",
    retry: "Retry",
  },
  modelSetup: {
    missingAuth: "No provider credential is configured for this model. Set it up in Model Setup.",
    heading: "Connect a verified AI model",
    intro:
      "OpenClaw discovers AI access on this Gateway. Choose a provider to begin; nothing is selected, tested, installed, or saved automatically.",
    required: {
      title: "No AI provider configured",
      body: "We couldn't find a provider and model configured for this agent. Choose a supported connection; OpenClaw will test it before enabling chat.",
      action: "Connect an AI provider",
    },
    loading: "Checking this Gateway for available AI access…",
    testing: "Testing — asking the selected model for a quick reply…",
    retry: "Retry",
    checkAgain: "Check again",
    recovery: {
      unknown:
        "The previous activation is unresolved. Check again refreshes the current setup without repeating it. If a model is available, you can verify and use it.",
      wait: "The previous setup attempt may still be running. Check again can refresh its result. If no model appears, check again after {time} to choose a provider.",
      useCurrent: "Verify & use selected model",
    },
    verify: {
      title: "Selected model",
      button: "Check model",
      retry: "Try again",
      checkAgain: "Check again",
      checkingButton: "Checking…",
      checking: "Checking — asking {modelRef} for a quick reply…",
      ready: "Ready",
      readyIn: "Ready · {latencyMs} ms",
      providerUnavailable: "{provider} isn’t responding.",
    },
    access: {
      adminRequired: "Model setup requires operator.admin access.",
      gatewayTooOld: "The Gateway is running an older OpenClaw version",
    },
    candidates: {
      title: "Found on this Gateway",
      recommended: "Recommended",
      credentialsReady: "Credentials ready",
      signInNeeded: "Sign-in needed",
      detected: "Detected",
      testAndUse: "Test & use",
      retry: "Retry test",
      testingButton: "Testing…",
    },
    nativeDiscovery: {
      title: "Discover existing conversations",
      body: "Show native assistant conversations from this Gateway host in OpenClaw. This is discovery, not an import or copy.",
      enable: "Show existing native conversations",
      decline:
        "Leave unchecked to keep native session catalogs off when you connect your AI provider. Existing installations are not changed.",
    },
    empty: {
      title: "Recommended installs",
      intro: "No existing AI access was detected. Install one of these tools, then check again.",
    },
    unavailable: {
      title: "Found, but needs attention",
      signIn: "Sign in with {provider}",
      useApiKey: "Use API key",
    },
    signIn: {
      title: "Connect an AI provider",
      install: "Review & install",
      custom: "Set up endpoint",
      signIn: "Sign in",
      pair: "Pair",
      more: "More sign-in options",
    },
    prepare: {
      title: "Run a model locally",
      intro: "Use a local model service or prepare a private GGUF model on this Gateway.",
      ollamaButton: "Check & set up",
      ollamaLabel: "Ollama",
      ollamaHint: "Connect to the Ollama service on this Gateway and prepare a tools-capable model",
      llamaCppLabel: "llama.cpp",
      providerNotReady:
        "{provider} did not expose a usable local model. Review the setup result, then retry.",
    },
    manual: {
      title: "Connect with an API key or token",
      provider: "Provider and access method",
      selectProvider: "Select a provider",
      selectProviderHint: "Choose where this credential comes from",
      accessValue: "API key or token",
      accessValueFor: "{provider} API key or token",
      accessValuePlaceholder: "Paste an API key or token",
      connectAndVerify: "Connect & verify",
      verifyHint: "OpenClaw verifies a real model reply before marking the connection ready.",
      required: "Choose a provider and enter an API key or token.",
    },
    success: {
      title: "Connection verified",
      body: "OpenClaw received a real reply from {modelRef}. You can start chatting now.",
      activeModel: "Active model",
      latency: "Verified in {latencyMs} ms",
      openChat: "Start chatting",
      continueSetup: "Continue setup",
      stayHere: "Stay in settings",
      configuredModel: "Configured model",
    },
    failure: {
      auth: "Authentication failed",
      rateLimit: "Rate limited",
      billing: "Billing problem",
      timeout: "Timed out",
      format: "Invalid response",
      unavailable: "Unavailable",
      unknown: "Connection failed",
    },
    failureGuidance: {
      auth: "Review the provider credential or sign-in, then retry.",
      rateLimit: "Wait for the provider limit to reset, then retry.",
      billing: "Restore provider billing or quota, then retry.",
      timeout:
        "The model did not finish the setup test in time. Warm it or choose a faster model, then retry.",
      unavailable: "Make sure the provider service is running and reachable, then retry.",
      format: "Check that the endpoint exposes a compatible chat model, then retry.",
      unknown: "Review the connection details, then retry.",
    },
    errors: {
      requestFailed: "Model setup request failed.",
      activationFailed: "The model could not be activated.",
    },
    wizard: {
      dialogLabel: "Provider sign-in",
      prepareDialogLabel: "Local model setup",
      title: "Sign in with a provider",
      prepareTitle: "Set up a local model",
      starting: "Starting provider sign-in…",
      prepareStarting: "Starting local model setup…",
      checking: "Checking your model setup…",
      working: "Working…",
      continue: "Continue",
      submit: "Submit",
      openSignIn: "Open sign-in page",
      copy: "Copy",
      expires: "Expires in {count} minutes",
      cancelled: "Provider sign-in was cancelled.",
      finishingStep: "Setup is finishing the current step. You can cancel when it finishes.",
      cancelFailed: "Could not confirm cancellation: {error}",
      sessionExpired:
        "The Gateway no longer has this setup session. It may already have finished. Close this dialog and choose Check again to review the current setup.",
      notComplete: "Sign-in finished, but model setup is not complete yet.",
    },
  },
  memoryImport: {},
  onboarding: {
    memoryImport: {
      title: "Bring your assistant memory with you",
      body: "OpenClaw found memory from other coding assistants. Import it into your agent workspace?",
      plannedCount: "{count} ready to import",
      alreadyImported: "Already imported: {count}",
      sourceUnavailable: "Source path unavailable",
      import: "Import",
      skip: "Skip",
      reviewDetails: "Review details",
      importingProvider: "Importing…",
      providerResult: "Migrated {migrated}, skipped {skipped}",
      providerIncomplete:
        "Migrated {migrated}, skipped {skipped}, {errors} failed, {conflicts} conflicts",
      providerError: "Import failed: {error}",
      connectionChanged: "Skipped: the Gateway connection changed during the import",
      unknownError: "Request failed",
      doneTitle: "Memory import finished",
      doneBody: "Migrated {migrated}, skipped {skipped}. You can continue setting up OpenClaw.",
    },
  },
  assistantPanel: {
    title: "Assistant sidebar",
    home: "Home",
    toggle: "Talk to your Home agent",
    openHome: "Open Home full page",
    close: "Close assistant sidebar",
    resize: "Resize assistant sidebar",
    dockBottom: "Dock assistant sidebar at bottom",
    dockRight: "Dock assistant sidebar at right",
    context: "Working on: {context}",
    contextOff: "Work context off",
    removeContext: "Remove work context",
    includeContext: "Include work context",
    selection: "Selected text",
    attachSelection: "Attach selected text",
    removeSelection: "Remove selected text",
  },
  custodian: {
    title: "OpenClaw",
    subtitleCaretaker: "System setup and care.",
    exitSetup: "Exit setup",
    newAgent: "New agent",
    hatchDraft: "Wake up, my friend!",
    placeholder: "Message OpenClaw…",
    sensitivePlaceholder: "Enter sensitive value…",
    sensitiveReply: "Sensitive reply sent",
    cancel: "Cancel",
    send: "Send",
    thinking: "OpenClaw is thinking",
    earlier: "Earlier",
    requestFailed: "OpenClaw could not reply. Try again.",
    connectionChanged: "The Gateway connection changed. Retry to continue this setup.",
    sessionRestarted:
      "{error} OpenClaw started a fresh session; earlier messages remain for context.",
    unsupportedGateway: "Update the Gateway to continue setup with OpenClaw.",
    panel: {
      title: "OpenClaw",
      toggle: "Toggle Ask OpenClaw",
      close: "Close Ask OpenClaw",
      resize: "Resize Ask OpenClaw",
      dockBottom: "Dock Ask OpenClaw at bottom",
      dockRight: "Dock Ask OpenClaw at right",
    },
    history: {
      button: "History",
      title: "Recent changes",
      description: "What changed on this system, newest first.",
      loading: "Loading recent changes…",
      empty: "No recorded changes yet.",
      requestFailed: "OpenClaw could not load change history.",
      loadMore: "Load more",
      loadingMore: "Loading more…",
      changedPaths: "Changed paths ({count})",
      invalidEdit: "This manual edit did not pass configuration validation.",
      opaqueChange: "Formatting or comments changed without visible configuration path changes.",
      sources: {
        systemAgent: "system-agent",
        doctor: "doctor",
        settings: "settings",
        cli: "CLI",
        pluginInstall: "plugin install",
        manualEdit: "manual edit",
        unknown: "unknown",
      },
    },
    nudge: {
      configReload: "Configuration reload stopped — ask me what happened",
      channelAuth: "{channel} authentication degraded — ask me what happened",
      channelDisconnected: "{channel} just disconnected — ask me what happened",
      channelDegraded: "{channel} is degraded — ask me what happened",
      channelFallback: "A channel",
      dismiss: "Dismiss this update",
      channelSetupTitle: "Reach OpenClaw outside this app",
      channelSetupBody:
        "The web app already works. Add a channel only if you want to message OpenClaw from another service.",
      channelSetupAction: "Set up a channel",
      channelSetupDismiss: "Keep using the web app",
      channelStatusErrorTitle: "Channel status is unavailable",
      channelStatusErrorBody: "Retry the check, or keep using the web app without a channel.",
    },
  },
  mcpServers: {
    add: "Add server",
    adding: "Adding…",
    nameLabel: "Name",
    transportLabel: "Transport",
    transportStreamableHttp: "Streamable HTTP",
    transportSse: "SSE",
    transportStdio: "Stdio",
    targetLabel: "URL or command",
    nameInvalid: "Server names use letters, numbers, dots, dashes, or underscores.",
    targetInvalid: "Enter a URL for HTTP transports or a valid command line for stdio.",
    sessionEnableFailed:
      "The server was saved disabled globally, but enabling it for this session failed: {error}",
    sessionChanged: "The active session changed before it could be enabled.",
    sessionUnavailable: "The active session is unavailable; refresh and try again.",
    nameTaken: "An MCP server named “{name}” already exists.",
    missing: "MCP server “{name}” was not found in the configuration.",
    missingTransport: "missing transport",
    addedSuccess: "Added MCP server {name}.",
    enabledSuccess: "Enabled MCP server {name}.",
    disabledSuccess: "Disabled MCP server {name}.",
    removedSuccess: "Removed MCP server {name}.",
    configUnavailable: "Configuration is unavailable; refresh and try again.",
    connectRequired: "Connect to the gateway to change MCP servers.",
    adminRequired: "MCP server changes require operator.admin access.",
    enable: "Enable",
    disable: "Disable",
    removeNamed: "Remove {name}",
    working: "Working…",
  },
  mcpPage: {
    intro: "Connect and manage MCP servers that provide tools to OpenClaw.",
    servers: "Servers",
    oauth: "OAuth",
    filtered: "Filtered",
    configuredServers: "Configured servers",
    noServers: "No MCP servers configured.",
    setUpFirstServer: "Set up your first MCP server",
    operatorCommands: "MCP operator commands",
    operatorCommandsHint: "Status, diagnostics, auth, probing, and runtime reload.",
    runtimeHint:
      "Edits save automatically; runtime changes apply after a gateway restart, and active agents rebuild MCP runtimes on next use.",
    toolFilter: "tool filter",
    parallel: "parallel",
    tlsVerifyOff: "TLS verify off",
    mtls: "mTLS",
  },
  talkPage: {
    intro: "Configure realtime voice providers, models, and speaker voices.",
    voiceSection: {
      title: "Realtime voice",
      description:
        "Continuous speech conversations with your agent. The pickers below write talk.realtime settings; the full form further down covers everything else.",
    },
    status: {
      title: "Status",
      ready: "Ready",
      notReady: "Not configured",
      unavailable: "Unavailable",
      unavailableHint: "Connect to the Gateway to check realtime voice readiness.",
      activeProvider: "Active provider: {provider}",
      noProvider: "No realtime voice provider is configured yet.",
    },
    provider: {
      title: "Provider",
      description: "Auto picks the first provider with working credentials.",
      auto: "Auto",
    },
    model: {
      title: "Model",
      description: "Realtime voice model for browser Talk sessions.",
      default: "Provider default",
      defaultNamed: "Default ({model})",
    },
    voice: {
      title: "Speaker voice",
      description: "Voice used for spoken replies. GPT-Live locks the voice once a call starts.",
      default: "Provider default",
      unsupported: "unsupported",
      unsupportedDefault:
        "This saved voice is unavailable for the selected route. Provider default will be used.",
    },
    gptLive: {
      title: "GPT-Live",
      hint: "Released browser/Gateway-owned WebRTC tries OAuth first and falls back to a Platform API key. Direct backend sockets and unlisted or private routes require Platform API-key access. Delegated work can be steered while running and requires exact spoken confirmation for high-impact actions.",
      ready: "Ready",
    },
  },
  memoryPage: {
    intro: "Choose how OpenClaw stores, searches, and maintains agent memory.",
    tablistLabel: "Memory sections",
    tabs: {
      overview: "Overview",
      memories: "Memories",
      dreams: "Dreams",
      settings: "Settings",
    },
    overview: {
      hero: {
        awake: "Memory is awake",
        waking: "Waking memory…",
        hibernating: "Memory is hibernating",
        needsAttention: "Memory needs attention",
        activeDescription: "{engine} · {mode}",
        loadingDescription: "Checking this agent's memory engine and dream cycle.",
        offDescription: "Choose a memory engine in Settings to wake it up.",
        disabledDescription: "The selected memory engine is disabled. Re-enable it in Settings.",
        gatewayOffline: "The gateway is offline, so memory status is unavailable.",
        hybridSearch: "hybrid search",
        keywordSearch: "keyword search (no embeddings)",
        openSettings: "Open Settings",
        retry: "Retry",
        refresh: "Refresh status",
      },
      schedule: {
        title: "Sleep schedule",
        lightDescription:
          "Sorts fresh short-term notes and stages promising candidates without changing long-term memory.",
        remDescription:
          "Reflects on themes and recurring ideas across recent activity to strengthen ranking without changing long-term memory.",
        deepDescription:
          "Scores staged candidates, promotes the keepers into long-term memory (MEMORY.md), and writes the dream diary.",
        nextRun: "next {time}",
        lastRun: "last {time}",
        notScheduled: "Not scheduled",
        learnMore: "How dreaming works",
        openDocs: "Open dreaming guide",
      },
      activity: {
        title: "Activity",
        promotedToday: "Promoted today",
        promotedTotal: "Promoted total",
        shortTermCount: "Pending short-term entries",
        phaseHitCount: "Phase signals",
        lightPhaseHitCount: "Light-phase hits",
        remPhaseHitCount: "REM-phase hits",
      },
      health: {
        title: "Engine health",
        provider: "Provider",
        embeddings: "Embeddings",
        runtime: "Embedding runtime",
        healthy: "Ready",
        unavailable: "Unavailable",
        notChecked: "Not checked",
        notCheckedDescription: "Embedding readiness has not been checked yet.",
        checking: "Checking…",
        test: "Test",
        testing: "Testing…",
      },
      shortcuts: {
        title: "Explore memory",
        memories: "Search memories",
        diary: "Read the dream diary",
        settings: "Configure memory",
      },
    },
    memories: {
      searchLabel: "Search memories",
      searchPlaceholder: "Search this agent's memories",
      searchButton: "Search",
      idle: "Search for a person, project, decision, or anything else this agent remembers.",
      searching: "Searching memories…",
      results: "{count} results",
      empty: "No memories matched “{query}”.",
      error: "Memory search failed: {message}",
      retry: "Retry",
      gatewayUpdateRequired: "Update the gateway to search memories from the Control UI.",
      hybridSearch: "hybrid search",
      keywordSearch: "keyword search",
      lineRange: "lines {start}–{end}",
      score: "score {score}",
      sourceMemory: "memory",
      sourceSessions: "sessions",
      fileLoading: "Loading the full memory file…",
      fileError: "Could not load this memory file: {message}",
      fileUnsupported: "This memory file cannot be shown as text.",
    },
    engine: {
      title: "Engine",
      description:
        "Exactly one memory plugin owns the memory slot. Selecting an engine enables it and disables the others.",
      rowTitle: "Memory engine",
      openClawMemory: "OpenClaw Memory",
      off: "Off",
      unavailable: "Unavailable",
      autoHint: "No engine is pinned in config, so the slot falls back to its default owner.",
      explicitHint: "This engine is pinned in config under plugins.slots.memory.",
      offHint: "Memory is switched off in config: plugins.slots.memory is set to none.",
      catalogUnavailable: "Connect to the gateway to change the memory engine.",
      changeFailed: "Could not change the memory engine",
      disabledTitle: "This engine is disabled",
      disabledHint:
        "The memory slot points at this plugin, but the plugin itself is disabled, so memory is not running.",
      enable: "Enable",
    },
    addons: {
      title: "Add-ons",
      description:
        "These plugins layer on top of the engine instead of competing for the slot, so any combination can run at once.",
      activeMemory: {
        title: "Active memory",
      },
      memoryWiki: {
        title: "Memory wiki",
      },
      toggleAriaLabel: "Enable or disable {plugin}",
      changeFailed: "Could not update {plugin}",
      stateUnknown: "Unknown",
      manage: "Enable or disable add-ons",
      manageLink: "Open Plugins",
    },
    import: {
      title: "Import",
      description: "Bring existing memory from other assistants into an agent workspace.",
      link: "Open Memory Import",
    },
    search: {
      intro: "Embedding and retrieval defaults shared by every agent that has no memory override.",
    },
    dreaming: {
      intro:
        "Dreaming runs as one managed automation across every agent workspace, so these settings are global. They are owned by the {plugin} plugin.",
      schedule: {
        title: "Schedule",
        description: "When the full sweep runs and which model narrates it.",
      },
      frequency: {
        label: "Dreaming frequency",
        help: "Cron cadence for the full dreaming sweep (light, REM, then deep). Leave empty for the plugin default.",
        placeholder: "0 3 * * *",
      },
      timezone: {
        label: "Timezone",
        help: "IANA timezone used to interpret the cron cadence.",
        placeholder: "Europe/Vienna",
        default: "Gateway local timezone",
      },
      model: {
        label: "Dreaming model",
        help: "Provider/model override for dream diary narration. Requires subagent model overrides to be allowed.",
        placeholder: "anthropic/claude-sonnet-4-6",
        default: "Agent model",
      },
      verboseLogging: {
        label: "Verbose logging",
        help: "Log each dreaming phase in detail. Useful when tuning thresholds.",
      },
      storage: {
        title: "Storage",
        description: "Where promoted memories and dreaming reports are written.",
        modeLabel: "Storage mode",
        modeHelp: "Inline writes into the memory file; separate keeps a dedicated report file.",
        modes: {
          inline: "Inline",
          separate: "Separate",
          both: "Both",
        },
        separateReportsLabel: "Separate reports",
        separateReportsHelp: "Keep dreaming reports out of the main memory file.",
      },
      phases: {
        light: {
          title: "Light phase",
          description: "Cheap recent-activity pass that stages replay candidates.",
        },
        deep: {
          title: "Deep phase",
          description: "Scored promotion pass that graduates short-term entries into memory.",
        },
        rem: {
          title: "REM phase",
          description: "Pattern pass that looks for recurring themes across the lookback window.",
        },
      },
      phaseFields: {
        enabled: "Enabled",
        enabledHelp: "Run this phase during the sweep.",
        lookbackDays: "Lookback days",
        lookbackDaysHelp: "How far back this phase reads. Leave empty for the plugin default.",
        limit: "Limit",
        limitHelp: "Maximum entries this phase processes per run.",
        dedupeSimilarity: "Dedupe similarity",
        dedupeSimilarityHelp: "Similarity above which two candidates are treated as duplicates.",
        minScore: "Minimum score",
        minScoreHelp: "Promotion score an entry must reach.",
        minRecallCount: "Minimum recalls",
        minRecallCountHelp: "How often an entry must be recalled before it can be promoted.",
        minUniqueQueries: "Minimum unique queries",
        minUniqueQueriesHelp: "How many distinct queries must have surfaced the entry.",
        recencyHalfLifeDays: "Recency half-life (days)",
        recencyHalfLifeDaysHelp: "How quickly older recall signals lose weight.",
        maxAgeDays: "Maximum age (days)",
        maxAgeDaysHelp: "Ignore short-term entries older than this.",
        maxPromotedSnippetTokens: "Max promoted snippet tokens",
        maxPromotedSnippetTokensHelp:
          "Token budget for each promoted snippet. Provenance stays attached.",
        minPatternStrength: "Minimum pattern strength",
        minPatternStrengthHelp: "Strength a recurring pattern must reach to be reported.",
      },
      agentScope: {
        rowTitle: "Agent",
      },
      unsupported: {
        title: "Dreaming settings",
        rowTitle: "Not available for this engine",
        description:
          "The {plugin} plugin owns the memory slot and its config schema has no dreaming section, so these settings cannot be stored. Switch the engine above to edit them.",
      },
    },
  },
  sessionsPage: {
    hubTablistLabel: "Session sections",
  },
  pluginsPage: {
    searchLabel: "Search plugins",
    searchPlaceholder: "Search plugins and ClawHub",
    browseClawHub: "Browse ClawHub",
    refresh: "Refresh",
    hubTablistLabel: "Plugins sections",
    installedTab: "Installed",
    discoverTab: "Discover",
    workshopTab: "Workshop",
    tryAgain: "Try again",
    loading: "Loading plugins…",
    searching: "Searching ClawHub…",
    searchResultCountOne: "{count} result",
    searchResultCount: "{count} results",
    fromClawHub: "From ClawHub",
    noClawHubResultsBody: "ClawHub has no results for “{query}”.",
    noDiscoverMatchTitle: "Nothing to discover matches",
    featuredGroup: "Featured",
    officialGroup: "Official plugins",
    connectorsGroup: "Connect your world",
    connectorsHint:
      "One-click MCP connectors and hand-picked ClawHub searches for popular services.",
    connectorGroupWork: "Work & productivity",
    connectorGroupDev: "Coding & infrastructure",
    connectorGroupHome: "Home & media",
    connectorGroupLife: "Everyday life",
    mcp: "MCP",
    oauth: "OAuth",
    connectorMcpNote: "One-click MCP server",
    connectorClawHubNote: "Community plugins on ClawHub",
    connectorAdd: "Add",
    connectorSearch: "Find on ClawHub",
    connectorAdded: "Added",
    connectorAddedOauth: "Added {name}. Authenticate with “{command}”, then restart the gateway.",
    connectorAddedEndpoint:
      "Added {name}. Update the endpoint and credentials in MCP settings before use.",
    connectorAddedReady: "Added {name}. New agent sessions can use it right away.",
    connectorDescriptions: {
      notion: "Search, create, and update pages and databases in your Notion workspace.",
      linear: "Triage issues, update cycles, and file bugs straight from chat.",
      todoist: "Read, add, and complete tasks and projects in Todoist.",
      airtable: "Query and update records, tables, and bases in Airtable.",
      jira: "Create, search, and triage Jira tickets from chat.",
      canva: "Create and edit Canva designs, manage assets, and export results.",
      stripe: "Check payments, customers, invoices, and subscriptions in your Stripe account.",
      googleCalendar: "Read, create, and get briefed on events — your agent owns your schedule.",
      email: "Mailbox triage, summaries, and drafts with send-on-approval.",
      pdf: "Extract, merge, convert, and OCR PDF documents.",
      transcription: "Turn audio and video into clean, structured transcripts.",
      github: "PR review queues, issue triage, and repo Q&A through the official GitHub MCP.",
      sentry: "Crash alerts explained and triaged the moment they fire.",
      context7: "Version-specific library docs and code examples while coding. No signup needed.",
      deepwiki: "Ask questions about any public GitHub repo. Free, no account needed.",
      huggingFace: "Search models, datasets, and papers; run Spaces as tools.",
      grafana: "Grafana know-how and community connectors for dashboards and alerts.",
      kubernetes: "Cluster operations and troubleshooting from chat.",
      homeAssistant: "Control lights, climate, and automations across your whole home.",
      spotify: "Search, queue, and soundtrack your day with mood-based playlists.",
      sonos: "Whole-home audio: play, group rooms, and queue by chat.",
      reddit: "Browse, search, and summarize subreddits and threads.",
      markets: "Live stocks and crypto with price alerts and daily digests.",
      travel: "Flight and hotel search with fare watching and trip memory.",
      news: "A personalized daily briefing: news, weather, and tasks in one message.",
      maps: "Places, routing, and travel-time answers.",
      translation: "Translate and localize text and documents.",
      notes: "Capture notes to Markdown, Obsidian, Notion, or Bear.",
    },
    noInstalledTitle: "No optional plugins installed",
    noInstalledBody: "Discover a featured plugin or search ClawHub to extend OpenClaw.",
    noInstalledMatchTitle: "No installed plugins match",
    noMatchBody: "Try a different search.",
    filterAll: "All",
    filterIssues: "Issues",
    filterLabel: "Filter installed plugins",
    categoryChannels: "Channels",
    categoryProviders: "Model providers",
    categoryMemory: "Memory",
    categoryContextEngine: "Context engines",
    categoryTools: "Tools",
    categoryOther: "Other",
    mcpServersGroup: "MCP servers",
    mcpSettingsLink: "MCP settings",
    mcpHint:
      "Connect Model Context Protocol servers to give your agent extra tools. Changes apply to new agent sessions.",
    mcpEmpty: "No MCP servers configured yet. Add one here or pick a connector from Discover.",
    remove: "Remove",
    removing: "Removing…",
    removeNamed: "Remove {name}",
    removeConfirmTitle: "Remove {name}?",
    removeConfirmMessage:
      "Removing this plugin package and all of its entries restarts the Gateway immediately and interrupts active sessions.",
    cancel: "Cancel",
    removedRestart: "Removed {name}. A Gateway restart is required to apply the change.",
    verifiedSource: "Verified source",
    enableAction: "Enable",
    disableAction: "Disable",
    working: "Working…",
    detailClose: "Close",
    detailOrigin: "Source",
    detailCategory: "Category",
    detailPackage: "Package",
    detailPluginId: "Plugin ID",
    capabilities: "Capabilities",
    offlineTitle: "Gateway offline",
    offlineBody: "Connect to browse installed and recommended plugins.",
    optionalCapability: "Optional OpenClaw capability.",
    enabled: "Enabled",
    disabled: "Disabled",
    available: "Available",
    needsAttention: "Needs attention",
    included: "Included",
    global: "Global",
    workspace: "Workspace",
    config: "Config",
    official: "Official",
    codePlugin: "Code plugin",
    bundlePlugin: "Bundle plugin",
    unavailable: "Unavailable",
    install: "Install",
    installing: "Installing…",
    installNamed: "Install {name}",
    installConfirmTitle: "Install {name}?",
    installConfirmMessage:
      "Installing this plugin restarts the Gateway immediately and interrupts active sessions.",
    policyReviewTitle: "Security review needed",
    policyReviewBodyKnown: "Policy warnings: {count}. Not installed.",
    policyReviewBodyReason: "{reason} Not installed.",
    policyReviewFindings: "Findings",
    policyReviewSeverityInfo: "Info",
    policyReviewSeverityWarn: "Warning",
    policyReviewSeverityCritical: "Critical",
    policyReviewTechnicalDetails: "Details",
    policyReviewScope:
      "Install anyway approves every install-policy warning encountered during this install. Each warning is checked again before installation continues.",
    installAnyway: "Install anyway",
    connectToChange: "Connect to the gateway to change plugins.",
    adminRequired: "Plugin changes require operator.admin access.",
    changesDisabled: "This gateway does not allow plugin changes.",
    configRefreshFailed: "Could not refresh Control UI configuration: {error}",
    installedSuccess: "Installed {name}.",
    installedRestart: "Installed {name}. A Gateway restart is required to apply the change.",
    enabledSuccess: "Enabled {name}.",
    enabledRestart: "Enabled {name}. A Gateway restart is required to apply the change.",
    disabledSuccess: "Disabled {name}.",
    disabledRestart: "Disabled {name}. A Gateway restart is required to apply the change.",
  },
  labsPage: {
    intro:
      "Labs contains experimental capabilities that may change, break, or disappear between releases.",
    sectionTitle: "Experimental features",
    sectionDescription: "Changes save immediately. Some features require a restart to take effect.",
    documentation: "Documentation",
    restartRequired: "Gateway restart required.",
    saveErrorTitle: "Could not update feature",
    saveFailed: "The feature setting could not be saved.",
    codeMode: {
      title: "Code Mode",
      description:
        "Set the global default for compact, sandboxed JavaScript tool workflows. On selects Auto for evaluated models; Off disables the default. Per-model Code Mode overrides are in Agent Defaults → Models (Advanced).",
    },
    swarm: {
      title: "Swarm",
      groupTitle: "Parallel tasks",
      description: "Coordinate parallel subagents and collect their results.",
      defaultPhase: "Unphased",
      progress: "{complete} of {total}",
      active: "{running} running · {queued} queued · {failed} failed",
      finished: "{done} completed · {failed} failed",
      childOutcome: "Child runs finished. Check the conversation for the final response.",
      details: "Child details",
      detailsUnavailable: "Child details are unavailable. Counts include all accepted workers.",
      otherGroups: "{count} more active groups",
    },
    toolSearch: {
      title: "Tool Search for all models",
      description:
        "Local models use Tool Search automatically. Enable this override to defer tool schemas for all models; turning it off restores each model's default.",
    },
    loopDetection: {
      title: "Tool-loop detection",
      description:
        "Enable rolling-history guards that warn or block repeated tool calls when an agent stops making progress.",
    },
    localModelLean: {
      title: "Lean tools for local models",
      description:
        "Remove optional tools such as browser and automations. Leave this off to keep those capabilities available through Tool Search.",
    },
    cliAgents: {
      title: "CLI agents",
      description:
        "Show external CLI session engines in the new-session model picker when their plugins support creating sessions.",
    },
    customPluginUi: {
      title: "Custom plugin UI",
      description:
        "Let installed plugins add pages, widgets, and custom views. Their JavaScript runs with your signed-in permissions, so enable only plugins you trust. Bundled plugin views remain available.",
      restartRequired:
        "Restart the Gateway and reload this browser tab after changing this setting.",
    },
    auditMessages: {
      title: "Message audit metadata",
      description:
        "Record content-free metadata for direct conversations in the audit ledger. Message content is never stored.",
    },
    hostDesktop: {
      title: "Host Desktop",
      description:
        "Watch and control this Gateway machine from the Desktop panel through its existing VNC or Screen Sharing server.",
    },
    workerDesktop: {
      title: "Cloud Worker Desktop",
      description:
        "Watch and control node-carried desktops from capable Crabbox AWS or Hetzner profiles with desktop: true.",
    },
  },
  aboutPage: {
    productName: "OpenClaw",
    tagline: "Your personal AI assistant, running on your own devices.",
    waveHello: "Wave hello to Clawd",
    linksLabel: "Community and resources",
    linkWebsite: "Website",
    linkDocs: "Docs",
    linkGitHub: "GitHub",
    linkDiscord: "Discord",
    linkX: "X (Twitter)",
    linkChangelog: "Changelog",
    license: "© 2026 OpenClaw Foundation — MIT License.",
    artifactTitle: "Control UI",
    artifactSubtitle: "Identity embedded when this browser artifact was built.",
    artifactDetails: "Control UI build details",
    version: "Version",
    commit: "Commit",
    branch: "Branch",
    built: "Built",
    unavailable: "Unavailable",
    copyCommit: "Copy full commit hash",
    copyingCommit: "Copying commit hash",
    copiedCommit: "Commit hash copied",
    copyCommitFailed: "Could not copy commit hash",
    gateway: "Gateway",
    gatewayVersion: "Connected Gateway version",
    gatewayVersionHint:
      "Reported by the active Gateway connection; separate from this Control UI build.",
  },
  appsPage: {
    heroTitle: "Take OpenClaw everywhere",
    heroTagline:
      "Companion apps for your phone, watch, desktop, and browser — plus plugins to extend what your agent can do.",
    sectionMobile: "On your phone",
    havePhone: "Already have the app?",
    pairDevice: "Pair your device",
    sectionWatch: "On your wrist",
    sectionDesktop: "On your desktop",
    sectionBrowser: "In your browser",
    sectionCommunity: "Community",
    badgeBundledIos: "Included with the iOS app",
    badgeBundledAndroid: "Included with the Android app",
    ctaAppStore: "App Store",
    ctaPlayStore: "Google Play",
    ctaDownload: "Download",
    ctaOpenMac: "Open in Mac app",
    ctaDocs: "Docs",
    ctaSetupGuide: "Setup guide",
    ctaChromeWebStore: "Chrome Web Store",
    ctaOpenPlugins: "Open Plugins",
    ctaBrowseClawHub: "Browse ClawHub",
    linkDiscord: "Discord community",
    linkDocs: "Docs",
    cards: {
      ios: {
        title: "iPhone",
        desc: "Chat, talk, approve actions, and share into OpenClaw from iOS.",
      },
      android: {
        title: "Android",
        desc: "Your Android phone as a full OpenClaw device — chat, camera, and Canvas.",
      },
      appleWatch: {
        title: "Apple Watch",
        desc: "Glanceable chats and quick replies from your wrist.",
      },
      wearOs: {
        title: "Wear OS",
        desc: "The Android companion extends OpenClaw to your watch.",
      },
      macos: {
        title: "macOS",
        desc: "Menu bar companion for your Gateway — notifications, approvals, quick chat.",
      },
      windows: {
        title: "Windows",
        desc: "The Windows companion connects your PC as an OpenClaw device.",
      },
      linux: {
        title: "Linux",
        desc: "Native desktop app — .deb and AppImage builds.",
      },
      chrome: {
        title: "Chrome extension",
        desc: "Let OpenClaw drive your existing Chrome — tabs, pages, and forms.",
      },
      plugins: {
        title: "Plugins & ClawHub",
        desc: "Extend OpenClaw with channels, tools, and skills from the community.",
      },
    },
  },
  presence: {
    rosterTitle: "Online",
    idle: "Idle",
    offline: "Offline",
    card: {
      details: "Details for {name}",
      loadFailed: "Could not open details. Try again, or open this person’s Activity page.",
      ariaLabel: "Activity for {name}",
      person: "Person",
      onlineFor: "Online for",
      where: "Where",
      reportedTimeZone: "Reported time zone: {zone}",
      lastActivity: "Last activity",
      notObserved: "Not observed yet",
      viewingNow: "Viewing now",
      recentSessions: "Recent sessions",
      noVisibleSessions: "No visible sessions being viewed.",
      noRecentSessions: "No recent visible sessions.",
      sessionUpdated: "Session updated",
      ago: "ago",
      viewActivity: "View activity",
      controlUi: "Control UI",
      cli: "Command line",
      app: "App",
    },
  },
  activityFeed: {
    partialHistory:
      "People and counts describe visible recorded session associations; retained history and this people list may be incomplete.",
    sessionsMode: "Sessions",
    searchPlaceholder: "Search session titles…",
    time: "Time",
    time24h: "Last 24 hours",
    time7d: "Last 7 days",
    time30d: "Last 30 days",
    timeAll: "All time",
    everyone: "Everyone",
    peopleButtonLabel: "Filter sessions by person",
    lastActive: "· {time}",
    unresolvedIdentities: "Unresolved identities",
    clearPersonFilter: "Clear person filter",
    sessions: "Sessions",
    showing: "Showing {shown} of {total}",
    today: "Today",
    yesterday: "Yesterday",
    unknownDate: "Unknown date",
    noSessions: "No sessions match these filters.",
    automationGroup: "{count} automation sessions",
    automation: "Automation",
    inspectRun: "Inspect run",
    backToSessions: "Back to sessions",
    channelLabel: "Channel: {value}",
    agentLabel: "Agent: {value}",
    online: "Online",
    idle: "Idle",
    offline: "Offline",
    unknownDevice: "Unknown device",
    lastInput: "Last input {time} ago",
    viewingNow: "Viewing now",
    notViewing: "Not viewing a session right now.",
    notFoundTitle: "Person not found",
    notFoundDescription: "No online presence or visible session association matches this identity.",
  },
  githubPublication: {
    personal: "My GitHub",
    system: "System",
    agent: "Agent override",
    publishAs: "Publish as @{account}",
    account: "Publication account",
    choose: "Choose an account",
    newAction: "Choose a new publication",
    capacity:
      'Too many publications are awaiting review. Finish an existing publication, then select "{newAction}" to make room. Existing retries remain available.',
    confirm: "Confirm original publication",
    check: "Check publication",
    refresh: "Refresh publication",
    unknown:
      "The outcome is unknown. Retry keeps the original account and request; it does not switch accounts.",
    target: "Pull request: {repository} \u2192 {base}",
    pushTarget: "Push: {repository} \u00b7 {branch}",
    snapshot: "Original accepted snapshot",
    head: "HEAD",
    index: "Index tree",
    workspace: "Workspace tree",
    dispatched: "GitHub {kind} was dispatched; its remote outcome may still be unknown.",
    observed: "GitHub {kind} was observed. Disconnecting does not undo this effect.",
    effectLink: "View GitHub effect",
    effectPush: "push",
    effectPullRequest: "pull request",
    personalWorkspace:
      "My GitHub requires an idle, reconciled local workspace. Wait for work to finish and reclaim the workspace. System and agent publication keep their existing shared flow.",
    scopeHelp:
      "My GitHub applies only to this explicit Publish PR action. Agent commands, previews, and cloud workers keep the shared account.",
    unidentified: "Sign in with a personal profile to use My GitHub.",
    connectHelp:
      "Connect or repair My GitHub in Settings \u2192 Profile \u2192 GitHub connections.",
  },
  githubConnections: {
    title: "GitHub connections",
    description:
      "Check the accounts used for agent commands, dashboard data, and publishing. These connections are separate from your GitHub sign-in and co-author credit.",
    mine: "My GitHub",
    system: "System GitHub",
    personalDescription: "Your account for explicitly selected Publish PR actions.",
    systemDescription:
      "Default account for agent commands, authenticated dashboards, and publishing.",
    unboundDescription:
      "Sign in with a personal Gateway profile to connect My GitHub. Administrators can still manage System GitHub.",
    signInRequired: "Personal sign-in required",
    connected: "Connected",
    disconnected: "Not connected",
    notLoaded: "Status not loaded",
    checking: "Checking connection…",
    statusUnavailable: "Connection status unavailable",
    manage: "Manage connections",
    reconnectRequired: "Reconnect required",
    connectMine: "Connect My GitHub",
    changeMine: "Change My GitHub",
    changeSystem: "Change System GitHub",
    disconnectMine: "Disconnect My GitHub",
    disconnectDescription:
      "Stops unfinished personal publication. Published work stays intact; revoke the app grant separately in GitHub settings.",
    adminManaged: "Admin managed",
    purpose: "Connect GitHub",
    forMe: "For me",
    forSystem: "For the system",
    purposeHint: "Only the selected connection changes.",
    continue: "Continue with GitHub",
    usage: "Where these accounts are used",
    usageDescription:
      "My GitHub is used only when you explicitly select it for Gateway-brokered Publish PR on an idle, reconciled local workspace. Publication still needs write access to the session. Agent git/gh, model actions, previews, and workers keep the shared account. Finish and reclaim remote work before personal publication. Connecting My GitHub changes no defaults.",
    details: "Connection details",
    agentTitle: "GitHub account",
    agentFor: "GitHub for {agent}",
    agentDescription:
      "Used for this agent's commands and authenticated dashboard data. Verified confirms the account; repository access is checked when data is requested.",
    viewAgent: "View agent account",
    agentOverride: "Agent override",
    advancedOverride: "Advanced: agent GitHub override",
    manageCommon: "Manage connections in Profile",
  },
  profilePage: {
    offline: "Connect to the gateway to meet your agent.",
    usageStatistics: "Usage statistics",
    usageStatisticsDescription: "View activity, costs, and usage trends.",
    identity: {
      title: "Identity",
      menuLabel: "Identity menu",
      menuButtonLabel: "Identity and app menu for {name}",
      description: "Your profile on this gateway.",
      loading: "Loading your identity…",
      profileUnavailable: "Your identity profile could not be loaded.",
      unidentified:
        "This connection has no personal profile; sign in through Cloudflare Access, Tailscale Serve, or a trusted proxy to set a name and avatar.",
      writeRequired: "Profile editing requires operator.write access.",
      avatar: "Avatar",
      avatarDescription: "PNG, JPEG, or WebP. Images are resized to 256 × 256 or smaller.",
      chooseAvatar: "Choose image",
      processingAvatar: "Processing…",
      displayName: "Display name",
      displayNameDescription: "Shown to other people using this gateway.",
      linkedEmails: "Linked emails",
      linkedEmailsDescription: "Email addresses connected to this profile.",
      githubAccount: "GitHub account",
      githubAccountDescription:
        "Verified sign-in identity, not permission to publish. Manage publishing access under GitHub connections below.",
      githubVerified: "Verified from your GitHub-backed sign-in",
      githubUnavailable: "Unavailable",
      githubUnavailableDescription: "GitHub-backed sign-in is unavailable. Refresh to retry.",
      ownerGithubDescription:
        "GitHub-backed sign-in through Cloudflare Access or Tailscale Serve provides this identity.",
      gitCoauthor: "Git co-author credit",
      gitCoauthorDescription:
        "Adds this account's public GitHub noreply address to commits created from shared sessions. Turning it off affects future commits only.",
      gitCoauthorUnavailable:
        "Available after your GitHub-backed sign-in is verified. Refresh to retry.",
      ownerGitCoauthorDescription:
        "Requires GitHub-backed sign-in through Cloudflare Access or Tailscale Serve.",
      avatarErrors: {
        invalid: "That image could not be processed.",
        sourceTooLarge: "Choose an image that is 10 MB or smaller.",
        tooLarge: "The processed avatar is larger than 512 KB.",
      },
    },
    modelAccounts: {},
  },
  tasksPage: {
    active: "Active",
    activeSub: "Queued and running background work.",
    recent: "Recent",
    recentSub: "Latest completed, failed, and cancelled tasks.",
    loading: "Loading tasks…",
    empty: "No background tasks yet.",
    emptyActive: "No queued or running tasks.",
    emptyRecent: "No recent completed tasks.",
    disconnected: "Connect to the gateway to load and manage tasks.",
    loadFailed: "Could not load tasks.",
    cancelFailed: "Could not cancel the task.",
    recoveryFailed: "Could not update completion delivery.",
    invalidResponse: "The gateway returned an invalid task list.",
    untitled: "Background task",
    taskCount: "{count} tasks",
    taskCountOne: "1 task",
    agent: "Agent: {agent}",
    openSession: "Open session",
    cancelTask: "Cancel {title}",
    cancelling: "Cancelling…",
    retryDelivery: "Retry delivery",
    dismissDelivery: "Dismiss delivery",
    copyResult: "Copy result",
    deliveryBlocked: "Completed, but result delivery is blocked.",
    deliveryDismissed: "Completed; result delivery was dismissed.",
    duplicateRisk: "Retrying may duplicate a result after an ambiguous acknowledgement.",
    status: {
      queued: "Queued",
      running: "Running",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
      timedOut: "Timed out",
    },
    runtime: {
      subagent: "Subagent",
      cron: "Automation",
      acp: "ACP",
      cli: "CLI",
      unknown: "Task",
    },
  },
  skillWorkshop: {
    title: "Skill Workshop",
    header: {
      selfLearning: "Self-learning",
      selfLearningAria: "Toggle autonomous self-learning",
      selfLearningTooltip:
        "Capture corrections and review completed work as reusable skills. Automatic mode applies scanner-approved captures to Skills.",
    },
    sections: {
      aria: "Workshop sections",
      skills: "Skills",
      suggestions: "Suggestions",
    },
    collection: {
      search: "Search installed skills\u2026",
      searchLabel: "Search installed skills",
      refresh: "Refresh skills",
      shelfLabel: "Installed skills",
      count: "{count} installed",
      countOne: "1 installed",
      countFiltered: "{shown} of {total} installed",
      countUnavailable: "Count unavailable",
      loading: "Loading installed skills\u2026",
      loadingSkill: "Loading {name}\u2026",
      errorTitle: "Could not load installed skills",
      errorBody: "Try again to reload the list.",
      readErrorTitle: "Could not open {name}",
      emptyTitle: "No skills installed yet",
      emptyBody: "Apply a suggestion and it appears here as an installed skill.",
      seeSuggestions: "See suggestions",
      noMatchTitle: "No skills match that search",
      noMatchBody: "Clear the search or try another word.",
      clearSearch: "Clear search",
      pickTitle: "Pick a skill",
      pickBody: "Select a skill to see its instructions or changes.",
      changes: "Instruction changes",
      savedOn: "Changes since {date}",
      changedSince: "Changed since {date}",
      noChanges: "No instruction changes",
      savedVersion: "Saved version → current",
      savedNote:
        "Compares saved instructions with the installed skill. Intermediate edits and supporting files are not shown.",
      noSavedVersion: "No saved version is available to compare with this skill.",
      savedVersionError: "Could not load saved versions. Refresh to try again.",
      comparing: "Comparing saved instructions…",
      unchanged: "The instructions match this saved version.",
    },
    recency: {
      today: "Today",
      yesterday: "Yesterday",
      earlier: "Earlier",
    },
    previewContext: "in {slug}",
    actions: {
      close: "Close",
      cancel: "Cancel",
      previous: "Previous",
      next: "Next",
      apply: "Apply",
      applying: "Applying\u2026",
      evaluate: "Evaluate",
      evaluating: "Evaluating\u2026",
      evaluated: "Evaluated",
      revise: "Revise",
      opening: "Opening\u2026",
      reject: "Reject",
      rejecting: "Rejecting\u2026",
      sending: "Sending\u2026",
    },
    notices: {
      applied: "Applied",
      proposalChanged:
        "Suggestion changed. Review the updated draft before choosing another action.",
      rejected: "Rejected",
      revisionRequested: "Revision requested",
    },
    revision: {
      title: "{verb} suggestion",
      description:
        "Tell the agent what should change. The suggestion stays pending and the workshop creates a revised version.",
      placeholder:
        "Example: Make this use Gmail labels instead of unread search, and add a safer dry-run step.",
      preparing: "Waiting for chat admission",
      notAdmitted:
        "Revision request was not admitted. Your instructions are still available; review the error and retry. {error}",
      send: "Send revision",
    },
    queue: {
      resize: "Resize list",
      searchSuggestions: "Search suggestions\u2026",
      searchHistory: "Search records\u2026",
      suggestionsLabel: "Search suggestions",
      historyLabel: "Search records",
      loadError: "Could not load this list.",
      loading: "Loading\u2026",
      noMatch: "Nothing matches that search.",
      noSuggestions: "No suggestions waiting.",
      noRecords: "No records yet.",
      noRecordsStatus: "No {status} records.",
    },
    detail: {
      edited: "Edited {time}",
      created: "Created {time}",
      supportFiles: "{count} support files",
      noSupportFiles: "0 support files",
      loading: "Loading\u2026",
      draftMissing:
        "This suggestion's draft is missing. Reject it and ask your agent to create a new suggestion.",
      supportFilesTitle: "Support files",
      clickToPreview: "\u00b7 click to preview",
    },
    evaluation: {
      title: "Evaluation",
      version: "Suggestion {version}",
      completedAt: "Completed {time}",
      status: {
        completed: "Completed",
        skipped: "Skipped",
        error: "Error",
      },
      decision: {
        pass: "Pass",
        revise: "Revise",
        block: "Block",
      },
      severity: {
        info: "Info",
        warn: "Warning",
        critical: "Critical",
      },
      evaluatorVersion: "Evaluator {version}",
      mode: "Mode {mode}",
      findings: "Findings",
      metrics: "Metrics",
      fileLine: "{file}:{line}",
      errors: {
        revisionHashUnavailable: "The current suggestion revision could not be identified.",
        revisionChanged: "The suggestion revision changed during evaluation.",
      },
    },
    empty: {
      searchTitle: "Nothing matches that search",
      searchBody: "Clear the search or try another word.",
      pendingTitle: "No suggestions waiting",
      pendingBody: "New suggestions appear here when they need review.",
      defaultAgent: "Your agent",
      noProposalsAria: "No Skill Workshop suggestions",
      noProposalsTitle: "No suggestions yet",
      noProposalsBody: "{agent} hasn\u2019t suggested any skills.",
      noProposalsFooter: "New suggestions appear here for review.",
    },
    selfLearning: {
      pitchTitle: "Turn on self-learning",
      pitchBody:
        "OpenClaw reviews corrections and substantial completed runs, then applies scanner-approved skills and lists them here. Experience review spends extra background tokens.",
      enable: "Enable self-learning",
      enabling: "Enabling\u2026",
      updateError: "Could not update the self-learning setting.",
    },
    history: {
      eyebrow: "Past work",
      title: "Find reusable workflows",
      body: "Review substantial sessions from newest to oldest. Only strong recovery patterns or workflows that save repeated tool calls become suggestions.",
      findIdeas: "Find skill ideas",
      scanEarlier: "Scan earlier work",
      scanNew: "Scan new work",
      scanning: "Reviewing sessions\u2026",
      loading: "Loading history\u2026",
      pendingOnly: "Creates suggestions only. Uses your configured model.",
      reviewed: "{count} sessions reviewed",
      found: "{count} ideas found",
      noSessions: "No substantial sessions found in this window.",
      today: "today",
    },
  },
  // Chat swarm summaries render before the lazy Activity catalog loads.
  // Keep their shared label in startup English; Activity replaces the full namespace on entry.
  activity: {
    status: {
      done: "Done",
    },
  },
  gatewayLogs: {
    title: "Logs",
    subtitle: "Gateway file logs (JSONL).",
    exportButton: "Export {label}",
    exportLabels: {
      filtered: "filtered",
      visible: "visible",
    },
    filter: "Filter",
    searchPlaceholder: "Search logs",
    autoFollow: "Auto-follow",
    file: "File: {file}",
    truncated: "Log output truncated; showing latest chunk.",
    empty: "No log entries.",
  },
  pluginTabs: {
    unavailableTitle: "Plugin panel unavailable",
    unavailableSubtitle:
      "The plugin that owns this tab is not active on the connected gateway, or it did not provide a panel.",
  },
  logbook: {
    nav: {
      previousDay: "Previous day",
      nextDay: "Next day",
      today: "Today",
    },
    status: {
      capturing: "Capturing every {seconds}s",
      paused: "Capture paused",
      disabled: "Capture off",
      nodeHelp: "Device providing screen snapshots.",
      pending: "{count} frames queued",
      pendingHelp: "Snapshots waiting for the next analysis batch.",
      analyzing: "Analyzing…",
      captureError: "Capture error",
      batchError: "Analysis error",
      modelMissing: "No vision model",
      modelMissingHelp:
        "Set plugins.entries.logbook.config.visionModel or configure tools.media models.",
    },
    actions: {
      pause: "Pause",
      resume: "Resume",
      analyzeNow: "Analyze now",
    },
    empty: {
      title: "Nothing on the timeline yet.",
      subtitle:
        "Logbook is collecting snapshots; cards appear after the first analysis batch completes.",
    },
    card: {
      keyframeAlt: "Screen snapshot from this activity",
      distractions: "Distractions",
    },
    stats: {
      title: "Day at a glance",
      focus: "{pct}% focus",
      tracked: "{duration} tracked",
    },
    standup: {
      title: "Daily standup",
      generate: "Generate",
      refresh: "Regenerate",
      empty: "Turn today's timeline into a ready-to-paste standup update.",
    },
    ask: {
      title: "Ask your day",
      placeholder: "When did I review the gateway PR?",
      submit: "Ask",
    },
  },
  board: {
    label: "Session dashboard",
    tabsLabel: "Dashboard tabs",
    moreTabs: "More dashboard tabs",
    gridLabel: "Dashboard widgets",
    emptyTitle: "A clear board, ready for work",
    emptyHint: "Your agent can pin widgets here — try asking for a status card.",
    actionFailed: "The dashboard change could not be saved.",
    actionInProgress: "Another dashboard change is still being saved.",
    announcement: {
      moved: "Moved {title}.",
      resized: "Resized {title}.",
      removed: "Removed {title}.",
      granted: "Widget access allowed.",
      rejected: "Widget access rejected.",
    },
    widget: {
      cellLabel:
        "Dashboard widget: {title}. Use arrow keys to navigate. Hold Alt and press an arrow key to move it.",
      moveHandle: "Move {title}",
      resizeHandle: "Resize {title}",
      menuLabel: "Widget options",
      moveToTab: "Move to tab",
      noOtherTabs: "No other tabs",
      resize: "Resize",
      autoHeight: "Auto height",
      remove: "Delete",
      needsApproval: "Needs approval",
      needsApprovalDetail: "This widget requested additional access.",
      networkAccess: "Network origins",
      hostTools: "Host tools and data",
      activeCapabilities: "Active widget capabilities",
      networkCapability: "Network: {capability}",
      toolCapability: "Tool: {capability}",
      granted: "Granted",
      allow: "Allow",
      allowFailed: "Could not allow widget access. Try again.",
      reject: "Reject",
      rejectFailed: "Could not reject widget access. Try again.",
      rejected: "Access rejected",
      rejectedDetail: "This widget stays inactive until it is removed or replaced.",
      appLoading: "Restoring app…",
      appStaleTitle: "This pinned app is stale",
      appStaleDetail: "Its server, resource, or originating transcript is no longer available.",
      retry: "Retry",
      frameResolverMissing: "Widget content is unavailable.",
      sandboxUnavailable: "Widget sandbox host is unavailable.",
      frameAuthorizationFailed: "Widget authorization failed after repeated refresh attempts.",
      sandboxOriginRequired:
        "Widget authorization failed after repeated refresh attempts. If the gateway runs behind a reverse proxy or tunnel that does not route the widget sandbox port, set mcp.apps.sandboxOrigin to a dedicated public origin routed to the sandbox listener.",
      errorTitle: "This widget could not load",
      errorDetail: "The problem is contained to this card.",
      actionErrorTitle: "Widget change failed",
      actionErrorDetail: "The dashboard kept the previous widget state.",
      errorShow: "Show details",
      kindMcp: "MCP",
      kindHtml: "HTML",
      kindPlugin: "Plugin",
      kindReport: "Report",
      pluginLoading: "Loading plugin widget…",
      disabledPlugin: "Widget from disabled plugin {pluginId}",
    },
  },
  connection: {
    disconnectedTitle: "Disconnected",
    queuedCount: "{count} queued",
    reconnecting: "Reconnecting…",
    restarting: "Restarting…",
    suspending: "Suspending…",
    suspended: "Suspended",
    retryNow: "Retry now",
    actionsUnavailable: "Actions are unavailable while the Gateway reconnects.",
    settingsChangesUnavailable:
      "Changes to settings are disabled while the Gateway is reconnecting.",
    sessionOperationCompletedPreviousConnection:
      "The session operation completed on the previous connection. Check the current session list before continuing.",
    sessionOperationCompletedPreviousConnectionWithRefreshError:
      "The session operation completed on the previous connection, but refreshing the current session list failed: {error}",
    scopeUpgrade: {
      limited: "This browser has limited access.",
      guidance:
        "This browser has limited access. Manage it with openclaw devices on the Gateway or from Devices on an admin browser.",
      status: "Limited access",
      inboxState: "Administrator access required",
      showDetails: "Show limited access details",
      closeDetails: "Close limited access details",
      request: "Request admin",
      requesting: "Requesting administrator access…",
      requestingAction: "Requesting…",
      pending:
        "Approve this browser by running openclaw devices on the Gateway or from Devices on an admin browser. Retry reattaches to the request; Cancel stops waiting.",
      retry: "Retry",
      cancel: "Cancel",
      rejected: "The administrator access request was rejected.",
      expired: "The administrator access request expired.",
      error: "Administrator access request failed: {error}",
    },
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      passwordPlaceholder: "system or shared password",
      sessionKey: "Default Session Key",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
      showToken: "Show token",
      hideToken: "Hide token",
      toggleTokenVisibility: "Toggle token visibility",
      showPassword: "Show password",
      hidePassword: "Hide password",
      togglePasswordVisibility: "Toggle password visibility",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      lastError: "Last error",
    },
    help: {
      title: "How to connect",
      step1: "Start the gateway on your host machine:",
      step2: "Get a tokenized dashboard URL:",
      step3: "Paste the WebSocket URL and token above, or open the tokenized URL directly.",
      docsLink: "Read the docs →",
      copyCommand: "Copy command",
      copyCommandAria: "Copy command: {command}",
    },
  },
  attention: {
    cronErrorUnknown: "Unknown error",
    cronFailed: "{job} failed",
    cronOverdue: "{job} overdue",
    automationFailed: "Failed · {time}",
    automationOverdue: "Overdue · {time}",
    failed: "Failed",
    overdue: "Overdue",
    dismissItem: "Dismiss {item}",
    dismissShown: "Dismiss shown",
    emptyTitle: "Nothing waiting",
    emptyBody: "New requests and alerts land here.",
    issues: "Inbox",
    issueCount: "{count} inbox item",
    issueCountPlural: "{count} inbox items",
    tabs: {
      label: "Inbox categories",
      all: "All",
      approvals: "Approvals",
      mentions: "Mentions",
      automations: "Automations",
      system: "System",
    },
    mentions: {
      from: "{sender} mentioned you",
      open: "Open",
      dismiss: "Dismiss",
      dismissing: "Dismissing…",
      emptyTitle: "No mentions yet",
      emptyBody: "When someone mentions you in a chat, it appears here.",
      retention:
        "Mentions are temporary: kept for up to seven days and cleared when the Gateway restarts.",
      notifications: "Notification settings",
      loading: "Loading mentions…",
      unavailable: "Sign in and connect to the Gateway to see your mentions.",
      refresh: "Refresh mentions",
      error: "Mentions could not be updated. Refresh or try dismissing again.",
    },
    modelAuthExpired: "Model auth expired: {providers}",
    authExpired: "Auth expired",
    modelAuthExpiredState: "Auth expired · {time}",
    modelAuthExpiredWithScope: "{scope} · Auth expired · {time}",
    reconnect: "Reconnect",
    pendingApproval: "{count} pending approval",
    pendingApprovals: "{count} pending approvals",
    alerts: {
      updateQuestion:
        "These are the available update facts:\n{facts}\nSummarize what is new and whether anything needs my attention before updating.",
      cronFailedQuestion:
        "These automations failed:\n{facts}\nExplain why they failed and how to fix them.",
      cronOverdueFact: "{job}: {duration} late",
      cronOverdueQuestion:
        "These automations are overdue:\n{facts}\nExplain why they have not run and how to fix them.",
      modelAuthExpiredQuestion:
        "These model-provider credentials need attention:\n{facts}\nExplain what expired and how to re-authenticate them.",
    },
  },
  standingGrants: {
    title: "Standing grants",
    description:
      "Allow-always on an automation approval mints a standing grant for that exact command. Revoking one makes the next occurrence ask again.",
    historyTitle: "Approval history",
    empty: "No standing grants. Answer an automation approval with Always allow to mint one.",
    stateUntilRevoked: "Until revoked",
    stateExpiresIn: "Expires in {count}d",
    stateExpired: "Expired",
    stateRevoked: "Revoked",
    revoke: "Revoke",
    revoking: "Revoking…",
    columns: {
      automation: "Automation",
      command: "Command",
      uses: "Uses",
      state: "State",
    },
  },
  approvalHistory: {
    description:
      "Terminal exec, plugin, and system-agent approvals recorded by this gateway, newest first.",
    loading: "Loading approval history…",
    loadingMore: "Loading…",
    loadMore: "Load more",
    empty: "No resolved approvals in the rolling 30-day window.",
    retention: "Approval history is a rolling 30-day window.",
    offline: "Connect to the gateway to load approval history.",
    invalidResponse: "The gateway returned an invalid approval history response.",
    unknown: "Unknown",
    notApplicable: "N/A",
    columns: {
      resolved: "Resolved",
      kind: "Kind",
      request: "Request",
      decision: "Decision",
      reason: "Resolution reason",
      source: "Source agent / session",
      resolver: "Resolver",
    },
    kinds: {
      exec: "Exec",
      plugin: "Plugin",
      systemAgent: "System agent",
    },
    statuses: {
      allowed: "Allowed",
      denied: "Denied",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    decisions: {
      allowOnce: "Allow once",
      allowAlways: "Always allow",
      deny: "Deny",
    },
    reasons: {
      user: "User decision",
      timeout: "Timed out",
      malformedVerdict: "Malformed verdict",
      noRoute: "No route",
      runAborted: "Run aborted",
      gatewayRestart: "Gateway restart",
      storageCorrupt: "Storage corrupt",
    },
  },
  palette: {
    placeholder: "Search chats and commands…",
    noResults: "No results",
    searchFailed: "Chat search failed — check the gateway logs and retry",
    modelSearchFailed: "Model search unavailable. Change your search to retry.",
    searchPartial: "Transcript search unavailable — showing chat titles and metadata",
    searchIncomplete: "Transcript matches may be incomplete — indexing or search limits apply",
    categories: {
      search: "Search",
      navigation: "Navigation",
      skills: "Skills",
    },
    items: {
      apps: "Apps",
      sessions: "Sessions",
      scheduled: "Automations",
      skills: "Skills",
      plugins: "Plugins",
      settings: "Settings",
      agents: "Agents",
      desktop: "Desktop",
    },
    descriptions: {
      verboseMode: "Toggle verbose mode.",
    },
    footer: {
      navigate: "navigate",
      select: "select",
      close: "close",
    },
  },
  dreaming: {
    tabs: {
      scene: "Scene",
      diary: "Diary",
      advanced: "Advanced",
    },
    header: {
      refresh: "Refresh",
      refreshing: "Refreshing…",
      on: "Dreaming On",
      off: "Dreaming Off",
      engineOff: "Memory engine is Off. Choose an engine in Settings to enable dreaming.",
    },
    toggleConfirmation: {
      subtitle: "Dreaming is a global setting; it is not scoped to this agent.",
      enableTitle: "Turn On Dreaming for All Agents",
      enableDetail:
        "The nightly dreaming sweep will run across every configured agent workspace, promoting short-term recalls into long-term memory. This applies right away.",
      enableConfirm: "Turn On Dreaming",
      disableTitle: "Turn Off Dreaming for All Agents",
      disableDetail:
        "The nightly dreaming sweep will stop for every configured agent, not just this one. Memories already written stay; nothing new gets promoted. This applies right away.",
      disableConfirm: "Turn Off Dreaming",
      saving: "Saving…",
      failed: "Could not apply change. Check your connection and try again.",
    },
    status: {
      active: "Dreaming Active",
      idle: "Dreaming Idle",
      promotedSuffix: "promoted",
      nextSweepPrefix: "next sweep",
    },
    scene: {
      backfill: "Backfill",
      dedupeDiary: "Dedupe Diary",
      reset: "Reset",
      clearGrounded: "Clear Replayed",
      repairCache: "Repair Dream Cache",
      working: "Working…",
    },
    phase: {
      light: "Light",
      deep: "Deep",
      rem: "Rem",
      off: "off",
    },
    advanced: {
      eyebrow: "Review",
      title: "Daily Log Review",
      description:
        "Review what came from the daily log, what is waiting for promotion, and what was promoted recently.",
      summaryFromDailyLog: "from daily log",
      summaryWaiting: "waiting",
      summaryPromotedToday: "promoted today",
      stagedTitle: "From the Daily Log",
      stagedDescription: "Replay candidates pulled from older daily log entries.",
      shortTermTitle: "Waiting for Promotion",
      shortTermDescription: "Current short-term candidates waiting to graduate into real memory.",
      sortRecent: "Most recent",
      sortSignals: "Strongest support",
      originDailyLog: "replayed",
      originLive: "live",
      originMixed: "mixed",
      promotedTitle: "Recent Promotions",
      promotedDescription: "Items that already made it through promotion.",
      emptyGrounded: "No staged grounded replay entries right now.",
      emptyShortTerm: "No short-term entries to inspect.",
      emptyPromoted: "No recent promotions to inspect.",
      updatedPrefix: "updated",
    },
    stats: {
      grounded: "Grounded",
      signals: "Signals",
    },
    diary: {
      title: "Dream Diary",
      noDreamsYet: "No dreams yet",
      noDreamsHint: "Dreams will appear here after the first dreaming cycle runs.",
      waitingTitle: "The diary is waiting",
      waitingHint: "Narrative entries will appear after the next dreaming cycle.",
      reload: "Reload",
      reloading: "Reloading…",
    },
    actions: {
      dedupeRemovedOneAndKept: "Removed {removed} duplicate dream entry and kept {kept}.",
      dedupeRemovedManyAndKept: "Removed {removed} duplicate dream entries and kept {kept}.",
      dedupeRemovedOne: "Removed {removed} duplicate dream entry.",
      dedupeRemovedMany: "Removed {removed} duplicate dream entries.",
      repairArchivedThreadCorpus: "archived session corpus",
      repairArchivedIngestionState: "archived ingestion state",
      repairArchivedDreamDiary: "archived dream diary",
      repairNoChanges: "Dream cache repair finished with no changes.",
      repairCompleteWithArchive: "Dream cache repair complete: {actions}. Archive: {archiveDir}",
      repairComplete: "Dream cache repair complete: {actions}.",
      backfillComplete: "Backfilled {count} dream diary entries.",
      resetDiaryComplete: "Removed {count} backfilled dream diary entries.",
      clearReplayedComplete: "Cleared {count} replayed short-term entries.",
      complete: "Dream diary action complete.",
      confirmRepairDescription:
        "This archives derived dream cache files and rebuilds them from clean inputs. Your dream diary stays untouched.",
      confirmDedupeDescription:
        "This rewrites DREAMS.md and removes only exact duplicate diary entries.",
      archivePathCopied: "Archive path copied.",
      archivePathCopyFailed: "Could not copy archive path.",
      updateFailed: "Could not update dreaming settings.",
      unsupportedPlugin: 'Selected memory plugin "{pluginId}" does not support dreaming settings.',
      configHashMissing: "Config hash missing; refresh and retry.",
    },
    wiki: {
      previewFallbackTitle: "Wiki page",
      close: "Close",
      loadingPage: "Loading wiki page…",
      dreamsTab: "Dreams",
      insightsTab: "Imported Insights",
      wikiTab: "Memory Wiki",
      dreamsExplainer:
        "This is the raw dream diary the system writes while replaying and consolidating memory; use it to inspect what the memory system is noticing, and where it still looks noisy or thin.",
      insightsExplainer:
        "These are imported insights clustered from external history; use them to review what imports surfaced before any of it graduates into durable memory.",
      wikiExplainer:
        "This is the compiled memory wiki surface the system can search and reason over; use it to inspect actual memory pages, claims, open questions, and contradictions rather than raw imported source chats.",
      copyArchivePath: "Copy archive path",
      loadingInsights: "Loading imported insights…",
      noInsights: "No imported insights yet",
      noInsightsHint:
        "Run a ChatGPT import with apply to surface clustered imported insights here.",
      candidateSignals: "Potentially useful signals",
      corrections: "Corrections or revisions",
      importDetails: "Import details",
      startedWith: "Started with:",
      endedOn: "Ended on:",
      messages: "Messages:",
      riskReasons: "Risk reasons:",
      labels: "Labels:",
      openSourcePage: "Open source page",
      loadingWiki: "Loading memory wiki…",
      emptyWiki: "Memory wiki is not populated yet",
      emptyWikiHint:
        "Right now the wiki mostly has raw source imports and operational reports. This tab becomes useful once syntheses, entities, or concepts start getting written.",
      claims: "Claims",
      openQuestions: "Open questions",
      contradictions: "Contradictions",
      pageDetails: "Page details",
      wikiPage: "Wiki page:",
      id: "Id:",
      openWikiPage: "Open wiki page",
      unavailable: "Memory Wiki is not enabled",
      unavailablePluginPrefix: "Imported Insights and Memory Wiki are provided by the bundled",
      unavailablePluginSuffix: "plugin.",
      enablePrefix: "Enable",
      enableSuffix: ", then reload this tab.",
      openConfig: "Open Config",
      howToEnable: "How to enable",
      pageTypes: {
        entity: "entity",
        concept: "concept",
        source: "source",
        synthesis: "synthesis",
        report: "report",
      },
      pageGroups: {
        sources: "Sources",
        syntheses: "Syntheses",
        reports: "Reports",
        entities: "Entities",
        concepts: "Concepts",
      },
      counts: {
        pageOne: "{count} page",
        pages: "{count} pages",
        claimRowOne: "{count} claim row",
        claimRows: "{count} claim rows",
        openQuestionOne: "{count} open question",
        openQuestions: "{count} open questions",
        contradictionOne: "{count} contradiction",
        contradictions: "{count} contradictions",
        chats: "{count} chats",
        sensitive: "{count} sensitive",
        signals: "{count} signals",
        messages: "{count} messages",
        userMessages: "{count} user",
        assistantMessages: "{count} assistant",
      },
      pageGroupSummary: "{label} · {count}",
      noPagesYet: "No pages yet",
      sectionPageSummary: "{label}: {count}",
      questionCountOnPages: "{questionCount} on {pageCount}",
      risk: {
        needsReview: "needs review",
        low: "low risk",
        medium: "medium risk",
        high: "high risk",
        unknown: "unknown risk",
      },
      pageNotFound: "No wiki page found for {lookup}.",
      previewTruncated: "Showing the first chunk of this page.",
      previewTruncatedWithTotal: "Showing the first chunk of this page ({count} total lines).",
      boundedResults: "Showing the newest {returned} of {total} items.",
      importedClusterSummary: "Imported chats clustered around {label}.",
      withheldDigestOne: "{count} digest was withheld pending review.",
      withheldDigests: "{count} digests were withheld pending review.",
      details: "Details",
      hideDetails: "Hide details",
      vault: "Vault",
      fullVaultBreakdown: "Full vault breakdown: {breakdown}.",
      selectedSection: "Selected section: {summary}.",
      latestUpdate: "Latest update {date}.",
      noContent: "No wiki content available.",
    },
    phrases: {
      consolidatingMemories: "consolidating memories…",
      tidyingKnowledgeGraph: "tidying the knowledge graph…",
      replayingConversations: "replaying today's conversations…",
      weavingShortTerm: "weaving short-term into long-term…",
      defragmentingMemoryLane: "defragmenting memory lane…",
      filingLooseThoughts: "filing away loose thoughts…",
      connectingDots: "connecting distant dots…",
      compostingContext: "composting old context windows…",
      alphabetizingSubconscious: "alphabetizing the subconscious…",
      promotingHunches: "promoting promising hunches…",
      forgettingNoise: "forgetting what doesn't matter…",
      dreamingEmbeddings: "dreaming in embeddings…",
      reorganizingAttic: "reorganizing the memory attic…",
      indexingDay: "softly indexing the day…",
      nurturingInsights: "nurturing fledgling insights…",
      simmeringIdeas: "simmering half-formed ideas…",
      whisperingVectorStore: "whispering to the vector store…",
    },
  },
  modelProviders: {},
  usage: {
    common: {
      emptyValue: "—",
      unknown: "unknown",
    },
    loading: {
      title: "Usage Overview",
      badge: "Loading",
    },
    metrics: {
      tokens: "Tokens",
      cost: "Cost",
      session: "session",
      sessions: "sessions",
    },
    heatmap: {
      title: "Token Activity",
      subtitle: "Daily token intensity for the selected range, up to one year.",
      cellTokens: "{tokens} tokens",
      less: "Less",
      more: "More",
    },
    providerUsage: {
      title: "Provider plans & billing",
      subtitle: "Live plan, quota, balance, and budget data reported by configured providers.",
      stalled: "Provider usage did not finish loading. Refresh to retry.",
      unavailable: "Provider usage is unavailable; the last request failed. Refresh to retry.",
      balance: "Balance",
      spend: "Usage",
      budget: "Budget",
      today: "Today",
      last7Days: "7 days",
      lastDays: "{count} days",
      dailyCost: "Daily provider cost",
      requests: "{count} requests",
      inputTokens: "{count} input",
      cacheTokens: "{count} cache",
      outputTokens: "{count} output",
      topModels: "Top models",
      costCategories: "Cost categories",
      remaining: "{percent}% left",
      resets: "Resets {date}",
    },
    presets: {
      today: "Today",
      last7d: "7d",
      last30d: "30d",
      last90d: "90d",
      last1y: "1y",
      all: "All",
    },
    scope: {
      instance: "Current instance",
      instanceHint: "Show only the active session id for each logical session.",
      family: "Historical lineage",
      familyHint: "Roll up known rotated transcript-backed session ids.",
      familyIncluded: "Historical lineage includes {count} session instances.",
    },
    filters: {
      title: "Filters",
      to: "to",
      startDate: "Start date",
      endDate: "End date",
      timeZone: "Time zone",
      timeZoneLocal: "Local",
      timeZoneUtc: "UTC",
      pin: "Pin",
      pinned: "Pinned",
      selectAll: "Select All",
      clear: "Clear",
      clearAll: "Clear All",
      remove: "Remove filter",
      removeDays: "Remove days filter",
      removeHours: "Remove hours filter",
      removeSession: "Remove session filter",
      all: "All",
      days: "Days",
      hours: "Hours",
      session: "Session",
      agent: "Agent",
      channel: "Channel",
      provider: "Provider",
      model: "Model",
      tool: "Tool",
      daysCount: "{count} days",
      hoursCount: "{count} hours",
      sessionsCount: "{count} sessions",
    },
    query: {
      placeholder:
        "Filter sessions (e.g. key:agent:main:cron* model:gpt-4o has:errors minTokens:2000)",
      apply: "Filter (client-side)",
      matching: "{shown} of {total} sessions match",
      inRange: "{total} sessions in range",
      tip: "Tip: use filters or click bars to refine days.",
    },
    export: {
      label: "Export",
      changed: "Session context changed while preparing the export. Refresh usage and try again.",
      sessionsCsv: "Sessions CSV",
      dailyCsv: "Daily CSV",
      json: "JSON",
    },
    cacheStatus: {
      warning: "Usage data may be incomplete. Checking for updated totals automatically.",
      paused:
        "Usage data may be incomplete. Automatic checks paused; select Refresh to check again.",
    },
    empty: {
      title: "Start with a date range",
      subtitle:
        "Load usage data to compare costs, inspect sessions, and drill into timelines without leaving the dashboard.",
      hint: "Select a date range and click Refresh to load usage.",
      noData: "No data",
      featureOverview: "Overview cards",
      featureSessions: "Session ranking",
      featureTimeline: "Timeline drilldown",
    },
    daily: {
      title: "Daily Usage",
      total: "Total",
      byType: "By Type",
      tokensTitle: "Daily Token Usage",
      costTitle: "Daily Cost",
      compressedScaleHint: "Square-root scale keeps low-usage days visible.",
    },
    costWindows: {
      title: "Cost Windows",
      subtitle: "Calendar windows ending {date}",
      selectedRange: "Selected Range",
      lastDays: "Last {count} days",
      perDay: "/ day",
    },
    breakdown: {
      output: "Output",
      input: "Input",
      cacheWrite: "Cache Write",
      cacheRead: "Cache Read",
      total: "Total",
      tokensByType: "Tokens by Type",
      costByType: "Cost by Type",
    },
    overview: {
      title: "Usage Overview",
      messages: "Messages",
      messagesHint: "Total user and assistant messages in range.",
      messagesAbbrev: "msgs",
      user: "user",
      assistant: "assistant",
      toolCalls: "Tool Calls",
      toolCallsHint: "Total tool call count across sessions.",
      toolsUsed: "tools used",
      errors: "Errors",
      errorsHint: "Total message and tool errors in range.",
      toolResults: "tool results",
      avgTokens: "Avg Tokens / Msg",
      avgTokensHint: "Average tokens per message in this range.",
      avgCost: "Avg Cost / Msg",
      avgCostHint: "Average cost per message when providers report costs.",
      avgCostHintMissing:
        "Average cost per message when providers report costs. Cost data is missing for some or all sessions in this range.",
      acrossMessages: "Across {count} messages",
      sessions: "Sessions",
      sessionsHint: "Distinct sessions in the range.",
      sessionsInRange: "of {count} in range",
      throughput: "Throughput",
      throughputHint: "Throughput shows tokens per minute over active time. Higher is better.",
      tokensPerMinute: "tok/min",
      perMinute: "/ min",
      errorRate: "Error Rate",
      errorHint: "Error rate = errors / total messages. Lower is better.",
      avgSession: "avg session",
      cacheHitRate: "Cache Hit Rate",
      cacheHint:
        "Cache hit rate = cache read / (input + cache read + cache write). Higher is better.",
      cached: "cached",
      prompt: "prompt",
      calls: "calls",
      costShare: "{percent}% of cost",
      topModels: "Top Models",
      topProviders: "Top Providers",
      topTools: "Top Tools",
      topAgents: "Top Agents",
      topChannels: "Top Channels",
      peakErrorDays: "Peak Error Days",
      peakErrorHours: "Peak Error Hours",
      noModelData: "No model data",
      noProviderData: "No provider data",
      noToolCalls: "No tool calls",
      noAgentData: "No agent data",
      noChannelData: "No channel data",
      noErrorData: "No error data",
    },
    sessions: {
      title: "Sessions",
      shown: "{count} shown",
      total: "{count} total",
      avg: "avg",
      all: "All",
      recent: "Recently viewed",
      recentShort: "Recent",
      sort: "Sort",
      ascending: "Ascending",
      descending: "Descending",
      clearSelection: "Clear Selection",
      noRecent: "No recent sessions",
      noneInRange: "No sessions in range",
      more: "+{count} more",
      selected: "Selected ({count})",
      copy: "Copy",
      limitReached: "Showing first 1,000 sessions. Narrow date range for complete results.",
    },
    details: {
      noUsageData: "No usage data for this session.",
      duration: "Duration",
      modelMix: "Model Mix",
      filtered: "(filtered)",
      close: "Close session details",
      noTimeline: "No timeline data",
      loadFailed: "Could not load {detail}: {error}",
      noDataInRange: "No data in range",
      usageOverTime: "Usage Over Time",
      reset: "Reset",
      perTurn: "Per Turn",
      cumulative: "Cumulative",
      turnRange: "Turns {start}–{end} of {total}",
      assistantOutputTokens: "Assistant output tokens",
      userToolInputTokens: "User + tool input tokens",
      tokensWrittenToCache: "Tokens written to cache",
      tokensReadFromCache: "Tokens read from cache",
      noContextData: "No context data",
      systemPromptBreakdown: "System Prompt Breakdown",
      collapse: "Collapse",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      baseContextPerMessage: "Base context per message",
      system: "System",
      systemShort: "Sys",
      skills: "Skills",
      tools: "Tools",
      files: "Files",
      ofInput: "of input",
      of: "of",
      timelineFiltered: "timeline filtered",
      conversation: "Conversation",
      noMessages: "No messages",
      tool: "Tool",
      toolResult: "Tool result",
      hasTools: "Has tools",
      searchConversation: "Search conversation",
      filterByRole: "Filter by role",
      filterByTool: "Filter by tool",
      you: "You",
      noMessagesMatch: "No messages match the filters.",
    },
    mosaic: {
      title: "Activity by Time",
      subtitleEmpty: "Estimates require session timestamps.",
      subtitle: "Estimated from session spans (first/last activity). Time zone: {zone}.",
      noTimelineData: "No timeline data yet.",
      dayOfWeek: "Day of Week",
      midnight: "Midnight",
      fourAm: "4am",
      eightAm: "8am",
      noon: "Noon",
      fourPm: "4pm",
      eightPm: "8pm",
      legend: "Low → High token density",
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
    },
  },
  login: {
    subtitle: "Gateway Dashboard",
  },
  chat: {
    modelAccounts: {
      label: "Account for this chat",
      automatic: "Automatic (new-chat default)",
      manage: "Manage saved accounts…",
      draftHint: "Applies only to this session. Your new-chat default is unchanged.",
    },
    mentions: {
      menu: "Mention a person",
      loading: "Loading people…",
      empty: "No eligible people found.",
      truncated: "Keep typing to find more people.",
      online: "Online",
      offline: "Offline",
      unavailable: "Could not load people. Try again.",
      limit: "You can mention up to 10 people per message.",
      selected: "Will notify: {names}",
      remove: "Remove mention",
      restoreFailed:
        "Mention selection could not be restored. Edit this message and select its recipients again.",
      unsupported:
        "Human mentions are not available in this mode. Remove the selected mentions or send from a normal chat.",
    },
    cloudWorkerFailed: "Runner failed: {error}",
    errorDetails: "Error details",
    details: "Details",
    copyError: "Copy error",
    diskSpace: {
      warningTitle: "Cloud session disk space is low",
      criticalTitle: "Cloud session disk space is critically low",
      warningBody:
        "{percent}% used · {free} free. Delete unneeded files or stop the cloud worker before large writes.",
      criticalBody:
        "{percent}% used · {free} free. New writes may fail and stop the agent. Delete unneeded files or stop the cloud worker before large writes.",
    },
    sendErrors: {
      outboxPayloadCopied:
        "This queued message was copied from another tab. Check the conversation and retry only if it has not arrived.",
      outboxPayloadCapacity:
        "Browser attachment storage is full. Try a smaller batch or send/discard queued messages to free space. No new message was sent; your input is retained.",
      outboxPayloadUnavailable:
        "Browser attachment storage is unavailable. Allow browser storage and close older dashboard tabs before reconnecting and retrying. No new message was sent.",
      outboxPayloadMissing:
        "Queued attachments are missing or unreadable. This may be a stale copy from another tab. Check the conversation, then discard this row and attach the files again if needed. No new message was sent.",
      activeLeafChanged: "The session switched branches — review and resend.",
    },
    waitingForApproval: "Waiting for approval…",
    startupStatus: {
      retrying: "Retrying… {attempt}/{maxAttempts}",
      preparingWorkspace: "Preparing workspace…",
      namingWorktree: "Naming worktree…",
      creatingWorktree: "Creating worktree…",
      runningSetup: "Running setup…",
      provisioningEnvironment: "Provisioning environment…",
      preparingContext: "Preparing this turn…",
      startingModel: "Waiting for a response…",
    },
    archivedSessionDisabled: "This session is archived. Unarchive it to continue the conversation.",
    subagentViewOnly: "View-only subagent",
    subagentSessionDisabled:
      "This is a subagent of {parent}. Continue the conversation in its parent session.",
    parentSession: "the parent session",
    openParentSession: "Open parent session",
    parentSessionUnavailable: "Parent session information is unavailable.",
    sessionRoute: {
      chooseTitle: "Choose a session",
      multipleMatches: "More than one session matches {shortId}.",
      additionalMatches: "Search results remain. Use a longer id prefix.",
      notFoundTitle: "Session not found",
      notFoundExplanation: "The session may have been removed, or the link may be incorrect.",
      goToMain: "Go to main session",
      viewSessions: "View sessions",
      catalogShareUnavailable: "This shared session route is unavailable.",
      catalogShareInvalid: "This {catalog} share URL is invalid.",
    },
    commandResults: {
      startingNewThread: "Starting new session...",
      resettingThread: "Resetting session...",
      stoppingCurrentRun: "Stopping current run...",
      chatHistoryCleared: "Chat history cleared.",
      exportingThread: "Exporting session...",
      emptyExport: "There are no messages to export yet.",
      unknownCommand: "Unknown command: `{command}`",
      options: "Options: {options}.",
      sessionUnavailable: "Session capability is unavailable",
      help: {
        availableCommands: "Available Commands",
        agentCommand: "agent",
        openMenu: "Type `/` to open the command menu.",
      },
      compaction: {
        failed: "Compaction failed.",
        failedWithReason: "Compaction failed: {reason}",
        tokenSummary: " ({before} -> {after} tokens)",
        succeeded: "Context compacted successfully",
        skipped: "Compaction skipped.",
        skippedWithReason: "Compaction skipped: {reason}",
      },
      model: {
        current: "**Current model:** {model}",
        available: "**Available:** {models}",
        more: " +{count} more",
        getFailed: "Failed to get model info: {error}",
        set: "Model set to {model}.",
        setFailed: "Failed to set model: {error}",
      },
      thinking: {
        current: "Current thinking level: {level}.",
        getFailed: "Failed to get thinking level: {error}",
        reset: "Thinking level reset to default.",
        resetFailed: "Failed to reset thinking level: {error}",
        unrecognized: 'Unrecognized thinking level "{level}". Valid levels: {options}.',
        unsupported:
          'Unsupported thinking level "{level}" for this model. Valid levels: {options}.',
        set: "Thinking level set to {level}.",
        setFailed: "Failed to set thinking level: {error}",
      },
      verbose: {
        current: "Current verbose level: {level}.",
        getFailed: "Failed to get verbose level: {error}",
        unrecognized: 'Unrecognized verbose level "{level}". Valid levels: off, on, full.',
        set: "Verbose mode set to {level}.",
        setFailed: "Failed to set verbose mode: {error}",
      },
      fast: {
        autoValue: "auto ({seconds} sec)",
        on: "on",
        off: "off",
        sourceSession: " (session)",
        sourceAgent: " (default: agent)",
        sourceModel: " (default: model)",
        sourceDefault: " (default)",
        current: "Current fast mode: {value}",
        options: "on, off, auto ({seconds} sec), default, status",
        getFailed: "Failed to get fast mode: {error}",
        reset: "Fast mode reset to default.",
        resetFailed: "Failed to reset fast mode: {error}",
        unrecognized:
          'Unrecognized fast mode "{mode}". Valid levels: on, off, auto, default, status.',
        setAuto: "Fast mode set to auto.",
        enabled: "Fast mode enabled.",
        disabled: "Fast mode disabled.",
        setFailed: "Failed to set fast mode: {error}",
      },
      usage: {
        noActiveThread: "No active session.",
        notAvailable: "n/a",
        title: "Session Usage",
        inputTokens: "Input: {count} tokens",
        outputTokens: "Output: {count} tokens",
        totalTokens: "Total: {count} tokens",
        context: "Context: {percent} of {total}",
        model: "Model: {model}",
        failed: "Failed to get usage: {error}",
      },
      agents: {
        none: "No agents configured.",
        title: "**Agents** ({count})",
        default: "default",
        runtime: " · runtime {runtime}",
        failed: "Failed to list agents: {error}",
      },
      steer: {
        timeout: "The active run ended before the steer message was accepted.",
        failed: "Steer failed before it reached the run; try again.",
        usage: "Usage: `/steer <message>`",
        succeeded: "Steered.",
        requestFailed: "Failed to steer: {error}",
      },
      redirect: {
        timeout: "The active run ended before the redirect message was accepted.",
        failed: "Redirect failed before it reached the run; try again.",
        usage: "Usage: `/redirect <message>`",
        succeeded: "Redirected.",
        requestFailed: "Failed to redirect: {error}",
      },
    },
    sessionSharing: {
      menu: "Session sharing",
      current: "Session visibility: {visibility}",
      visibility: "Visibility",
      shared: "Shared",
      readOnly: "Read-only",
      suggest: "Suggest",
      draft: "Draft",
      publishDraft: "Publish draft",
      owner: "Owner",
      members: "Members",
      selected: "Member",
      noPeople: "No paired people found.",
      readOnlyNotice: "Only the session owner and members can act in this session.",
      publicAccess: "Public access",
      publicIndicator: "Public",
      worldReadable: "Public — anyone can read without signing in.",
      notPublic: "Only signed-in people with access can view this session.",
      enablePublicAccess: "Enable public access…",
      disablePublicAccess: "Disable public access",
      copyPublicLink: "Copy public link",
      publicConfirmTitle: "Make this session world-readable?",
      publicConfirmMessage:
        "Anyone with the public link can read existing and future conversation text without signing in. Tools, reasoning, files, and widgets are excluded. Review the conversation for private information before sharing. Disabling access cannot recall copies already downloaded.",
      publicConfirmEnable: "Make public",
      publicEnabled: "Public access enabled. Copy the public link from Session sharing.",
      publicDisabled: "Public access disabled.",
      publicUnavailable: "Public access requires a saved, non-incognito session.",
    },
    sessionSuggestions: {
      suggest: "Suggest",
      suggestMessage: "Suggest message",
      attachmentsUnsupported: "Remove attachments before submitting a text suggestion.",
      sendNow: "Send {author}'s suggestion now",
      queue: "Queue {author}'s suggestion",
      edit: "Edit {author}'s suggestion",
      dismiss: "Dismiss {author}'s suggestion",
      typing: "{name} is typing…",
      typingMany: "{names} are typing…",
      state: {
        pending: "Pending",
        accepted: "Accepted",
        dismissed: "Dismissed",
      },
    },
    sessionHeader: {
      renameTooltip: "Rename session",
      renameAria: "Rename session {title}",
      renameInputAria: "Session title",
      renameInputPlaceholder: "Session title",
      openParent: "Open parent session {title}",
      panels: "Panels",
      layout: "Layout",
      continueInTerminal: {
        action: "Continue in terminal…",
        title: "Continue in terminal",
        description:
          "Copy this command to continue the current session. It is safe to paste in common terminals and shells.",
        authNote:
          "The command contains no credentials. The terminal authenticates independently, and the session's access controls still apply.",
        disconnected: "Connect to the Gateway to continue this session in a terminal.",
        queryRouted:
          "Query-routed Gateway URLs cannot create credential-free continuation commands because authentication and stored device scope are not query-aware. Use a manually authenticated CLI target or a queryless configured Gateway URL.",
        unavailable: "This session or Gateway address cannot be continued in a terminal.",
      },
      workspaceAria: "Workspace actions for {workspace}",
      revealFinder: "Reveal in Finder",
      revealFileExplorer: "Reveal in File Explorer",
      revealFileManager: "Open in file manager",
      copyPath: "Copy path",
      copyBranch: "Copy branch name",
      copied: "Copied",
      incognito: "Incognito session",
      branches: "Session branches",
      branchSwitchUnavailable: "Branch switch is unavailable while the agent is working.",
      untitledBranch: "Untitled branch",
      oneMessage: "{count} message",
      messages: "{count} messages",
      activeBranch: "Active branch",
      gatewayPicker: {
        menuLabel: "Gateway: {gateway}",
        primaryTag: "primary",
        setPrimary: "Set as primary…",
        openSettings: "Gateway settings…",
        connected: "Connected",
        unreachable: "Unreachable",
        unknown: "Unknown status",
      },
    },
    board: {
      faceLabel: "Session face",
      chatFace: "Chat",
      splitFace: "Split",
      dashboardFace: "Dashboard",
      dockMenu: "Chat dock: {dock}",
      dockLeft: "Dock chat left",
      dockRight: "Dock chat right",
      dockBottom: "Dock chat bottom",
      resizeDock: "Resize chat dock",
      enterFullscreen: "Enter fullscreen",
      exitFullscreen: "Exit fullscreen",
      fullscreenUnavailable: "Fullscreen is unavailable in this browser",
      fullscreenFailed: "Could not change fullscreen mode: {error}",
      workboardCard: "Workboard card: {title}, {status}",
      defaultTab: "Main",
      mockPlaceholder: "Board view seam · {tabs} tabs · {widgets} widgets",
      resetTitle: "Reset conversation?",
      resetDescription: "The conversation context resets. Your dashboard stays.",
    },
    catalog: {
      remoteViewOnly: "This session is on a paired device and is view-only.",
      unsupportedViewOnly: "This external session source is view-only.",
      sessionMenu: "External session actions",
      openInOpenClaw: "Open in OpenClaw",
      openInTerminal: "Open in terminal",
      terminalUnavailable: "Terminal opening is unavailable for this session.",
    },
    taskSuggestions: {
      eyebrow: "Suggested task · in {repo}",
      startSession: "Start in a new session",
      copyPrompt: "Copy prompt",
      copyPromptFailed: "Couldn't copy the prompt to the clipboard",
      promptCopied: "Copied",
      showInstructions: "Show instructions",
      hideInstructions: "Hide instructions",
      previous: "Previous suggested task",
      next: "Next suggested task",
      starting: "Starting…",
      dismiss: "Dismiss {title}",
      adminRequired: "Administrator access is required to start suggested tasks.",
    },
    pullRequests: {
      linkLabel: "Pull request #{number}: {title}",
      createPr: "Create PR",
      createPrLabel: "Create a pull request for {branch}",
      publishPr: "Publish PR",
      publishing: "Publishing…",
      publicationRequested: "Requested",
      retryPublication: "Retry publication",
      openPublishedPr: "Open PR",
      cloudPublicationGuidance:
        "Start a live agent turn and ask it to publish this cloud workspace after reconciliation.",
      dismiss: "Dismiss pull request #{number}",
      open: "Open",
      draft: "Draft",
      merged: "Merged",
      closed: "Closed",
      checks: "CI",
      checksPassing: "CI checks passing",
      checksFailing: "CI checks failing",
      checksPending: "CI checks running",
      ciMonitoring: "CI monitoring",
      openChecks: "Open checks on GitHub",
      checksPassed: "Passed",
      checksFailed: "Failed",
      checksRunning: "Running",
      checksSkipped: "Skipped",
      showMore: "Show {count} more",
      rateLimited:
        "GitHub API rate limit reached. Pull request status may be out of date until the limit resets.",
    },
    usageRemaining: "Usage Remaining",
    view: {
      menu: "View",
      reasoning: "Reasoning",
      toolCalls: "Tool calls",
      commentary: "Keep commentary",
    },
    sendShortcut: "Send shortcut",
    sendShortcutEnter: "Enter",
    sendShortcutModifierEnter: "⌘/Ctrl+Enter",
    followUpMode: "Follow-ups while the agent is working",
    followUpModeQueue: "Queue until the run ends",
    followUpModeServer: "Server default ({mode})",
    followUpModeLoading: "loading…",
    followUpModeSteer: "Steer into the active run",
    followUpModeUsingServer: "Using server default ({mode})",
    followUpModeOverriding: "Overriding server default ({mode})",
    followUpModeReset: "Reset to server default",
    catalogOpenTarget: "Open external sessions in",
    catalogOpenTargetViewer: "OpenClaw viewer",
    catalogOpenTargetTerminal: "Terminal",
    catalogOutputTruncated: "[Output truncated]",
    onboardingDisabled: "Disabled during setup",
    commandPaletteTitle: "Search or jump to… (⌘K)",
    openCommandPalette: "Open command palette",
    updating: "Updating…",
    actions: {
      copyAsMarkdown: "Copy as markdown",
      dismissError: "Dismiss error",
      exitFocusMode: "Exit focus mode",
      scrollToLatest: "Scroll to latest",
    },
    markdown: {
      truncated: "… truncated ({total} chars, showing first {shown}).",
    },
    mermaid: {
      title: "Mermaid diagram",
      options: "Diagram options",
      diagram: "Show diagram",
      source: "Show source",
      copySource: "Copy source",
      expand: "Expand diagram",
      rendering: "Rendering diagram…",
      error: "This diagram could not be rendered. Check the source or simplify the diagram.",
      rendererError:
        "The Mermaid renderer could not load or finish. Reload the dashboard; if this continues, check proxy or authentication rules for its renderer asset URLs.",
      imageError: "The diagram image could not be displayed. Reload the dashboard to try again.",
    },
    codeBlock: {
      languageFallback: "Code",
      hiddenLine: "1 hidden line",
      hiddenLines: "{count} hidden lines",
      showHiddenLine: "Show 1 hidden line",
      showHiddenLines: "Show {count} hidden lines",
      enableWrap: "Enable word wrap",
      disableWrap: "Disable word wrap",
      jsonBadge: "JSON",
      jsonArrayItem: "Array ({count} item)",
      jsonArrayItems: "Array ({count} items)",
      jsonObjectKeys: "Object ({count} keys)",
    },
    workspaceConflict: {
      titleOne: "1 cloud workspace conflict",
      titleMany: "{count} cloud workspace conflicts",
      description:
        "OpenClaw kept your local versions and applied the other cloud changes. Inspect the staged result or take its version for a conflicted path.",
      summary: "Local versions kept; inspect or take the cloud version.",
      showCommands: "Show commands",
      morePaths: "+{count} more paths",
      stagedResult: "Staged cloud result",
      copyStagedResult: "Copy staged result ref",
      inspectCloud: "Inspect the first cloud version",
      takeCloud: "Take the first cloud version",
      copyInspectCommand: "Copy cloud inspect command",
      copyTakeCommand: "Copy take-cloud command",
      commandHelp:
        "Run these in Bash or zsh (Git Bash on Windows). If inspect says the path does not exist, the cloud deleted it; verify and remove the local path manually. If checkout reports a file/directory conflict, move or remove the blocking local path, then retry. If the staged ref is missing, the notice is stale; do not change the local path.",
      commandsUnavailable:
        "This filename contains terminal control characters, so OpenClaw will not build a copyable shell command for it. Inspect the staged ref directly and enter the path manually with care.",
      dismiss: "Dismiss workspace conflict notice",
      eventSender: "Cloud workspace",
      eventTitleOne: "Cloud result applied with 1 conflict",
      eventTitleMany: "Cloud result applied with {count} conflicts",
      eventDescription:
        "Local versions were kept for these paths; other cloud changes were applied.",
    },
    compaction: {
      label: "Compacted history",
      savedTokens: "saved {count} tokens",
      description: "The compacted transcript is preserved as a checkpoint.",
      openCheckpoints: "Open checkpoints",
    },
    sessionReset: {
      label: "Session reset",
      description: "The earlier conversation was cleared.",
    },
    outboxRecoveryTitle: "Saved messages need a destination",
    outboxRecoveryDescription:
      "An older browser version did not preserve every destination. These drafts and queued messages have not been sent by recovery. Open an empty non-Incognito conversation, then restore an entry here for review. Attachment drafts may appear separately.",
    outboxRecoveryConfirm:
      "Confirm this destination for the saved entry. Queued messages will remain paused for review and Retry. If delivery was uncertain, check the conversation before retrying.",
    outboxRecoveryRestore: "Restore here for review",
    outboxRecoveryConflict:
      "This destination has a newer draft or queue, or changed during confirmation. Open an empty conversation and try again. The saved entry is still available.",
    outboxRecoveryStorageFailed:
      "Browser storage could not complete recovery. The original saved data has been retained. Free browser storage and reload to try again.",
    outboxRecoveryFull:
      "Recovery is full. Restore saved entries to make room; remaining legacy data is still retained in this browser.",
    outboxRecoveryMessages: "Queued messages: {count}",
    restartRecoveryTitle: "This session ended during a restart.",
    restartRecoveryDisabled: "Its transcript is safe.",
    resumeInNewSession: "Resume in new session",
    resumingSession: "Resuming…",
    systemNotice: {
      guardian: {
        approvedSummary: "Guardian approved {action}.",
        deniedLabel: "Guardian denied",
        deniedSummary: "{action} · risk: {risk}\n\n{rationale}",
        warningLabel: "Guardian warning",
        warningFallback: "Guardian stopped the requested action.",
        strictReviewRequiredLabel: "Guardian review required",
        strictReviewRequiredSummary: "Guardian is reviewing this action before it can continue.",
        requestedAction: "Requested action",
        unknownRisk: "unknown",
        noRationale: "No rationale was provided.",
      },
      restartRecovery: {
        label: "System · restart recovery",
        summary:
          "Turn interrupted by a gateway restart — asked the agent to resume and finish the response.",
      },
      gatewayRestarted: {
        label: "System · gateway restarted",
      },
      cliHarnessContext: {
        label: "System · injected context",
      },
      showContent: "Show content",
    },
    progressLabels: {
      shelling: "Shelling",
      scuttling: "Scuttling",
      clawing: "Clawing",
      pinching: "Pinching",
      molting: "Molting",
      bubbling: "Bubbling",
      tiding: "Tiding",
      reefing: "Reefing",
      cracking: "Cracking",
      sifting: "Sifting",
      brining: "Brining",
      nautiling: "Nautiling",
      krilling: "Krilling",
      barnacling: "Barnacling",
      lobstering: "Lobstering",
      tidepooling: "Tidepooling",
      pearling: "Pearling",
      snapping: "Snapping",
      surfacing: "Surfacing",
    },
    turnRecap: {
      doneIn: "Done in {duration}",
      tokens: "{count} output tokens",
      tokensOne: "1 output token",
    },
    commands: {
      arguments: "Command arguments",
      menu: "Slash commands",
      optionCount: "{count} options",
      clearDescription: "Clear chat history",
      redirectDescription: "Abort and restart with a new message",
      steerDescription: "Inject a message into the active run",
      categories: {
        session: "Session",
        model: "Model",
        agents: "Agents",
        tools: "Tools",
      },
    },
    skills: {
      menu: "Skill references",
      label: "Skills",
      loading: "Loading skills…",
    },
    splitView: {
      open: "Open split view",
      splitRight: "Split right",
      splitDown: "Split down",
      closePane: "Close pane",
      dropSplit: "Split",
      dropOpenHere: "Open here",
    },
    sidebar: {
      updateMacAndGateway: "Update Mac app + Gateway",
      updateGateway: "Update Gateway",
      serverUpdatedTitle: "Server updated",
      serverUpdatedRefresh: "Refresh for full capabilities",
      threads: "Sessions",
      otherSessions: "Other",
      groups: "Groups",
      coding: "Coding",
      catalogViewOptions: "View options",
      hideFromSidebar: "Hide from sidebar",
      sectionHidden: "{section} hidden.",
      sectionHiddenRecovery: "Show it again in Settings > Appearance > Sidebar.",
      hiddenSessionSections: "Hidden session sections",
      showSessionSection: "Show",
      catalogGroupByProject: "Project",
      catalogGroupByPerson: "Person",
      openSessionMenu: "Open session menu",
      sortBy: "Sort by",
      sortCreated: "Created",
      sortSessions: "Filter & sort",
      sortUpdated: "Last updated",
      sessionMenu: "Actions for {session}",
      sessionMenuMany: "Actions for {count} sessions",
      toolActivity: "Using {tool}",
      catalogDiscoveryHelp:
        "{error}. Configure native session discovery in Settings > Automation > Plugins.",
    },
    welcome: {
      hintBeforeShortcut: "Type a message below ·",
      hintAfterShortcut: "for commands",
      recentSessions: "Recent chats",
      suggestions: {
        whatCanYouDo: "What can you do?",
        summarizeRecentSessions: "Summarize my recent sessions",
        configureChannel: "Help me configure a channel",
        checkSystemHealth: "Check system health",
      },
    },
    runControls: {
      newSession: "New session",
      newSessionWorktree: "New session in worktree",
      exportChat: "Export chat",
      queue: "Queue",
      queueMessage: "Queue message",
      stop: "Stop",
      stopWithShortcut: "Stop (Esc)",
      stopGenerating: "Stop generating",
      send: "Send",
      sendMessage: "Send message",
    },
    queue: {
      connectionPending: "Finishing connection recovery. Try sending again when it is ready.",
      initialTurnPending:
        "The initial message is unresolved. Reconnect if needed, then review it before sending another message.",
      notSent: "Not sent",
      deliveryUnconfirmed: "Delivery unconfirmed",
      checkDelivery: "Check delivery",
      checkDeliveryHelp:
        "Delivery is unconfirmed. Check delivery looks for the original message without resending it or starting a worker. Inspect the conversation, or copy the retained prompt if you choose to start a separate attempt.",
      retry: "Retry",
      retryQueuedMessage: "Retry queued message",
      discard: "Discard",
      discardPendingMessage:
        "Discard this local pending copy. This does not cancel a message already received by the Gateway.",
      steer: "Steer",
      steerQueuedMessage: "Steer queued message",
      removeQueuedMessage: "Remove queued message",
      reorderQueuedMessage: "Reorder queued message with the arrow keys",
      reorderUnavailable: "This message holds its place and cannot be reordered",
      editQueuedMessage: "Edit queued message",
      moreActions: "More queued message actions",
      cancelEdit: "Cancel editing and keep the queued message",
      states: {
        applyingSettings: "Applying chat settings",
        blockedByUnconfirmed:
          "Queue paused. Retry or discard the earlier unconfirmed message in the conversation.",
        runningCommand: "Running command",
        waitingForReconnect: "Waiting for reconnect",
        editing: "Editing",
        needsReview: "Delivery uncertain",
      },
      imageCount: "Image ({count})",
    },
    goals: {
      composerMode: "Goal",
      sessionChanged: "Conversation changed. Cancel and select Goal again.",
      start: "Start goal",
      save: "Save goal",
      startHint: "Enter your objective.",
      editHint: "Save without starting a run.",
      objectivePlaceholder: "What should this goal accomplish?",
      cancel: "Cancel goal entry",
      offline: "Reconnect to manage goals.",
      busy: "Wait for this run to finish. Your draft is unchanged.",
      annotationUnsupported: "Send or remove browser annotations first. Your draft is unchanged.",
      actionPending: "Wait for the pending goal action.",
      admissionImmutable: "Retry or remove this request. Editing requires a new goal.",
      edit: "Edit goal",
      editChip: "Edit",
      pause: "Pause goal",
      pauseChip: "Pause",
      resume: "Resume goal",
      resumeChip: "Resume",
      clear: "Clear goal",
      clearChip: "Clear",
      showDetails: "Show goal details",
      hideDetails: "Hide goal details",
    },
    questions: {
      other: "Type your own answer here",
      answer: "Answer",
      answerPlaceholder: "{label}",
      submit: "Submit",
      next: "Next",
      back: "Back",
      skip: "Skip",
      skipping: "Skipping…",
      skipped: "Skipped",
      collapse: "Collapse question",
      expand: "Expand question",
      eyebrow: "Question",
      summaryLabel: "Question outcome",
      ownAnswerFor: "Your own answer for {header}",
      storeRequestedBy: "Requested by {agent} · {session}",
      storeEntry: "Stores {name} as {kind}",
      storeReplacement: "Replaces {name} — last updated {updated}",
      storeReplacementBy: "Replaces {name} — last updated {updated} by {updatedBy}",
      submitting: "Submitting…",
      submitFailed: "Could not submit: {error}",
      answered: "Answered",
      answeredElsewhere: "Answered elsewhere",
      unavailable: "Unavailable",
      expired: "Expired",
      disconnected: "Not connected. Try again after reconnecting.",
    },
    imageLightbox: {
      label: "Image preview: {title}",
      open: "Open image {title}",
      openOriginal: "Open in new tab",
      copy: "Copy image",
      download: "Download image",
      copyFailed: "Could not copy this image. Check clipboard access and try again.",
      downloadFailed: "Could not download this image. Try again.",
      loadFailed: "Could not load this image. Try again.",
      zoomIn: "Zoom in",
      zoomOut: "Zoom out",
      resetZoom: "Reset zoom",
      close: "Close image preview",
      untitled: "Image",
    },
    externalImage: {
      notLoaded: "External image not loaded",
      open: "Open image",
    },
    messages: {
      copySelection: "Copy",
      forkFromHere: "Fork from here",
      reply: "Reply",
      replyToMessage: "Reply to message",
      replyingTo: "Replying to {name}",
      originalUnavailable: "The original message is unavailable.",
      message: "message",
      currentMessage: "current message",
      actions: "Message actions",
      selectionActions: "Selection actions",
      askInSideChat: "Ask in side chat",
      rewind: "Rewind",
      rewindConfirm: "Rewind to before this message?",
      dontAskAgain: "Don't ask again",
      rewindToHere: "Rewind to here",
      rewindUnavailable: "Rewind is unavailable while the agent is working",
      forkUnavailable: "Fork is unavailable while the agent is working",
      showLess: "Show less",
      showMore: "Show more",
      tooLargeToDisplay: "This message is too large to display here.",
      unknownDate: "Unknown date",
      toolSender: "Tool",
      forwardedFrom: "From",
      forwardedFromAgent: "Forwarded from {agentId}",
      forwardedMessage: "Forwarded message",
      fullContentLoadExhausted: "Could not load the full message.",
      voiceNote: "Voice note",
      duplicatesCollapsed: "{count} consecutive identical messages collapsed",
      contextFor: "Message context for {timestamp}",
    },
    mediaPlayer: {
      play: "Play",
      pause: "Pause",
      seek: "Seek media",
      mute: "Mute",
      unmute: "Unmute",
      volume: "Volume",
      download: "Download {filename}",
      preparing: "Preparing playback…",
      openVideo: "Expand {filename} in the media overlay",
      videoPreview: "Video preview: {title}",
      closeVideoPreview: "Close video preview",
    },
    modelControls: {
      default: "Default",
      effort: "Effort",
      faster: "Faster",
      smarter: "Smarter",
      fastMode: "Fast mode",
      searchModels: "Search models",
      noMatchingModels: "No models match your search",
      configureModels: "Configure models",
      selectionScopeSessionLabel: "This session",
      selectionScopeAgentLabel: "Agent default",
      selectionScopeGlobalLabel: "Global default",
      selectionScopeSession: "Selecting a model changes only this session.",
      selectionScopeAgent: "Selecting a model updates this agent's default.",
      selectionScopeGlobal: "Selecting a model updates the global default.",
      resetSessionModel: "Reset session model",
      useDefaultModel: "Use default ({model}) for this session",
      defaultWithModel: "Default ({model})",
      defaultWithLevel: "Default ({level})",
      fastHelp: "Faster responses, higher usage of limits.",
      contextWindow: "Context window",
      contextWindowAria: "Context window: {state}",
      speedUnsupported: "Speed control is not supported for this model.",
      contextActiveAndMax: "{active} active · {maximum} max",
      chatOnly: "Chat only",
      chatOnlyHelp:
        "This model can chat, but it cannot use tools. Choose another model for files, commands, web, or media tasks.",
      loadingModels: "Loading models…",
      modelsUnavailable: "Models unavailable",
      noModelsAvailable: "No models available",
      emptyModelsAction: "Manage models",
      providerModels: "{provider} models",
      useDefaultReasoning: "Use default reasoning ({level})",
      fastResponsesAria: "Fast responses: {state}",
    },
    permissionControls: {
      label: "Permissions",
      help: "Choose permissions for this session.",
      default: "Default",
      defaultDescription: "Follow the agent's configured policy.",
      defaultWithMode: "Default ({mode})",
      fullRequiresAdmin: "Full access requires operator.admin access.",
      updateFailed: "Failed to update permissions: {error}",
      refreshFailed: "Permissions were saved, but refreshing the session failed: {error}",
      modes: {
        "read-only": {
          label: "Read Only",
          description: "Read within the session root; writes and commands are blocked.",
        },
        guarded: {
          label: "Guarded",
          description: "A human reviews requests beyond the session root.",
        },
        workspace: {
          label: "Workspace",
          description: "An AI reviewer checks requests beyond the session root.",
        },
        full: {
          label: "Full Access",
          description: "No reviewer; files and commands are unrestricted.",
        },
      },
    },
    rail: {
      title: "Side chat",
      subtitle: "Ask about this session or its project",
      expand: "Expand side chat",
      collapse: "Collapse side chat",
      close: "Close side chat",
      show: "Show side chat",
      clear: "Clear side chat",
      pullRequests: "Pull requests",
      checksPassing: "{count} passed",
      checksFailing: "{count} failed",
      checksPending: "{count} running",
      finished: "Finished {time}",
      empty: "Answers come from this session's transcript and its project files.",
      starters: {
        changed: "What changed?",
        stopped: "Why did it stop?",
        remaining: "What's left?",
      },
      askLabel: "Ask in side chat",
      askPlaceholder: "Ask a question",
      askSubmit: "Ask",
      askPending: "Answering from this session…",
      askBusy: "Side chat is already answering a question.",
      askHistoryUnavailable: "Couldn't load this session's history.",
      askMissing: "This session is no longer available.",
      askModelUnavailable: "No utility model is configured for this session.",
      askRateLimited: "Side chat reached its question limit. Try again shortly.",
      askUnavailable: "Side chat cannot answer right now.",
      askRetry: "Retry",
      asOf: "as of {time}",
      health: {
        "on-track": "On track",
        grinding: "Working",
        stuck: "Stuck",
        "waiting-on-user": "Waiting on you",
        "wrapping-up": "Wrapping up",
        done: "Done",
        failed: "Failed",
      },
    },
    detailPanel: {
      close: "Close sidebar",
      copyPath: "Copy path",
      discard: "Discard",
      editFile: "Edit file",
      searchInFile: "Search in file",
      showInFiles: "Show in Files",
      previousMatch: "Previous match",
      nextMatch: "Next match",
      overwrite: "Overwrite",
      viewRawText: "View Raw Text",
      renderedMarkdown: "Rendered Markdown",
      renderedMarkdownHint: "Sanitized rich-text preview for quick reading.",
      noPreviewableMarkdown: "No previewable markdown content.",
      noContent: "No content available",
      fullContentOversized:
        "Full content is unavailable because the stored transcript entry is too large to return safely.",
      fullContentNotVisible:
        "Full content is unavailable because this transcript entry does not have a visible WebChat projection.",
      fullContentUnavailable: "Full content is no longer available for this transcript entry.",
      copyContents: "Copy file contents",
      fileChanged: "File changed on disk since it was loaded.",
      renderPreview: "Render preview",
      imagePreview: "Image preview",
      file: "File",
      markdownPreview: "Markdown preview",
      toolDetails: "Tool details",
      reloadFailed: "Failed to reload the latest file.",
      overwriteLoadFailed: "Failed to load the latest file before overwriting.",
      fullContentLoadFailed: "Failed to load full content: {error}",
    },
    sidebarColumns: {
      close: "Close {panel}",
    },
    sidePanel: {
      label: "Side panel",
      addTab: "Add side panel tab",
      expand: "Focus",
      restore: "Restore split",
      swap: "Swap {main} and {side}",
      layout: "Layout",
      dockLeft: "Move side panel left",
      dockRight: "Move side panel right",
      dockBottom: "Move side panel below",
      conversation: "Chat",
      conversationEmpty: "The conversation for this task.",
      minimize: "Minimize side panel",
      resize: "Resize side panel",
      review: "Review",
      reviewEmpty: "Open a change, file, image, or tool result to review it here.",
      terminal: "Terminal",
      terminalEmpty: "Open a shell for this session's workspace.",
      browser: "Browser",
      browserEmpty: "A shared browser for you and the agent.",
      files: "Files",
      filesEmpty: "Browse files, artifacts, and changes from this session.",
      companion: "Side chat",
      companionEmpty: "Ask a focused question about this session.",
      tasks: "Tasks",
      tasksEmpty: "Follow active and recently completed background tasks.",
      desktop: "Desktop",
      desktopEmpty: "Connect to an available remote desktop.",
      discussion: "Discussion",
      discussionEmpty: "Open the shared discussion for this session.",
      dashboard: "Dashboard",
      dashboardEmpty: "This task does not have a dashboard yet.",
    },
    thread: {
      search: "Search messages",
      searchPlaceholder: "Search messages...",
      closeSearch: "Close search",
      loading: "Loading chat",
      showEarlier: "Show earlier",
      loadingEarlier: "Loading earlier history…",
      noMatches: "No matching messages",
      positionRail: "Conversation position",
      positionMarker: "{label}, marker {position} of {count}",
      positionUserMessage: "User message",
      positionAssistantMessage: "Assistant message",
      positionMarkerHint:
        "Use arrow keys to choose a marker, Enter or Space to jump, and Escape to dismiss the preview.",
    },
    pendingInputs: {
      resuming:
        "Interrupted by a Gateway restart. This saved message will resume when the session is ready.",
      cancelled:
        "Cancelled before the agent started it. It will not run automatically; copy it and send again.",
      interrupted:
        "Interrupted before the agent started it. It will not run automatically; copy it and send again.",
      earlier: "Show earlier messages",
      latest: "Show latest messages",
    },
    pairingQrExpired: {
      title: "Pairing QR expired",
      reason: "Run /pair qr again to generate a fresh setup code.",
      badge: "Expired",
    },
    composer: {
      composerInput: "Chat composer",
      placeholder: "Message {name}",
      emptyHint: "Write a message to send.",
      placeholderWithAttachments: "Add a message or paste more images...",
      offlineHint: "Offline — messages will be queued and sent when the connection returns.",
      offlineQueuedHint: "Offline — {count} queued; messages send when the connection returns.",
      preparingModel: "Preparing model...",
      responding: "{name} is responding...",
      sendingMessage: "Sending message...",
      working: "{name} is working...",
      runDone: "Done",
      runInterrupted: "Interrupted",
      runStatus: "Run status: {status}",
      compactingContext: "Compacting context...",
      contextCompacted: "Context compacted",
      fallbackActive: "Fallback active: {model}",
      fallbackCleared: "Fallback cleared: {model}",
      fallbackSelected: "Selected: {model}",
      fallbackCurrent: "Active: {model}",
      fallbackPrevious: "Previous fallback: {model}",
      fallbackReason: "Reason: {reason}",
      fallbackAttempts: "Attempts: {attempts}",
      cancelReply: "Cancel reply",
      attachmentPreview: "Attachment preview",
      browserAnnotation: "Browser annotation",
      browserAnnotationPreview: "Browser annotation preview",
      browserAnnotationRegion: "{count} marked region",
      browserAnnotationRegions: "{count} marked regions",
      browserAnnotationInspectedElement: "Element inspected",
      browserAnnotationRemoved: "Browser annotation removed.",
      browserAnnotationUndoUnavailable:
        "Undo is unavailable because the browser annotation limit has been reached.",
      removeAttachment: "Remove attachment",
      removeBrowserAnnotation: "Remove browser annotation: {name}",
      addAttachment: "Add attachment",
      attachPhoto: "Photo",
      attachFileOption: "File",
      menu: {
        back: "Back",
        skills: "Skills",
        connectors: "Connectors",
        webSearch: "Web search",
        managePlugins: "Manage plugins",
        manageSkills: "Manage skills",
        browseConnectors: "Browse connectors",
        addMcpServer: "Add MCP server…",
        addMcpServerTitle: "Add MCP server",
        addMcpServerDescription: "Configure the server and choose where it is enabled.",
        scopeLabel: "Availability",
        scopeSession: "This session",
        scopeEverywhere: "Everywhere",
        scopeSessionHint:
          "The server is saved globally disabled and enabled only for this session.",
        scopeEverywhereHint: "The server is saved and enabled for every session.",
        toolAccess: {
          label: "Tool access",
          loading: "Loading tools…",
          loadFailed: "Couldn’t load tools.",
          noTools: "No tools available for this connector.",
          summary: "{enabled} of {total} tools on",
          summaryOne: "{enabled} of {total} tool on",
        },
        enabledCount: "{count} on",
        loadingSkills: "Loading skills…",
        skillsLoadFailed: "Couldn’t load skills.",
        noSkills: "No skills available.",
        noConnectors: "No MCP servers configured.",
        depsMissing: "deps missing",
        skillBlocked: "not available for this agent",
        sessionTag: "session",
        offlineBlocked: "Connect to the gateway to change session capabilities.",
        adminBlocked: "Admin access is required to manage connectors.",
        savingBlocked: "Wait for the current session capability change to finish.",
      },
      overrides: {
        count: "{count} overrides",
        countOne: "{count} override",
        clear: "Clear session overrides",
      },
      contextUsage: {
        title: "Context usage details",
        open: "Open context usage details",
        summary: "Session context usage: {used} of {limit} ({pct}%)",
        contextWindow: "Context window",
        latestRunTokens: "Latest run tokens",
        estimatedCost: "Est. cost",
        planUsage: "Plan usage",
        openUsage: "Open usage dashboard",
        resets: "Resets {time}",
        usageCredits: "Usage credits",
        budgetValue: "{used} of {limit}",
        limitFiveHour: "5-hour limit",
        limitWeekly: "Weekly",
        limitDaily: "Daily limit",
        limitHours: "{hours}-hour limit",
      },
      takePhoto: "Take photo",
      cameraAccessFailed: "Unable to access the camera.",
      cameraBusy: "The camera is busy or unavailable to the browser.",
      cameraFallback: "Camera {number}",
      cameraInput: "Camera",
      cameraListUnsupported: "This browser cannot list cameras.",
      cameraNoneFound: "No camera was found.",
      cameraPageInactive: "Cameras are unavailable while this page is inactive.",
      cameraPermissionBlocked:
        "Camera access is blocked. Allow camera access in browser site settings.",
      cameraPreview: "Camera preview",
      dismissVoiceInputError: "Dismiss voice input error",
      microphoneAccessFailed: "Unable to access microphone inputs.",
      microphoneAccessPending:
        "Waiting for microphone access. Bring this tab to the foreground and allow access if prompted.",
      microphoneBusy: "Microphone inputs are busy or unavailable to the browser.",
      microphoneStopped: "Microphone input stopped. Choose an available input and start again.",
      microphoneFallback: "Microphone {number}",
      microphoneInput: "Microphone input",
      microphoneAppliesNextSession: "Changes apply when you start your next Talk session.",
      microphoneListUnsupported: "This browser cannot list microphone inputs.",
      noCameras: "No additional cameras found",
      noMicrophones: "No additional microphones found",
      microphoneNoneFound: "No microphone found. Plug one in and it appears here.",
      microphonePageInactive: "Microphone inputs are unavailable while this page is inactive.",
      microphonePermissionBlocked:
        "Microphone access is blocked. Allow it in browser site settings to list inputs.",
      realtimeTalkCapability: "Tap to talk",
      dictationCapability: "Dictation",
      realtimeTalkProviderUnavailable: "No realtime voice provider is configured.",
      dictationProviderUnavailableShort: "No transcription provider is configured.",
      talkCapabilityChecking: "Checking provider availability…",
      talkCapabilityUnknown: "Provider availability could not be verified.",
      openRealtimeTalkSettings: "Talk settings",
      openDictationSettings: "Transcription setup",
      configureCapability: "Configure",
      holdToRecordSetting: "Hold microphone button to start dictation",
      holdToDictate: "Hold to start dictation",
      holdToRecordSettingDescription:
        "Hold until dictation starts, then release and keep speaking. Tap Stop to insert the transcript.",
      dictationAudioUnsupported: "The Gateway returned an unsupported dictation audio format.",
      dictationBrowserAudioUnsupported: "This browser cannot capture dictation audio at 8 kHz.",
      dictationDisconnected: "Dictation stopped because the Gateway disconnected.",
      dictationFailed: "Dictation failed.",
      dictationInterruptedRecovery:
        "Captured text was inserted. Check your connection and try again.",
      dictationStartRecovery: "Check microphone access and transcription settings, then try again.",
      dictationFinalizing: "Finishing dictation…",
      dictationListening: "Listening…",
      dictationStopAndKeep: "Stop and keep text",
      dictationProviderUnavailable:
        "No transcription provider is configured for dictation. Choose one in Settings to dictate.",
      realtimeTalkCancellationRejected: "Realtime output cancellation was not accepted.",
      realtimeTalkMissingTurnIdentity: "Realtime output is missing its turn identity.",
      realtimeTalkRequiresMicrophone: "Realtime voice input requires browser microphone access.",
      selectedMicrophoneUnavailable:
        "The selected microphone is unavailable. Choose another input or System default.",
      selectedCameraUnavailable:
        "The selected camera is unavailable. Choose another camera or System default.",
      startVoiceInput: "Start voice input",
      voiceGestureHint: "Tap to talk · Hold to start dictation",
      stillListening: "Still listening",
      stopVoiceInput: "Stop voice input",
      systemDefaultMicrophone: "System default",
      useSystemDefaultMicrophoneForCall: "Use System default for this call",
      systemDefaultCamera: "System default",
      switchCamera: "Switch camera",
      turnCameraOff: "Turn camera off",
      turnCameraOn: "Turn camera on",
      voiceTranscript: "Voice transcript",
    },
    attachments: {
      allowImage: "Allow image",
      attachedFile: "Attached file",
      audio: "Audio",
      expand: "Open {filename} in the side panel",
      open: "Open",
      previewUnavailable: "Preview unavailable",
      readFailed: "Could not attach: {names}{more}",
      tooLarge: "Too large to send: {names}{more}",
      showInTextField: "Show in text field",
      outsideAllowedFolders: "Outside allowed folders",
      unavailable: "Unavailable",
      failureDeliveryFailed: "Delivery failed. Try sending this file again.",
      failureFileNotFound: "File not found. Check the path and try again.",
      failureUnsupportedFormat:
        "Rejected by the local attachment allowlist. Send a supported file type.",
      history: "History",
      image: "Image",
      notSent: "Not sent",
      omittedFromHistory: "Omitted from history",
      omittedFromHistoryWithSize: "Omitted from history · {size}",
      video: "Video",
    },
    voice: {
      asking: "Asking OpenClaw...",
      preparing: "Preparing voice session...",
      connecting: "Connecting voice input...",
      listening: "Listening...",
    },
    selectors: {
      loadMoreSessions: "Show more",
      loadMoreRosterSessions: "Load more sessions",
      model: "Chat model",
      modelSection: "Model",
      modelLocked: "Locked",
      modelLockedLabel: "Model selection controlled for this session",
      lockedSessionModel: "Session model",
      thinkingLevel: "Chat thinking level",
    },
    toolCards: {
      tool: "Tool",
      canvas: "Canvas",
      summary: "Summary",
      toolInput: "Tool input",
      toolOutput: "Tool output",
      toolError: "Tool error",
      rawDetails: "Raw details",
      showRawDetails: "Show raw details",
      hideRawDetails: "Hide raw details",
      viewMode: "Tool detail view",
      diff: "Diff",
      raw: "Raw",
      openFile: "Open file",
      openDetails: "Open tool details in side panel",
      widgetActions: "Widget actions",
      copyToClipboard: "Copy to clipboard",
      downloadFile: "Download file",
      copyAsImage: "Copy as image",
      downloadAsImage: "Download as image",
      widgetExportRerender: "This widget needs to be re-rendered to export as an image.",
      widgetExportHtmlFallback: "Image unavailable. Downloaded the widget as HTML instead.",
      widgetExportFailed: "Widget export failed. Try again.",
      pinToDashboard: "Pin to dashboard",
      pinToDashboardPending: "Pinning…",
      pinToDashboardFailed: "Could not pin to dashboard. Try again.",
      pinnedToDashboard: "Pinned",
      fileChanges: "File changes",
      attemptedChanges: "Attempted changes",
      failed: "failed",
      running: "Running",
      completed: "Completed",
      exitCode: "Exit code {code}",
      noOutputFailed: "No output — tool failed.",
      noOutputSucceeded: "No output — tool completed successfully.",
      noResult: "No result available.",
      review: {
        reviewing: "{reviewer} reviewing",
        approved: "{reviewer} approved",
        denied: "{reviewer} denied",
        timedOut: "{reviewer} timed out",
        aborted: "{reviewer} stopped",
        risk: "{level} risk",
        authorization: "{level} authorization",
        noRationale: "No rationale was provided.",
      },
      verbs: {
        read: "Read",
        edit: "Edit",
        editing: "Editing",
        edited: "Edited",
        create: "Create",
        creating: "Creating",
        created: "Created",
        delete: "Delete",
        deleting: "Deleting",
        deleted: "Deleted",
        change: "Change",
        changing: "Changing",
        changed: "Changed",
        write: "Write",
        writing: "Writing",
        wrote: "Wrote",
        searched: "Searched",
        fetched: "Fetched",
      },
      group: {
        commandsOne: "ran a command",
        commandsMany: "ran {count} commands",
        readsOne: "read a file",
        readsMany: "read {count} files",
        editsOne: "edited a file",
        editsMany: "edited {count} files",
        writesOne: "created a file",
        writesMany: "created {count} files",
        deletesOne: "deleted a file",
        deletesMany: "deleted {count} files",
        searchesOne: "ran a search",
        searchesMany: "ran {count} searches",
        fetchesOne: "fetched a page",
        fetchesMany: "fetched {count} pages",
        namedTool: "used {names}",
        namedToolRepeated: "used {names} ×{count}",
        otherOne: "used a tool",
        otherMany: "used {count} tools",
        emptyOne: "Ran a tool call",
        emptyMany: "Ran {count} tool calls",
      },
    },
    workRun: {
      workedFor: "Worked for {duration}",
      worked: "Worked",
    },
    backgroundTasks: {
      label: "Background tasks",
      title: "Background tasks",
      show: "Show background tasks",
      collapse: "Collapse background tasks",
      refresh: "Refresh background tasks",
      loading: "Loading background tasks…",
      running: "Running ({count})",
      finished: "Finished ({count})",
      statusRunningOne: "1 running task",
      statusRunningMany: "{count} running tasks",
      statusPreviewMore: "+{count} more",
      stopTask: "Stop {title}",
      toolUseOne: "1 tool use",
      toolUseMany: "{count} tool uses",
      detailLoading: "Loading task details…",
      detailFailed: "Could not load task details.",
      detailRetry: "Try again",
      transcriptLoading: "Loading task transcript…",
      transcriptEmpty: "No transcript messages yet.",
      transcriptFailed: "Could not load task transcript.",
      taskDetailTitle: "Task details",
      taskUnavailable: "This task is no longer available.",
      prompt: "Prompt",
      output: "Output",
      promptUnavailable: "Prompt unavailable.",
      outputPending: "No output yet.",
      subagentActivity: {
        label: "Subagent activity",
        running: "Subagent",
        finished: "Subagent finished",
        failed: "Subagent failed",
        cancelled: "Subagent cancelled",
        openDetails: "Open subagent details for {title}",
        moreWorking: "+{count} more working",
      },
    },
    sessionDiff: {
      title: "Changes",
      show: "Show session changes",
      refresh: "Refresh changes",
      loading: "Loading changes…",
      empty: "No changes in this session's checkout.",
      notGit: "This session's workspace is not a git checkout.",
      unknownSession: "No workspace is associated with this session.",
      unknownCommit: "This commit is no longer available in the session checkout.",
      workspaceStopped:
        "Saved changed files are shown. Start the cloud session to load full diffs and line counts.",
      workspaceStoppedFile: "Start the cloud session to load this diff.",
      disconnected: "Gateway is disconnected.",
      allChanges: "All Changes",
      uncommitted: "Uncommitted",
      commitsAhead: "{count} commits ahead of {base}",
      head: "HEAD",
      mergeBase: "Merge Base",
      scopeMenu: "Choose change scope",
      sync: "Sync",
      syncLocally: "Sync Locally",
      syncDescription:
        "Run this command in a local checkout to mirror this session's committed changes.",
      copyCommand: "Copy sync command",
      checkoutPath: "Checkout path",
      branchName: "Branch name",
      uncommittedStay: "Uncommitted changes stay in the session checkout.",
      viewOptions: "Change view options",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      enableWrapping: "Enable Wrapping",
      disableWrapping: "Disable Wrapping",
      switchSplit: "Switch to Split Diff",
      switchUnified: "Switch to Unified Diff",
      fileActions: "Actions for {path}",
      copyPath: "Copy Path",
      openFile: "Open File",
      openInEditor: "Open in Editor",
      revealInFileTree: "Reveal in File Tree",
      unmodifiedLines: "{count} unmodified lines",
      expandPreviousLines: "Show previous {count} unmodified lines",
      expandNextLines: "Show next {count} unmodified lines",
      expandAllLines: "Show all {count} unmodified lines",
      binaryFile: "Binary file",
      untracked: "untracked",
      tooLarge: "Diff too large to display.",
      truncatedFile: "Diff truncated.",
      truncatedResult: "Some changes were omitted because the diff is very large.",
      statusAdded: "Added",
      statusDeleted: "Deleted",
      statusRenamed: "Renamed",
      statusModified: "Modified",
    },
    sessionDiscussion: {
      show: "Show discussion",
      hide: "Hide discussion",
      disconnected: "Gateway is disconnected.",
      loading: "Loading discussion…",
      opening: "Opening discussion…",
      requiresWriteAccess: "Operator write access is required to open this discussion.",
      openExternal: "Open discussion in a new tab",
      frameTitle: "Session discussion",
      unavailable: "This discussion cannot be embedded.",
    },
    workspaceFiles: {
      label: "Session workspace",
      collapse: "Collapse session workspace",
      showFiles: "Show session files",
      dockRight: "Dock to the right",
      dockBottom: "Dock to the bottom",
      workspace: "Session",
      files: "Workspace",
      refresh: "Refresh session workspace",
      loading: "Loading session workspace…",
      changed: "Changed",
      read: "Read",
      artifacts: "Artifacts",
      browser: "Project files",
      root: "Root",
      search: "Search files",
      searchResults: "Search results",
      parentFolder: "Parent folder",
      noBrowserFiles: "No files in this folder.",
      noSearchResults: "No matching files.",
      truncated: "Showing the first matching files. Refine the search to narrow results.",
      session: "Session",
      missing: "Missing",
      summary: "Session workspace summary",
      changedCount: "{count} changed",
      readCount: "{count} read",
      artifactCount: "{count} artifacts",
      browserCount: "{count} shown",
      actions: "Workspace file actions",
      preview: "Preview",
      copyPath: "Copy path",
    },
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
    es: "Español (Spanish)",
    jaJP: "日本語 (Japanese)",
    ko: "한국어 (Korean)",
    fr: "Français (French)",
    hi: "हिन्दी (Hindi)",
    ar: "العربية (Arabic)",
    it: "Italiano (Italian)",
    tr: "Türkçe (Turkish)",
    uk: "Українська (Ukrainian)",
    id: "Bahasa Indonesia (Indonesian)",
    pl: "Polski (Polish)",
    th: "ไทย (Thai)",
    vi: "Tiếng Việt (Vietnamese)",
    nl: "Nederlands (Dutch)",
    fa: "فارسی (Persian)",
    ru: "Русский (Russian)",
  },
  secretsStore: {
    name: "Name",
    access: "Access",
    accessMode: "Access mode",
    value: "Value",
    allowedHosts: "Allowed hosts",
    allowedHostsPlaceholder: "api.example.com",
    allowedHostsHint:
      "Exact hostnames only, one per line or comma-separated. No wildcards or ports.",
    noAllowedHosts: "None",
    updated: "Last updated",
    by: "{time} by {name}",
    actions: "Actions",
    add: "Add",
    edit: "Edit",
    bulk: "Bulk Add",
    protectedSecret: "Protected secret",
    protectedSecretHint:
      "Hidden after save and inert unless referenced by a SecretRef or used through enabled destination-bound Gateway egress. It is never directly readable.",
    agentReadable: "Agent-readable environment",
    agentReadableHint:
      "High risk: visible to admins and plaintext to Gateway-hosted agent commands. The agent can print, transmit, or persist it. Applies from the next run.",
    hint: "Choose protected, write-only secrets or intentionally agent-readable Gateway environment values.",
    required: "Enter a value.",
    detect: "Protect credential-like names automatically",
    detected: "{count} protected secrets detected",
    detectedOne: "{count} protected secret detected",
    unavail: "Gateway/admin required.",
    badName: "Use SERVICE_API_KEY.",
    tooLarge: "Max 64 KiB.",
    savedProtected:
      "Saved {name} as Protected secret. Add a SecretRef or enable destination-bound Gateway egress to use it.",
    savedReadable:
      "Saved {name} as Agent-readable environment. It is available to Gateway-hosted agent commands from the next run.",
    savedMany:
      "Saved {count} entries ({protected} protected, {readable} agent-readable). Protected secrets need a SecretRef or enabled destination-bound Gateway egress; agent-readable environment values reach Gateway-hosted agent commands from the next run.",
    warnings: "{count} runtime warnings.",
    partial: "{saved}/{total}: {error}",
    confirmDelete: "Delete {name}?",
    deleted: "Deleted {name}.",
    deleteFailed: "The secret was not deleted. Reload the list and try again.",
  },
  cron: {
    adminRequired: "Browsing only. Automation changes require operator.admin access.",
    tabs: {
      filterLabel: "Automation status",
      all: "All",
      active: "Active",
      paused: "Paused",
    },
    list: {
      viewLabel: "Automation views",
      searchPlaceholder: "Search automations",
      newTask: "New automation",
      filters: "Filters",
      shownOf: "{shown} of {total}",
      emptyTitle: "No automations yet",
      emptyHint: "Describe what OpenClaw should do and when — it runs on schedule.",
      noMatching: "No automations match the current filters.",
      loadMore: "Load more",
      loading: "Loading...",
      schedulerOff: "Scheduler disabled",
      refresh: "Refresh",
      refreshing: "Refreshing...",
      paused: "Paused",
      autoDisabledRunFailures: "Auto-disabled · {count} run failures",
      autoDisabledScheduleErrors: "Auto-disabled · {count} schedule errors",
      tasksTab: "Automations",
      activityTab: "Run history",
    },
    suggestions: {
      title: "Starter automations",
      schedules: {
        weekdayMornings: "Weekdays at 9:00 AM",
        everyMorning: "Daily at 8:00 AM",
        weekly: "Mondays at 9:00 AM",
        hourly: "Every hour",
      },
      ideas: {
        repoPulse: {
          name: "Repo pulse",
          tagline: "Overnight issues, PRs, and CI failures, ranked by urgency.",
          prompt:
            "Review overnight activity in my repositories: new issues, pull requests, and CI failures. Summarize the three things that most need my attention today, each with a link and a one-line reason.",
        },
        standupGhostwriter: {
          name: "Standup ghostwriter",
          tagline: "Your standup update, drafted from yesterday's work.",
          prompt:
            "Draft my standup update from yesterday's commits, merged pull requests, and open review threads. Three bullets max: done, doing, blocked.",
        },
        hackerNewsScout: {
          name: "Hacker News scout",
          tagline: "Three links worth your coffee, with hot takes.",
          prompt:
            "Scan today's Hacker News front page for posts about AI agents, developer tooling, and TypeScript. Send me the three most interesting links, each with a one-line hot take.",
        },
        dependencyRadar: {
          name: "Dependency radar",
          tagline: "Outdated or vulnerable dependencies, with upgrade notes.",
          prompt:
            "Check my main project for outdated or vulnerable dependencies. List the notable updates with a one-line risk note each, and draft the upgrade command.",
        },
        watchdog: {
          name: "Night watch",
          tagline: "Hourly health check with a one-line verdict.",
          prompt:
            "Check that my services and gateway are healthy: scan recent logs for new errors, restarts, or unusual load. Reply with a single short all-clear line when everything is fine; if something looks broken, report what failed and where to start looking.",
        },
        polyglotMinute: {
          name: "Polyglot minute",
          tagline: "One useful foreign phrase with your morning coffee.",
          prompt:
            "Teach me one useful phrase in Japanese: the phrase, how to pronounce it, its literal meaning, and when to use it. Keep it under five lines.",
        },
      },
    },
    stats: {
      tasks: "Automations",
      failing: "Failing",
      nextWake: "Next wake",
    },
    detail: {
      tabsLabel: "Automation details",
      newTitle: "New automation",
      newSubtitle: "Describe what OpenClaw should do, then pick when it runs.",
      back: "All automations",
      settingsTab: "Settings",
      historyTitle: "Run history",
      generalSection: "General",
      scheduleSection: "Schedule",
      deliverySection: "Delivery",
      active: "Active",
      paused: "Paused",
    },
    actions: {
      runNow: "Run now",
      runNowJob: "Run now: {name}",
      runIfDue: "Run if due",
      pauseJob: "Pause: {name}",
      resumeJob: "Resume: {name}",
      clone: "Clone",
      remove: "Remove",
      removeConfirmTitle: 'Remove "{name}"?',
      removeConfirmMessage:
        "This permanently deletes the automation and stops all future runs. This action cannot be undone.",
      moreJob: "More actions for {name}",
    },
    runNotStarted: {
      notDue: "This automation is not due yet.",
      alreadyRunning: "This automation is already running.",
      recoveryPending: "Scheduler recovery is still in progress.",
      invalidSpec: "This automation has an invalid schedule or payload.",
      stopped: "The scheduler is stopped.",
      unknown: "This automation could not be started.",
    },
    jobs: {
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      condition: "Condition",
      conditional: "Conditional",
      unconditional: "Unconditional",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
    },
    runs: {
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or task",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      noMatching: "No matching runs.",
      emptyTitle: "No runs yet",
      emptyHint: "Runs show up here once an automation fires.",
      conditionActivity: "Condition activity",
      conditionActivityHint: "Checks run on schedule before the payload starts.",
      checks: "Checks",
      lastChecked: "Last checked",
      lastFired: "Last fired",
      notChecked: "Not checked yet",
      neverFired: "Never",
      emptyConditionTitle: "No payload runs yet",
      emptyConditionHint:
        "The condition has been checked {count} times without matching. Run history starts when the payload executes.",
      emptyConditionHintOne:
        "The condition has been checked once without matching. Run history starts when the payload executes.",
      emptyConditionUnchecked:
        "The condition has not been checked yet. Run history starts when the payload executes.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      runStatusRunning: "Running",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      requiredSr: "required",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this task",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      promptPlaceholder: "Describe what OpenClaw should do...",
      repeat: "Repeat",
      repeatInterval: "Interval",
      repeatOnce: "Once",
      repeatOnExit: "On exit",
      repeatStream: "Stream source",
      onExitHelp: "Runs when a watched command exits. The schedule cannot be edited here.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      summaryEverySecondOne: "Runs every second",
      summaryEveryMinuteOne: "Runs every minute",
      summaryEveryHourOne: "Runs every hour",
      summaryEveryDayOne: "Runs every day",
      summaryEverySeconds: "Runs every {amount} seconds",
      summaryEveryMinutes: "Runs every {amount} minutes",
      summaryEveryHours: "Runs every {amount} hours",
      summaryEveryDays: "Runs every {amount} days",
      summaryOnce: "Runs once at {at}",
      summaryCron: "Cron schedule {expr}",
      summaryCronTz: "Cron schedule {expr} ({tz})",
      timezoneOptional: "Timezone",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp:
        "Optional. Leave blank to use the Gateway host timezone, or enter any valid IANA timezone.",
      runsIn: "Runs in",
      mainSession: "Main session",
      isolatedSession: "Isolated session",
      sessionHelp: "Main posts into the agent's timeline. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      action: "Action",
      command: "Command",
      script: "Script",
      readOnlyPayloadHelp:
        "This payload was created outside Control UI. Its contents stay read-only and are preserved when you save other changes.",
      systemEvent: "Post to main timeline",
      agentTurn: "Run agent task",
      systemEventHelp: "Sends your text to the gateway main timeline (good for reminders).",
      agentTurnHelp: "Starts an agent run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp: "Optional. Leave blank to use the gateway default timeout for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Prompt",
      deliveryModeLabel: "Mode",
      announceDefault: "Announce summary",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional condition checks, delivery guarantees, schedule jitter, and model controls.",
      conditionTrigger: "Condition trigger",
      conditionTriggerHelp:
        "Run a quiet headless check before the task and call the model only when it matches.",
      triggerConfigured: "Trigger configured",
      triggerScript: "Trigger script",
      triggerScriptHelp:
        "Runs unattended with this automation's tool policy. Return json({ fire, message?, state? }); limits: 30 seconds, 5 tool calls, 16 KB state.",
      triggerOnce: "Disable after first match",
      triggerOnceHelp: "Disable this automation after the first successful fired task.",
      triggerDisabled: "Condition triggers are disabled by cron.triggers.enabled.",
      triggerDisabledConfigured:
        "Condition triggers are disabled. Existing configuration is preserved until you clear it.",
      clearTrigger: "Clear trigger",
      startEnabled: "Start enabled",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this task to use the gateway default agent.",
      sessionKey: "Session key",
      sessionKeyHelp: "Optional routing key for job delivery and wake routing.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      accountId: "Account ID",
      accountIdHelp: "Optional channel account ID for multi-account setups.",
      lightContext: "Light context",
      lightContextHelp: "Use lightweight bootstrap context for this agent job.",
      model: "Model",
      customModel: "Custom model…",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp:
        "Start typing to pick a known model, or enter a custom one. Routine jobs (summaries, triage, classification) run well on a lighter model — cheaper and faster than your default.",
      thinking: "Reasoning",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      failureAlerts: "Failure alerts",
      failureAlertInherit: "Inherit global setting",
      failureAlertDisabled: "Disable for this job",
      failureAlertCustom: "Custom per-job settings",
      failureAlertsHelp: "Control when this job sends repeated-failure alerts.",
      failureAlertAfter: "Alert after",
      failureAlertAfterHelp: "Consecutive errors before alerting.",
      failureAlertCooldown: "Cooldown (seconds)",
      failureAlertCooldownHelp: "Minimum seconds between alerts.",
      failureAlertChannel: "Alert channel",
      failureAlertTo: "Alert to",
      failureAlertToPlaceholder: "+1555... or chat id",
      failureAlertToHelp: "Optional recipient override for failure alerts.",
      failureAlertMode: "Alert mode",
      failureAlertAnnounce: "Announce (via channel)",
      failureAlertWebhook: "Webhook (HTTP POST)",
      failureAlertAccountId: "Alert account ID",
      failureAlertAccountPlaceholder: "Account ID for multi-account setups",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the task if delivery itself fails.",
      cantAddYet: "Can't save yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      createTask: "Create automation",
      createAndRun: "Create & run now",
      cancel: "Cancel",
    },
    jobState: {
      next: "Next",
    },
    runEntry: {
      noSummary: "No summary.",
      deliverySuppression: "Delivery suppression: {reason}",
      runAt: "Run at",
      openRunChat: "Open run chat",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      triggerIntervalTooShort:
        "Condition-triggered automations must run at least every 30 seconds.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      triggerScriptRequired: "Trigger script is required when the condition trigger is enabled.",
      triggerScriptPayloadUnsupported:
        "Script payloads cannot use condition triggers because both own the same saved state.",
      triggerScheduleUnsupported:
        "Condition triggers require an interval, cron, or stream schedule.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be greater than 0 seconds.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must start with http:// or https://.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },
};
